import { isMapEditing, mapEditRevision } from './editlock';
// Content panels: Objects | Places | Bookmarks | Live | Log, presented as a
// slide-out drawer driven by the left icon dock (main.ts owns the dock DOM;
// this module wires its buttons, badges, and the drawer views). Overlay
// toggles live in a "Layers" panel on the map's right rail — one merged
// layers-and-legend list, grouped by domain (Places / Objects / Events /
// Actors) with non-entity overlays at the end.
//
// One /api/entities fetch feeds the shared views (split client-side by the
// category's registry domain); personal bookmarks come from /api/bookmarks;
// the 15 s updates loop in main.ts routes deltas through applyUpdates().

import { areaFor, groupArea, redundantArea, type Area } from './area';
import type { Polygon } from 'geojson';
import * as maplibregl from 'maplibre-gl';
import type {
  BookmarkDto, EntityDto, IncidentDto, Me, RegistryDto, UpdatesDto,
} from './api';
import { ApiError, del, get, post, put } from './api';
import {
  setHiddenUserBookmarks, setUserBookmarks, setUserBookmarksVisible,
} from './bookmarks';
import { TERRAIN_SOURCE_ID } from './basemaps';
import { entityRow, isCompactViewport, relTime, toast, type TickerItem } from './hud';
import { flashContaining, regionFlashCfg, setAdmOverlay } from './boundaries';
import {
  markupCount, onMarkupCountChange, onMarkupVisibleChange, setMarkupVisible,
} from './markup';
import {
  applyObjectFilter, cacheEntities, DAMAGE_RECENT_DAYS, bumpTiles,
  getRegistry, PLACE_MINZOOM,
  setCoverageEntities, setLiveEvents, uncacheEntities,
} from './objects';
import {
  ageColor, feedOpacity, fmtAbs, lastReportOf, liveFadeHours, MATERIAL_SKEW_MS,
  relTimeMs, setLiveTiming, timeHtml, visibilityOf, whenOf,
} from './when';
import {
  getVolumeStyle, setVolumeStyle, updateDomes,
  type VolumeKind, type VolumeStyle,
} from './domes';
import { openStudio } from './studio';
import * as firms from './firms';
import * as aircraft from './aircraft';
import { claimHtml, confidenceChip } from './claim';
import {
  type Corroboration, damageFor, echoChip, fetchHeat, fetchMediaEcho,
  type HeatHit, heatChip, type MediaEcho,
} from './corroborate';
import { bestMedia, channelLabel, kindColor, LIVE_KINDS, mediaGlyph,
         mediaLabel, roleLabel, sourceLinks, type SourceLink,
         UNCLEAR } from './livekinds';
import { actorsOf, contextOf, headlineOf as newsHeadlineOf, isNewsItem,
         reportCountOf, topicOf } from './news';
import { haversineKm } from './radar';
import {
  clearMutes, dismiss, isDismissed, isMuted, isPinned, isUnread, markRead,
  mutePlace, onReadStateChange, resetReadStateListeners, togglePin, undismissAll,
} from './readstate';
import { loadChannelMeta, reliabilityOf, treatmentOf } from './channelmeta';
import {
  applySuggestion, detach as detachReports, dismissSuggestion, type FeedGroup,
  groupFeed, groupFeedWindowed, groupSignature, incidentDetail,
  knownIncidents, loadIncidents, regroup,
  // Aliased: panels has its own local loadSuggestions for the intel pane (the
  // suggestion ENGINE's entity proposals — a different feature entirely).
  loadSuggestions as loadMergeSuggestions,
  mergeIncidents, mergeInto, renameIncident, setIncidentLinks, splitOut,
  suggestionsFor as mergeSuggestionsFor,
} from './incidents';
import {
  aliasSuggestionsFor, applyAdvice, applyEquipment, applyGeo, controlSuggestions,
  dismissAdvice,
  equipmentSuggestions, filingFor, geoSuggestions, incidentLinksFor,
  linkSuggestionsFor,
  linkWording, loadAdvice, removeIncidentLink, researchEquipment,
} from './advice';
import { applyFactionColors, controlRows, legendBlock as territoryLegend } from './territory';
import { setBuildings } from './buildings';
import { bumpPipelines, energyStatus, setPipelines, statusLabel } from './pipelines';
import { createColorsPopover } from './colorpick';
import { armMapPick, closeSelection, setCompanionCardClose } from './select';
import { loadSequences, precedentFor, sequenceChipHtml } from './sequences';
import {
  CARD_PX, feedThumbHtml, feedThumbsOn, learnMediaHashes, mediaGroups,
  mediaStripHtml, mountMedia, ROW_PX, setFeedThumbs, wireFeedThumb, wireMediaStrip,
} from './media';
import {
  attributionConflict, headlineOf, latestDevelopment, situationAnchor,
} from './situations';
import {
  getActiveStory, inScope, isOnlyThisStory, onStoryChange, setOnlyThisStory,
} from './story';
import { openTypeCreator } from './tactical';
import { isoDay, normalizeWindow, resolveWindow } from './timewindow';
import {
  cleanName, GROUP_NAME_MAX, GROUPS_MAX, groupState, LAYER_GROUPS_KEY, type LayerGroup,
  liveMembers, loadGroups, newGroupId, OVERLAY_PREFIX, saveGroups, tapTurnsOn,
} from './layergroups';

// Height-exaggeration readout: make the multiplier unmistakable so a tall lobe
// reads as "stretched N× vertically", not as its real physical height.
function exaggLabel(x: number): string {
  return x === 1 ? '×1 true scale' : `×${x} vertical`;
}

// ---------------------------------------------------------------------------
// DTOs (monitor payloads mirror backend/app/monitor/routes.py)

interface NextPassDto {
  datetime: string;
  windowStart?: string;
  windowEnd?: string;
  satellite?: string | null;
  sunElevation?: number | null;
  closestTrackDistanceKm?: number | null;
}

interface MonitorEntry {
  updatedAt?: string | null;
  scenes?: { date: string; cloud: number | null }[];
  nextPass?: NextPassDto | null;
  scenesError?: string;
  passError?: string;
}

interface SuggestionDto {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  proposedType: string | null;
  proposedName: string | null;
  proposedAttrs: Record<string, unknown> | null;
  geometry: { type: string; coordinates: [number, number] } | null;
  anchorName: string | null;
  evidenceCount: number;
  createdAt: string;
  edgeType: string | null;
  edgeSrcId: string | null;
  edgeDstId: string | null;
}

interface SuggestionDebugDto {
  llm: { configured: boolean; reviewsEnabled: boolean; model: string | null;
         webSearch: boolean; hint: string | null };
  config: { enabled: boolean; intervalHours: number; windowDays: number;
            minEvents: number; clusterEpsKm: number; suppressDays: number };
  lastRun: string | null;
  pendingCount: number;
  events: { windowDays: number; total: number; eligible: number;
            byKind: Record<string, { total: number; eligible: number }> };
  groups: {
    group: string; kinds: string[]; clusterCount: number;
    clusters: { lat: number; lon: number; events: number; place: string | null;
                firstAt: string | null; decision: string; reason: string }[];
  }[];
  graph: { snapshotEntities: number; heuristicPairs: number };
  wouldEmit: number;
}

export type PanelId = 'objects' | 'places' | 'bookmarks' | 'live' | 'damage'
  | 'intel' | 'log';

export interface PanelsOptions {
  map: maplibregl.Map;
  registry: RegistryDto;
  me: Me;
  openEntity: (id: string) => void;
  /** Dock buttons (main.ts renders them); panels wires clicks + badges. */
  dockButtons: Record<PanelId, HTMLElement>;
  /** Host element for the "Layers" popover button (a wrapper in the top strip). */
  layersHost?: HTMLElement;
  /** Server layer defaults (config.defaultLayers); localStorage wins over these. */
  overlayDefaults?: Record<string, boolean>;
  /** Called whenever the object/alert tallies change (feeds the gauges). */
  onCounts?: (c: { objects: number; alerts: number }) => void;
  /** Latest live event + events-per-half-hour buckets (feeds ticker + spark). */
  onLive?: (info: { latest: TickerItem | null; buckets: number[] }) => void;
  /** Draw a polygon footprint on the map (reuses the Add-area draw tool);
   *  resolves the closed ring, or null if cancelled. Used to place a suggested
   *  site as an area rather than a single point. */
  captureArea?: (label: string) => Promise<[number, number][] | null>;
  /** Click a single point on the map; resolves it, or null if cancelled.
   *  Used by the damage panel's add-point flow. */
  capturePoint?: (label: string) => Promise<[number, number] | null>;
}

/** What a sheet remembers of the Layers panel (sheets.ts / cockpitsheet.ts):
 *  the narrowing state, never the fresh-every-session opt-ins (FIRMS,
 *  aircraft). Every field optional, so an older sheet applies what it has. */
export interface LayerStateDto {
  categories?: Record<string, boolean>;
  types?: Record<string, boolean>;
  adm?: boolean;
  watchedOnly?: boolean;
  country?: string;
  damage?: string;
}

export interface PanelsApi {
  /** The Layers panel's narrowing state, for a sheet to keep. */
  layerState: () => LayerStateDto;
  /** Put a sheet's layer state back: map filters, stored prefs and the panel. */
  applyLayerState: (state: LayerStateDto) => void;
  /** The story-scoped entity list the map is drawing from. */
  entities: () => EntityDto[];
  refresh: () => Promise<void>;
  applyUpdates: (dto: UpdatesDto) => void;
  applyOverlays: () => void;
  counts: () => { objects: number; alerts: number };
  /** Open the drawer on a view (or close it with null). */
  open: (id: PanelId | null) => void;
  /** Phone: drop a full-height sheet back to its half-height peek. */
  peek: () => void;
}

// ---------------------------------------------------------------------------
// Small helpers

type BBox = [number, number, number, number];

const esc = (v: unknown): string => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const truncate = (s: string, n: number): string =>
  s.length > n ? `${s.slice(0, n - 1)}…` : s;

function fmtScene(s: { date: string; cloud: number | null } | undefined): string {
  if (!s?.date) return '—';
  const day = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(`${s.date}T00:00:00Z`));
  return `${day}${s.cloud == null ? '' : ` · ${Math.round(s.cloud)}% cloud`}`;
}

function fmtDateTime(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(new Date(iso));
}

// 4 km box around a point place (side/2 = 2 km), matching the server's
// MONITOR_SQUARE_KM box so Studio and monitor look at the same area.
function pointBox(lon: number, lat: number): BBox {
  const dLat = 2 / 110.574;
  const dLon = 2 / (111.320 * Math.cos((lat * Math.PI) / 180));
  return [lon - dLon, lat - dLat, lon + dLon, lat + dLat];
}

function boxPolygon(b: BBox): Polygon {
  const [w, s, e, n] = b;
  return { type: 'Polygon', coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]] };
}

function placeShape(e: EntityDto): { bbox: BBox; polygon: Polygon } | null {
  const g = e.geometry;
  if (g?.type === 'Polygon') {
    const ring = g.coordinates[0] ?? [];
    if (ring.length < 4) return null;
    const lons = ring.map((c) => c[0] ?? 0);
    const lats = ring.map((c) => c[1] ?? 0);
    return {
      bbox: [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)],
      polygon: g,
    };
  }
  if (g?.type === 'Point') {
    const [lon, lat] = g.coordinates as [number, number];
    const bbox = pointBox(lon, lat);
    return { bbox, polygon: boxPolygon(bbox) };
  }
  return null;
}

// Age ramp, lightning-tracker style (shared with the map dots in objects.ts):
// the pin colour encodes HOW OLD.
const agePin = ageColor;

const ACT_VERBS: Record<string, string> = {
  create: 'added', update: 'updated', merge: 'merged',
};

const OVERLAY_KEY = 'cockpit-overlays';
const GROUPS_KEY = 'cockpit-groups';
const FADE_KEY = 'cockpit-fade-hours';   // per-user event window override
const COLORBY_KEY = 'cockpit-live-colorby';   // feed accent: age (default) vs kind
const FEEDMODE_KEY = 'cockpit-feed-mode';     // situations (default) vs everything
const FEEDDIM_KEY = 'cockpit-feed-dim';       // rows dim with age: off by default
const FEEDBUBBLE_KEY = 'cockpit-feed-bubble'; // updates resurface: off by default
const DRAWER_KEY = 'cockpit-drawer';
const CHECK_CONCURRENCY = 4;  // gentle on the upstream catalog, like the old app
const MAX_PLACE_ROWS = 250;   // DOM cap for the places pane; search narrows
// One collator for every name sort: the same ordering as localeCompare with
// default options, several times faster per comparison (localeCompare builds
// the collation state on each call), and these sorts run over the whole
// places list on every render.
const NAME_COLLATOR = new Intl.Collator();
// The route's hard ceiling (`le=10000` on /api/entities). Reference data is
// fetched under it separately from events so a growing event history can't
// push places off the tail; if the reference half ever fills it, the Places
// pane says so rather than silently listing a subset.
const ENTITY_FETCH_CAP = 10000;
const TYPE_FILTER_KEY = 'cockpit-type-filter';    // per-type map visibility
const COUNTRY_KEY = 'cockpit-filter-country';     // map country filter
const DAMAGE_DAYS_KEY = 'cockpit-damage-days';    // damage window (see timewindow.ts)
const FIRMS_WINDOW_KEY = 'cockpit-firms-window';  // FIRMS window (see timewindow.ts)
const BM_HIDDEN_KEY = 'cockpit-bookmarks-hidden'; // bookmarks hidden one by one

/** Trailing-edge debounce for filter inputs — re-rendering hundreds of rows on
 *  every keystroke is what made typing in the search boxes feel sticky. */
function debounced(fn: () => void, ms = 150): () => void {
  let t: number | undefined;
  return () => {
    if (t !== undefined) window.clearTimeout(t);
    t = window.setTimeout(() => { t = undefined; fn(); }, ms);
  };
}

const PANEL_TITLES: Record<PanelId, string> = {
  objects: 'Objects', places: 'Places', bookmarks: 'Bookmarks',
  live: 'Live feed', damage: 'Damage', intel: 'Suggestions', log: 'Log',
};

// ---------------------------------------------------------------------------
// Drawer shell: slide-out from the dock; one view visible at a time.

interface Pane {
  root: HTMLElement;
  tools: HTMLElement;
  body: HTMLElement;
}

interface DrawerShell {
  panes: Record<PanelId, Pane>;
  /** Only the live dock button has a badge element (unread is a feed concept);
   *  setBadge on any other id is a silent no-op via the null-check. */
  setBadge: (id: PanelId, text: string) => void;
  open: (id: PanelId | null) => void;
  peek: () => void;
  current: () => PanelId | null;
  onOpen: (cb: (id: PanelId | null) => void) => void;
}

/** Phone sheet height, remembered per browser: '' peek (half) or 'tall'. */
const SHEET_KEY = 'cockpit-sheet';

function buildDrawer(drawer: HTMLElement,
                     dockButtons: Record<PanelId, HTMLElement>,
                     closeCard: () => void): DrawerShell {
  const IDS: PanelId[] = ['objects', 'places', 'bookmarks', 'live', 'damage',
                          'intel', 'log'];
  drawer.innerHTML = `
    <header class="drawer-head">
      <h3 data-d="title"></h3>
      <button class="x sheet-ex" data-d="expand" title="Expand">▴</button>
      <button class="x" data-d="close" title="Close">✕</button>
    </header>
    ${IDS.map((id) => `
    <div class="pane" data-pane="${id}">
      <div class="pc-tools" hidden></div>
      <div class="body"></div>
    </div>`).join('')}`;
  const titleEl = drawer.querySelector('[data-d="title"]') as HTMLElement;

  const panes = {} as Record<PanelId, Pane>;
  for (const id of IDS) {
    const root = drawer.querySelector(`[data-pane="${id}"]`) as HTMLElement;
    panes[id] = {
      root,
      tools: root.querySelector('.pc-tools') as HTMLElement,
      body: root.querySelector('.body') as HTMLElement,
    };
  }

  let current: PanelId | null = null;
  const listeners: ((id: PanelId | null) => void)[] = [];

  function open(id: PanelId | null): void {
    current = id;
    localStorage.setItem(DRAWER_KEY, id ?? '');
    drawer.classList.toggle('open', id !== null);
    for (const pid of IDS) {
      panes[pid].root.classList.toggle('active', pid === id);
      dockButtons[pid].classList.toggle('active', pid === id);
    }
    if (id) titleEl.textContent = PANEL_TITLES[id];
    for (const cb of listeners) cb(id);
  }

  for (const id of IDS) {
    dockButtons[id].addEventListener('click', () => {
      // Phone: while a selection card is showing, the open sheet is slid out
      // of the way (mobile.css) and the tab still reads as "on". Tapping it
      // then means "back to the list", not "close the list I can't see".
      if (current === id && isCompactViewport()
          && document.querySelector('.cockpit > .selcard')) {
        closeCard();
        return;
      }
      open(current === id ? null : id);
    });
  }
  drawer.querySelector('[data-d="close"]')!.addEventListener('click', () => open(null));

  // --- phone sheet: half-height peek, or tall ------------------------------
  // The drawer is a bottom sheet under the compact query (mobile.css). Two
  // heights: peek (the map stays visible above — a feed tap flies the map and
  // you see it land) and tall (the list gets the screen). The header is the
  // handle: tap or swipe up expands, swipe down shrinks, and a swipe down on
  // a peek closes. The choice persists so a tall reader stays tall.
  const head = drawer.querySelector('.drawer-head') as HTMLElement;
  const exBtn = drawer.querySelector('[data-d="expand"]') as HTMLButtonElement;
  const setTall = (tall: boolean): void => {
    drawer.classList.toggle('tall', tall);
    exBtn.textContent = tall ? '▾' : '▴';
    exBtn.title = tall ? 'Shrink to half height' : 'Expand';
    localStorage.setItem(SHEET_KEY, tall ? 'tall' : '');
  };
  setTall(localStorage.getItem(SHEET_KEY) === 'tall');
  exBtn.addEventListener('click', () => setTall(!drawer.classList.contains('tall')));
  // A swipe ends in a click too (preventDefault on pointerup doesn't stop
  // it), so the swipe marks itself and the click after it is swallowed.
  let swipeY: number | null = null;
  let swiped = false;
  head.addEventListener('click', (e) => {
    if (swiped) { swiped = false; return; }
    if ((e.target as HTMLElement).closest('button')) return;
    if (isCompactViewport()) setTall(!drawer.classList.contains('tall'));
  });
  head.addEventListener('pointerdown', (e) => {
    if (!isCompactViewport() || (e.target as HTMLElement).closest('button')) return;
    swipeY = e.clientY;
  });
  head.addEventListener('pointerup', (e) => {
    if (swipeY == null) return;
    const dy = e.clientY - swipeY;
    swipeY = null;
    if (Math.abs(dy) < 28) return;             // a tap — the click handler's
    swiped = true;
    if (dy < 0) setTall(true);
    else if (drawer.classList.contains('tall')) setTall(false);
    else open(null);
  });
  head.addEventListener('pointercancel', () => { swipeY = null; });

  // Restore last state (default: closed — the map leads).
  const stored = localStorage.getItem(DRAWER_KEY) as PanelId | '' | null;
  if (stored && IDS.includes(stored)) open(stored);

  return {
    panes,
    setBadge: (id, text) => {
      const b = dockButtons[id].querySelector('.bdg') as HTMLElement | null;
      if (!b) return;
      b.textContent = text;
      b.hidden = !text;
    },
    open,
    peek: () => setTall(false),
    current: () => current,
    onOpen: (cb) => listeners.push(cb),
  };
}

// ---------------------------------------------------------------------------

export function installPanels(drawer: HTMLElement, opts: PanelsOptions): PanelsApi {
  const { map, registry, me, openEntity } = opts;
  // Either card (selection or group) — closeGroupCard is hoisted from below.
  const shell = buildDrawer(drawer, opts.dockButtons,
                            () => { closeSelection(map); closeGroupCard(); });
  const objectsPane = shell.panes.objects;
  const placesPane = shell.panes.places;
  const bmPane = shell.panes.bookmarks;
  const livePane = shell.panes.live;
  const damagePane = shell.panes.damage;
  const intelPane = shell.panes.intel;
  const logPane = shell.panes.log;

  // Map lookups: both run once per entity per rebuild, over the whole list.
  const catByKey = new Map(registry.categories.map((c) => [c.key, c]));
  const domainOf = (e: EntityDto): string => catByKey.get(e.category)?.domain ?? 'object';

  let all: EntityDto[] = [];
  // `all` narrowed to the active story (rebuildViews). Everything the operator
  // reads off — panes, gauges, legend counts — comes from here, so the panes
  // and the map agree at the story boundary instead of the panes listing rows
  // the tiles are filtering out.
  let scoped: EntityDto[] = [];
  let places: EntityDto[] = [];
  let referenceTruncated = false;
  let liveEvents: EntityDto[] = [];
  let newsItems: EntityDto[] = [];
  let objects: EntityDto[] = [];
  let damage: EntityDto[] = [];
  let alertCount = 0;

  // A PANE THAT IS NOT OPEN IS NOT PAINTED. Its render marks it dirty and the
  // drawer paints it on open. Every pane used to rebuild its whole DOM on
  // every poll — hundreds of rows for the objects, places and damage lists and
  // up to 150 situation cards — for the one pane (if any) actually showing.
  const paneDirty: Record<PanelId, boolean> = {
    objects: false, places: false, bookmarks: false, live: false,
    damage: false, intel: false, log: false,
  };
  const paneShowing = (id: PanelId): boolean => {
    if (shell.current() === id) { paneDirty[id] = false; return true; }
    paneDirty[id] = true;
    return false;
  };

  const counts = () => ({ objects: scoped.length, alerts: alertCount });
  const pushCounts = () => opts.onCounts?.(counts());

  function pushLive(): void {
    const latest = liveEvents[0];
    let item: TickerItem | null = null;
    if (latest) {
      const kind = LIVE_KINDS[String(latest.attrs.kind ?? 'unclear')] ?? UNCLEAR;
      item = {
        glyph: kind.glyph,
        color: agePin(whenOf(latest).at),
        place: String(latest.attrs.place ?? '') || (latest.name ?? '(unknown)'),
        // Summary first, like the live feed (liveRow): it is the extractor's
        // English rendering. attrs.text is the raw post — Arabic/Farsi on most
        // channels — and is always set, so preferring it left the ticker showing
        // untranslated source text while the feed below it read fine.
        text: truncate(String(latest.attrs.summary ?? latest.attrs.text ?? ''), 140),
        at: whenOf(latest).at,
        id: latest.id,
        coords: latest.geometry?.type === 'Point'
          ? (latest.geometry.coordinates as [number, number]) : undefined,
      };
    }
    // 12 half-hour buckets over the last 6 h, oldest -> newest. By OCCURRENCE:
    // bucketing by validFrom drew a backlog replay as one spike in the current
    // bin for events actually spread over six hours.
    const buckets = new Array(12).fill(0) as number[];
    const now = Date.now();
    for (const e of liveEvents) {
      const age = now - whenOf(e).at;
      if (age < 0 || age >= 6 * 3_600_000) continue;
      const i = 11 - Math.floor(age / 1_800_000);
      buckets[i] = (buckets[i] ?? 0) + 1;
    }
    opts.onLive?.({ latest: item, buckets });
  }

  // --- Objects (grouped by registry category) ------------------------------

  objectsPane.tools.hidden = false;
  objectsPane.tools.innerHTML = '<input type="text" data-f="q" placeholder="Filter objects…" />';
  const objectsFilter = objectsPane.tools.querySelector('input')!;
  objectsFilter.addEventListener('input', debounced(() => renderObjects()));

  function groupPrefs(): Record<string, boolean> {
    try { return JSON.parse(localStorage.getItem(GROUPS_KEY) ?? '{}') as Record<string, boolean>; }
    catch { return {}; }
  }
  function saveGroupPref(key: string, collapsed: boolean): void {
    const p = groupPrefs();
    p[key] = collapsed;
    localStorage.setItem(GROUPS_KEY, JSON.stringify(p));
  }

  // Group label: the registry category. Damage has its own category now, so
  // no type carries a special case here.
  function groupOf(e: EntityDto): { key: string; label: string; sort: number } {
    const cat = catByKey.get(e.category);
    return cat
      ? { key: `cat:${cat.key}`, label: cat.label, sort: cat.sort }
      : { key: 'cat:other', label: 'Other', sort: 99 };
  }

  function renderObjects(): void {
    if (!paneShowing('objects')) return;
    const needle = objectsFilter.value.trim().toLowerCase();
    const filtered = needle
      ? objects.filter((e) => (e.name ?? '').toLowerCase().includes(needle)
          || e.type.includes(needle) || e.category.includes(needle))
      : objects;
    objectsPane.body.innerHTML = '';
    if (!filtered.length) {
      objectsPane.body.innerHTML = `<div class="empty">${
        objects.length ? 'Nothing matches.'
          : 'No objects yet — use the Add unit or Draw area tools in the left dock.'}</div>`;
      return;
    }

    const groups = new Map<string, { label: string; sort: number; items: EntityDto[] }>();
    for (const e of filtered) {
      const g = groupOf(e);
      let bucket = groups.get(g.key);
      if (!bucket) {
        bucket = { label: g.label, sort: g.sort, items: [] };
        groups.set(g.key, bucket);
      }
      bucket.items.push(e);
    }
    const collapsed = groupPrefs();
    const ordered = [...groups.entries()].sort((a, b) => a[1].sort - b[1].sort);
    for (const [key, g] of ordered) {
      g.items.sort((a, b) => NAME_COLLATOR.compare(a.name ?? '', b.name ?? ''));
      // While filtering, always show matches — ignore the collapse preference.
      const isCollapsed = !needle && (collapsed[key] ?? false);
      const head = document.createElement('div');
      head.className = `grp-head${isCollapsed ? ' collapsed' : ''}`;
      head.innerHTML = `<span class="tw">${isCollapsed ? '▸' : '▾'}</span>
        ${esc(g.label)}<span class="grp-count">${g.items.length}</span>`;
      head.addEventListener('click', () => {
        saveGroupPref(key, !(collapsed[key] ?? false));
        renderObjects();
      });
      objectsPane.body.appendChild(head);
      if (isCollapsed) continue;
      for (const e of g.items.slice(0, 100)) {
        objectsPane.body.appendChild(entityRow(e, registry, () => {
          if (e.geometry?.type === 'Point') {
            map.flyTo({ center: e.geometry.coordinates as [number, number], zoom: 11 });
          }
          openEntity(e.id);
        }));
      }
    }
  }

  // --- Damage (the dedicated add/edit view for damage markers) --------------
  //
  // Damage is a durable record of what was hit, so every row carries its audit
  // line — who recorded it and when (entity.created_by / created_at, exposed on
  // the DTO as createdBy/createdAt). Adding goes through the map capture hooks
  // (point click or area outline); editing reuses the selection card.

  const damageColor = registry.types.find((t) => t.key === 'damage_marker')
    ?.style.color ?? '#d93025';

  damagePane.tools.hidden = false;
  damagePane.tools.innerHTML = `
    <input type="text" data-f="q" placeholder="Filter damage…" />
    <button type="button" class="mini" data-f="addpt"
      title="Click the map to drop a damage point">＋ Point</button>
    <button type="button" class="mini" data-f="addarea"
      title="Draw the damaged area's outline">＋ Area</button>`;
  const damageFilter = damagePane.tools.querySelector('[data-f="q"]') as HTMLInputElement;
  damageFilter.addEventListener('input', debounced(() => renderDamage()));

  async function addDamage(kind: 'point' | 'polygon'): Promise<void> {
    let geometry: Record<string, unknown>;
    if (kind === 'point') {
      const pt = await opts.capturePoint?.('the damage marker');
      if (!pt) return;
      geometry = { type: 'Point', coordinates: pt };
    } else {
      const ring = await opts.captureArea?.('the damaged area');
      if (!ring) return;
      geometry = { type: 'Polygon', coordinates: [ring] };
    }
    const name = prompt('Label this damage (what was hit):')?.trim() ?? '';
    try {
      const created = await post<EntityDto>('/api/entities', {
        type: 'damage_marker', name: name || null,
        // Reported date defaults to today; the card's Edit fixes it when the
        // strike is older than the recording.
        attrs: { date: new Date().toISOString().slice(0, 10) },
        geometry,
      });
      bumpTiles(map, created.rev);
      await refresh();
      openEntity(created.id);   // straight into the card for note/date edits
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not save the damage marker.');
    }
  }
  (damagePane.tools.querySelector('[data-f="addpt"]') as HTMLButtonElement)
    .addEventListener('click', () => void addDamage('point'));
  (damagePane.tools.querySelector('[data-f="addarea"]') as HTMLButtonElement)
    .addEventListener('click', () => void addDamage('polygon'));

  // Sort by the reported strike date when it parses, else by when the row was
  // recorded — so an operator back-filling last week's damage files it there.
  function damageWhen(e: EntityDto): number {
    const w = whenOf(e);
    // A 'row' basis means the marker carries no reported date. Prefer the
    // entity's creation over this VERSION's valid_from so editing a note on an
    // old marker does not move it to the top of the list.
    return w.basis === 'row' ? Date.parse(e.createdAt ?? e.validFrom) : w.at;
  }

  function renderDamage(): void {
    if (!paneShowing('damage')) return;
    const needle = damageFilter.value.trim().toLowerCase();
    const filtered = needle
      ? damage.filter((e) => [e.name, e.attrs.note, e.attrs.date, e.createdBy]
          .some((v) => String(v ?? '').toLowerCase().includes(needle)))
      : damage;
    damagePane.body.innerHTML = '';
    if (!filtered.length) {
      damagePane.body.innerHTML = `<div class="empty">${
        damage.length ? 'Nothing matches.'
          : 'No damage recorded yet — add a point or draw the damaged area with the buttons above.'}</div>`;
      return;
    }
    for (const e of filtered.slice(0, 300)) {
      const date = typeof e.attrs.date === 'string' ? e.attrs.date : e.validFrom.slice(0, 10);
      const note = typeof e.attrs.note === 'string' ? e.attrs.note : '';
      const isArea = e.geometry != null && e.geometry.type !== 'Point';
      const audit = `added by ${e.createdBy ?? 'system'}${
        e.createdAt ? ` · ${relTime(e.createdAt)} ago` : ''}`;
      const row = document.createElement('div');
      row.className = 'row entity-row dmg-row';
      row.innerHTML = `<span class="dot" style="background:${esc(damageColor)}"></span>
        <span class="t"><span class="n">${esc(e.name || note || date)}</span>
        <span class="s">${esc([date, isArea ? 'area' : 'point',
          e.name ? truncate(note, 46) : ''].filter(Boolean).join(' · '))}</span>
        <span class="s dmg-audit">${esc(audit)}</span></span>`;
      row.addEventListener('click', () => {
        const shape = placeShape(e);
        if (shape) {
          const [w, s, ee, n] = shape.bbox;
          map.fitBounds([[w, s], [ee, n]], { padding: 80, maxZoom: 14 });
        }
        openEntity(e.id);
      });
      damagePane.body.appendChild(row);
    }
  }

  // --- Places (the shared map of tracked locations; watched areas carry the
  // scene monitor, other place types get Studio + focus) --------------------

  const monitorEntries: Record<string, MonitorEntry> = {};
  const checking = new Set<string>();
  // Per-entity watch overrides; effective watched = override ?? type default.
  const watchOverrides: Record<string, boolean> = {};
  const isWatched = (p: EntityDto): boolean =>
    watchOverrides[p.id]
    ?? (registry.types.find((t) => t.key === p.type)?.watchDefault ?? false);
  // Anything with a boxable footprint can be scanned and monitored — the
  // limit is geometry, not type (a road's LineString has no usable box).
  const scannable = (p: EntityDto): boolean => placeShape(p) !== null;

  placesPane.tools.hidden = false;
  // The search box gets a row to itself: sharing one line with two selects,
  // a checkbox and a button left it a few characters wide.
  placesPane.tools.classList.add('places-tools');
  placesPane.tools.innerHTML = `
    <div class="pt-row">
      <input type="text" data-f="q" placeholder="Search all places — name, type, country…" />
    </div>
    <div class="pt-row">
    <select data-f="watch" title="Filter by watched">
      <option value="all">All</option>
      <option value="watched">Watched</option>
      <option value="unwatched">Not watched</option>
    </select>
    <select data-f="sort" title="Sort places">
      <option value="name">Name</option>
      <option value="country">Country</option>
      <option value="latest">Latest</option>
      <option value="pass">Next pass</option>
    </select>
    <label class="mini inview-toggle" title="Only list places inside the current map view">
      <input type="checkbox" data-f="inview" /> In view</label>
    <button type="button" class="mini" data-f="updateAll"
      title="Re-check every watched place">Update all</button>
    <span class="progress" data-f="progress"></span>
    </div>`;
  const placeFilter = placesPane.tools.querySelector('[data-f="q"]') as HTMLInputElement;
  const placeWatchFilter = placesPane.tools.querySelector('[data-f="watch"]') as HTMLSelectElement;
  const placeSort = placesPane.tools.querySelector('[data-f="sort"]') as HTMLSelectElement;
  const placeUpdateAll = placesPane.tools.querySelector('[data-f="updateAll"]') as HTMLButtonElement;
  const placeProgress = placesPane.tools.querySelector('[data-f="progress"]') as HTMLElement;
  placeWatchFilter.addEventListener('change', () => renderPlaces());

  // "In view": the list follows the map — pan or zoom and it re-narrows to
  // what the viewport actually shows.
  const placeInViewCb = placesPane.tools.querySelector('[data-f="inview"]') as HTMLInputElement;
  let placesInView = overlayPrefs()['_placesInView'] ?? false;
  placeInViewCb.checked = placesInView;
  placeInViewCb.addEventListener('change', () => {
    placesInView = placeInViewCb.checked;
    saveOverlayPref('_placesInView', placesInView);
    renderPlaces();
  });
  map.on('moveend', debounced(() => { if (placesInView) renderPlaces(); }, 200));

  function placeInBounds(p: EntityDto, b: maplibregl.LngLatBounds): boolean {
    const shape = placeShape(p);
    let w: number; let s: number; let e: number; let n: number;
    if (shape) {
      [w, s, e, n] = shape.bbox;
    } else if (p.geometry) {
      // Lines and other shapes placeShape doesn't box: rough bbox over every
      // coordinate pair in the geometry.
      const nums = JSON.stringify(p.geometry).match(/-?\d+\.?\d*(e-?\d+)?/gi);
      if (!nums || nums.length < 2) return false;
      const coords = nums.map(Number);
      const lons = coords.filter((_, i) => i % 2 === 0);
      const lats = coords.filter((_, i) => i % 2 === 1);
      w = Math.min(...lons); e = Math.max(...lons);
      s = Math.min(...lats); n = Math.max(...lats);
    } else {
      return false;
    }
    return e >= b.getWest() && w <= b.getEast() && n >= b.getSouth() && s <= b.getNorth();
  }

  async function toggleWatch(p: EntityDto, watched: boolean): Promise<void> {
    try {
      await put('/api/monitor/watch', { entityId: p.id, watched });
      watchOverrides[p.id] = watched;
      renderPlaces();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not change watch state.');
    }
  }
  placeSort.addEventListener('change', () => renderPlaces());
  placeFilter.addEventListener('input', debounced(() => renderPlaces()));

  function focusPlace(p: EntityDto): void {
    const shape = placeShape(p);
    if (!shape) return;
    if (!map.getSource('place-focus')) {
      map.addSource('place-focus', {
        type: 'geojson', data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'place-focus', type: 'line', source: 'place-focus',
        paint: { 'line-color': '#f4b860', 'line-width': 2, 'line-dasharray': [3, 2] },
      });
    }
    // setData replaces the previous outline, so focusing one clears the last.
    (map.getSource('place-focus') as maplibregl.GeoJSONSource).setData({
      type: 'Feature', geometry: shape.polygon, properties: {},
    });
    const [w, s, e, n] = shape.bbox;
    map.fitBounds([[w, s], [e, n]], { padding: 60, maxZoom: 14 });
  }

  // A place's country: imports stamp attrs.country ("Iran", possibly
  // ";"-joined near borders — first segment wins); events/admin resolution
  // uses admin0. Empty when neither is set (hand-placed markers).
  function placeCountry(p: EntityDto): string {
    return String(p.attrs.country ?? p.attrs.admin0 ?? '').split(';')[0]!.trim();
  }

  function sortedPlaces(): EntityDto[] {
    const needle = placeFilter.value.trim().toLowerCase();
    const wf = placeWatchFilter.value;
    const bounds = placesInView ? map.getBounds() : null;
    const list = places.filter((p) =>
      (!needle || (p.name ?? '').toLowerCase().includes(needle)
        || p.type.includes(needle) || p.category.includes(needle)
        || placeCountry(p).toLowerCase().includes(needle))
      && (wf === 'all' || (wf === 'watched' ? isWatched(p) : !isWatched(p)))
      && (!bounds || placeInBounds(p, bounds)));
    const latest = (id: string) => monitorEntries[id]?.scenes?.[0]?.date ?? null;
    const passMs = (id: string) => {
      const dt = monitorEntries[id]?.nextPass?.datetime;
      return dt ? Date.parse(dt) : null;
    };
    if (placeSort.value === 'latest') {
      return list.sort((a, b) => {
        const da = latest(a.id);
        const db = latest(b.id);
        if (da && db) return db.localeCompare(da);
        return (db ? 1 : 0) - (da ? 1 : 0);
      });
    }
    if (placeSort.value === 'pass') {
      return list.sort((a, b) => {
        const pa = passMs(a.id);
        const pb = passMs(b.id);
        if (pa != null && pb != null) return pa - pb;
        return (pa != null ? 0 : 1) - (pb != null ? 0 : 1);
      });
    }
    if (placeSort.value === 'country') {
      // Countryless places sink to the end; renderPlaces groups by country
      // when this sort is active, so the order here is also the group order.
      return list.sort((a, b) =>
        NAME_COLLATOR.compare(placeCountry(a) || '￿', placeCountry(b) || '￿')
        || NAME_COLLATOR.compare(a.name ?? '', b.name ?? ''));
    }
    return list.sort((a, b) => NAME_COLLATOR.compare(a.name ?? '', b.name ?? ''));
  }

  function placeRow(p: EntityDto): HTMLElement {
    const e = monitorEntries[p.id];
    const shape = placeShape(p);
    const row = document.createElement('div');
    row.className = 'row bm-row';
    if (checking.has(p.id)) row.classList.add('bm-checking');

    // Scene lines appear once there is something to say; until then the row
    // stays a compact one-liner rather than three columns of em-dashes.
    let detail: string;
    if (e) {
      const latest = fmtScene(e.scenes?.[0]);
      const prev = e.scenes?.[1] ? `prev ${fmtScene(e.scenes[1])}` : '';
      let next = '—';
      let nextTitle = '';
      if (e.nextPass?.datetime) {
        next = fmtDateTime(e.nextPass.datetime);
        const track = e.nextPass.closestTrackDistanceKm;
        nextTitle = `${e.nextPass.satellite ?? 'Sentinel-2'}`
          + `${track == null ? '' : ` · closest track ${Math.round(track)} km`}`;
      } else if (e.passError) {
        next = 'unavailable';
        nextTitle = e.passError;
      } else {
        next = 'no pass in 14 days';
      }
      const checked = e.updatedAt ? `checked ${relTime(e.updatedAt)} ago` : 'never checked';
      detail = `
        <span class="s">${esc(latest)}${prev ? ` <em>${esc(prev)}</em>` : ''}</span>
        <span class="s bm-next" title="${esc(nextTitle)}">next ${esc(next)}</span>
        <span class="s bm-checked">${esc(checked)}</span>`;
    } else {
      detail = `<span class="s">${esc(
        registry.types.find((t) => t.key === p.type)?.label ?? p.type)}</span>`;
    }

    const watched = isWatched(p);
    const country = placeCountry(p);
    row.innerHTML = `
      <span class="t">
        <span class="n">${esc(p.name ?? '(unnamed)')}${country
          ? ` <em class="bm-cty">${esc(country)}</em>` : ''}</span>${detail}
      </span>
      ${shape ? `<span class="bm-actions">
        <label class="watch-toggle" title="Watch for imagery — included in Update all">
          <input type="checkbox" data-f="watch" ${watched ? 'checked' : ''} /></label>
        <button type="button" class="mini" data-f="studio" title="Open in the comparison Studio">Studio</button>
        <button type="button" class="mini" data-f="check" title="Re-check latest scenes and next pass"><span class="ref-glyph">↻</span></button>
      </span>` : ''}`;
    if (shape && !watched) row.classList.add('place-unwatched');
    if (e?.scenesError) {
      row.classList.add('bm-stale');
      row.title = e.scenesError;
    }
    row.querySelector('[data-f="watch"]')?.addEventListener('change', (ev) => {
      ev.stopPropagation();
      void toggleWatch(p, (ev.currentTarget as HTMLInputElement).checked);
    });
    row.querySelector('[data-f="watch"]')?.addEventListener('click',
      (ev) => ev.stopPropagation());
    row.addEventListener('click', () => focusPlace(p));
    row.querySelector('[data-f="studio"]')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      // Passing the polygon matters for scene-cache reuse; the place ref is
      // what arms Studio's boundary-redraw button.
      if (shape) {
        void openStudio(shape.bbox, shape.polygon, { id: p.id, name: p.name }, {
          // Damage recorded in Studio has to reach the cockpit's own map and
          // panes; Studio's tiles are a separate instance and cannot do it.
          onEntityAdded: (created) => {
            bumpTiles(map, created.rev);
            void refresh();
          },
        });
      }
    });
    row.querySelector('[data-f="check"]')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      void checkPlace(p.id);
    });
    return row;
  }

  function renderPlaces(): void {
    if (!paneShowing('places')) return;
    placesPane.body.innerHTML = '';
    if (!places.length) {
      placesPane.body.innerHTML = `<div class="empty">No places yet — draw a
        watched area with the Draw area tool, or accept a suggestion.</div>`;
      return;
    }
    const rows = sortedPlaces();
    if (!rows.length) {
      // Say which filter is doing the narrowing: "In view" is a persisted
      // checkbox, and a search that comes back empty because of it reads as
      // the place not existing.
      const narrowing: string[] = [];
      if (placesInView) narrowing.push('untick “In view” to search everywhere');
      if (placeWatchFilter.value !== 'all') narrowing.push('watch filter is set');
      const q = placeFilter.value.trim();
      const what = q ? `No places match “${esc(q)}”` : 'No places to show';
      const where = placesInView ? ' in the current view' : '';
      placesPane.body.innerHTML =
        `<div class="empty">${what}${where}.${narrowing.length ? ' ' + esc(narrowing.join('; ')) + '.' : ''}</div>`;
      return;
    }
    if (referenceTruncated) {
      const warn = document.createElement('div');
      warn.className = 'empty';
      warn.textContent = `Listing is capped at ${ENTITY_FETCH_CAP} reference objects — some places may be missing.`;
      placesPane.body.appendChild(warn);
    }

    // Grouped by type (Bases, Power plants, Watched areas…): thousands of
    // imported places in one flat list were unusable and slow. Bulky groups
    // start collapsed; searching always shows matches. Each expanded group
    // renders a bounded number of rows — the DOM cap is what keeps the pane
    // responsive. The Country sort regroups by country instead, in the order
    // sortedPlaces already produced (alphabetical, countryless last).
    const needle = placeFilter.value.trim();
    const byCountry = placeSort.value === 'country';
    const groups = new Map<string, EntityDto[]>();
    for (const p of rows) {
      const gkey = byCountry ? (placeCountry(p) || 'No country') : p.type;
      let bucket = groups.get(gkey);
      if (!bucket) {
        bucket = [];
        groups.set(gkey, bucket);
      }
      bucket.push(p);
    }
    const typeSort = new Map(registry.types.map((t) => [t.key, t.sort]));
    const typeLabel = new Map(registry.types.map((t) => [t.key, t.label]));
    const ordered = byCountry
      ? [...groups.entries()]   // Map preserves the sorted insertion order
      : [...groups.entries()].sort((a, b) =>
          (typeSort.get(a[0]) ?? 99) - (typeSort.get(b[0]) ?? 99));
    const collapsed = groupPrefs();
    for (const [typeKey, items] of ordered) {
      const key = byCountry ? `place:cty:${typeKey}` : `place:${typeKey}`;
      const isCollapsed = !needle && (collapsed[key] ?? items.length > 50);
      const head = document.createElement('div');
      head.className = `grp-head${isCollapsed ? ' collapsed' : ''}`;
      head.innerHTML = `<span class="tw">${isCollapsed ? '▸' : '▾'}</span>
        ${esc(byCountry ? typeKey : typeLabel.get(typeKey) ?? typeKey)}<span class="grp-count">${items.length}</span>`;
      head.addEventListener('click', () => {
        saveGroupPref(key, !(collapsed[key] ?? items.length > 50));
        renderPlaces();
      });
      placesPane.body.appendChild(head);
      if (isCollapsed) continue;
      for (const p of items.slice(0, MAX_PLACE_ROWS)) {
        placesPane.body.appendChild(placeRow(p));
      }
      if (items.length > MAX_PLACE_ROWS) {
        const note = document.createElement('div');
        note.className = 'empty';
        note.textContent =
          `…${items.length - MAX_PLACE_ROWS} more — search to narrow.`;
        placesPane.body.appendChild(note);
      }
    }
  }

  async function checkPlace(id: string): Promise<void> {
    if (checking.has(id)) return;
    checking.add(id);
    renderPlaces();  // show the spinner state
    try {
      monitorEntries[id] = await post<MonitorEntry>('/api/monitor/check', { key: id });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Monitor check failed.');
    } finally {
      checking.delete(id);
    }
    renderPlaces();
  }

  async function updateAll(): Promise<void> {
    // Only watched places are swept; others stay manual (per-row ↻).
    const queue = places.filter((p) => scannable(p) && isWatched(p)).map((p) => p.id);
    const total = queue.length;
    if (!total) { toast('No watched places to update.'); return; }
    placeUpdateAll.disabled = true;
    let done = 0;
    placeProgress.textContent = `0 / ${total}`;
    const worker = async () => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        await checkPlace(id);
        done += 1;
        placeProgress.textContent = `${done} / ${total}`;
      }
    };
    await Promise.all(Array.from({ length: Math.min(CHECK_CONCURRENCY, total) }, worker));
    placeProgress.textContent = `updated ${total}`;
    placeUpdateAll.disabled = false;
  }
  placeUpdateAll.addEventListener('click', () => void updateAll());

  async function loadMonitor(): Promise<void> {
    try {
      const dto = await get<{ squareKm: number; entries: Record<string, MonitorEntry>;
                              watch?: Record<string, boolean> }>('/api/monitor');
      Object.assign(monitorEntries, dto.entries);
      Object.assign(watchOverrides, dto.watch ?? {});
      renderPlaces();
    } catch { /* cache load is best-effort */ }
  }
  void loadMonitor();

  // --- Bookmarks (personal nav pins from /api/bookmarks — not entities, not
  // shared, rendered client-side) -------------------------------------------

  let myBookmarks: BookmarkDto[] = [];
  let bookmarksLocked = false;  // guests have no user account to save under

  // Whether they DRAW is a display choice, per browser like every other layer
  // toggle: "On map" hides every pin at once (the Layers panel's "My
  // bookmarks" row is the same switch — both write through setBookmarksShown),
  // and a row's own checkbox hides one pin without deleting it. The list keeps
  // every bookmark either way, so a hidden pin is still one click from flying
  // there.
  let bmShown = overlayPrefs()['_bookmarks'] ?? true;
  const bmHidden: Set<string> = (() => {
    try { return new Set(JSON.parse(localStorage.getItem(BM_HIDDEN_KEY) ?? '[]') as string[]); }
    catch { return new Set<string>(); }
  })();
  const saveBmHidden = () => localStorage.setItem(BM_HIDDEN_KEY, JSON.stringify([...bmHidden]));
  // Markup (markup.ts) is the operator's own annotation of the map — private
  // like bookmarks, hidden the same way, and drawn from its own source rather
  // than the shared tiles.
  let markupOn = overlayPrefs()['_markup'] ?? true;
  setMarkupVisible(map, markupOn);
  // Set once the Layers panel exists: bookmarks off is a "not default" state
  // its button marks, like any other hidden layer.
  let syncLayersBtn: (() => void) | null = null;
  setUserBookmarksVisible(map, bmShown);
  setHiddenUserBookmarks(map, bmHidden);

  bmPane.tools.hidden = false;
  bmPane.tools.innerHTML = `
    <button type="button" class="mini" data-f="add"
      title="Drop a bookmark on the map">+ Add bookmark</button>
    <label class="mini inview-toggle" data-f="shown"
      title="Draw your bookmarks on the map. Untick to hide every pin; a row’s own checkbox hides just that one.">
      <input type="checkbox" data-bookmarks-toggle /> On map</label>
    <button type="button" class="mini" data-f="unhide" hidden
      title="Show every bookmark you hid one by one"></button>`;
  const bmAdd = bmPane.tools.querySelector('[data-f="add"]') as HTMLButtonElement;
  const bmShownLabel = bmPane.tools.querySelector('[data-f="shown"]') as HTMLElement;
  const bmUnhide = bmPane.tools.querySelector('[data-f="unhide"]') as HTMLButtonElement;
  bmShownLabel.querySelector('input')!.addEventListener('change', (ev) => {
    setBookmarksShown((ev.currentTarget as HTMLInputElement).checked);
  });

  // Everything that reads the show/hide state outside a row: both switches,
  // the Layers row's count, the "Show N hidden" clear and the Layers button.
  function syncBookmarkUi(): void {
    let hiddenCount = 0;
    for (const b of myBookmarks) if (bmHidden.has(b.id)) hiddenCount += 1;
    document.querySelectorAll<HTMLInputElement>('[data-bookmarks-toggle]')
      .forEach((el) => { el.checked = bmShown; });
    document.querySelectorAll<HTMLElement>('[data-bookmarks-count]').forEach((el) => {
      el.textContent = String(myBookmarks.length - hiddenCount);
      el.hidden = !bmShown || !myBookmarks.length;
    });
    // [hidden] loses to .ovl-row's and .inview-toggle's display; toggle display.
    document.querySelectorAll<HTMLElement>('[data-bookmarks-row]')
      .forEach((el) => { el.style.display = bookmarksLocked ? 'none' : ''; });
    bmShownLabel.style.display = bookmarksLocked ? 'none' : '';
    bmUnhide.hidden = hiddenCount === 0;
    bmUnhide.textContent = `Show ${hiddenCount} hidden`;
    bmPane.body.classList.toggle('bm-layer-off', !bmShown);
    syncLayersBtn?.();
  }

  function setBookmarksShown(on: boolean): void {
    bmShown = on;
    saveOverlayPref('_bookmarks', on);
    setUserBookmarksVisible(map, on);
    syncBookmarkUi();
  }

  function setBookmarkHidden(id: string, hide: boolean): void {
    if (hide) bmHidden.add(id); else bmHidden.delete(id);
    saveBmHidden();
    setHiddenUserBookmarks(map, bmHidden);
    // Ticking one pin back on while the whole layer is off would change
    // nothing on the map, and a checkbox that visibly does nothing reads as
    // broken — so showing a pin shows the layer.
    if (!hide && !bmShown) setBookmarksShown(true);
    else syncBookmarkUi();
  }

  bmUnhide.addEventListener('click', () => {
    bmHidden.clear();
    saveBmHidden();
    setHiddenUserBookmarks(map, bmHidden);
    if (!bmShown) setBookmarksShown(true);
    renderBookmarks();
  });

  function bookmarkBounds(b: BookmarkDto): BBox | null {
    if (b.geometry.type === 'Point') {
      const [lon, lat] = b.geometry.coordinates as [number, number];
      return pointBox(lon, lat);
    }
    if (b.geometry.type === 'Polygon') {
      const ring = b.geometry.coordinates[0] ?? [];
      if (ring.length < 4) return null;
      const lons = ring.map((c) => c[0] ?? 0);
      const lats = ring.map((c) => c[1] ?? 0);
      return [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)];
    }
    return null;
  }

  function bookmarkRow(b: BookmarkDto): HTMLElement {
    const row = document.createElement('div');
    row.className = `row bm-row${bmHidden.has(b.id) ? ' bm-off' : ''}`;
    row.innerHTML = `
      <span class="t">
        <span class="n">${esc(b.name)}</span>
        ${b.note ? `<span class="s">${esc(truncate(b.note, 90))}</span>` : ''}
        <span class="s bm-checked">saved ${relTime(b.createdAt)} ago<span
          class="bm-hid"> · hidden on the map</span></span>
      </span>
      <span class="bm-actions">
        <label class="watch-toggle" title="Show this bookmark on the map">
          <input type="checkbox" data-f="vis" ${bmHidden.has(b.id) ? '' : 'checked'} /></label>
        <button type="button" class="mini" data-f="remove" title="Delete this bookmark">✕</button>
      </span>`;
    row.addEventListener('click', () => {
      const box = bookmarkBounds(b);
      if (!box) return;
      const [w, s, e, n] = box;
      map.fitBounds([[w, s], [e, n]], { padding: 60, maxZoom: 13 });
    });
    const visCb = row.querySelector('[data-f="vis"]') as HTMLInputElement;
    // The row's own click flies to the bookmark; ticking its box must not.
    visCb.closest('label')!.addEventListener('click', (ev) => ev.stopPropagation());
    visCb.addEventListener('change', () => {
      row.classList.toggle('bm-off', !visCb.checked);
      setBookmarkHidden(b.id, !visCb.checked);
    });
    row.querySelector('[data-f="remove"]')!.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      try {
        await del(`/api/bookmarks/${b.id}`);
        myBookmarks = myBookmarks.filter((x) => x.id !== b.id);
        if (bmHidden.delete(b.id)) saveBmHidden();
        setUserBookmarks(map, myBookmarks);
        renderBookmarks();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Delete failed.');
      }
    });
    return row;
  }

  function renderBookmarks(): void {
    bmPane.body.innerHTML = '';
    if (bookmarksLocked) {
      bmPane.body.innerHTML = '<div class="empty">Personal bookmarks need a user account.</div>';
    } else if (!myBookmarks.length) {
      bmPane.body.innerHTML = `<div class="empty">No bookmarks yet — your own
        pins, visible only to you. Use + Add bookmark, then click the map.</div>`;
    } else {
      for (const b of myBookmarks) bmPane.body.appendChild(bookmarkRow(b));
    }
    syncBookmarkUi();
  }

  async function loadBookmarks(): Promise<void> {
    try {
      const dto = await get<{ bookmarks: BookmarkDto[] }>('/api/bookmarks');
      myBookmarks = dto.bookmarks;
      // A bookmark deleted in another tab or on another device leaves its id
      // behind in this browser's hidden set; drop those.
      const ids = new Set(myBookmarks.map((b) => b.id));
      const stale = [...bmHidden].filter((id) => !ids.has(id));
      if (stale.length) {
        for (const id of stale) bmHidden.delete(id);
        saveBmHidden();
      }
      setUserBookmarks(map, myBookmarks);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        bookmarksLocked = true;
        bmAdd.hidden = true;
      }
    }
    renderBookmarks();
  }
  void loadBookmarks();

  bmAdd.addEventListener('click', () => {
    toast('Click the map to drop a bookmark (Esc cancels).');
    map.getCanvas().style.cursor = 'crosshair';
    const cancel = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      map.off('click', place);
      map.getCanvas().style.cursor = '';
      document.removeEventListener('keydown', cancel);
    };
    const place = async (ev: maplibregl.MapMouseEvent) => {
      map.getCanvas().style.cursor = '';
      document.removeEventListener('keydown', cancel);
      const name = prompt('Bookmark name:')?.trim();
      if (!name) return;
      try {
        const created = await post<BookmarkDto>('/api/bookmarks', {
          name,
          geometry: { type: 'Point', coordinates: [ev.lngLat.lng, ev.lngLat.lat] },
        });
        myBookmarks = [created, ...myBookmarks];
        setUserBookmarks(map, myBookmarks);
        // A pin dropped into a hidden layer would vanish the moment it landed.
        if (!bmShown) setBookmarksShown(true);
        renderBookmarks();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not save the bookmark.');
      }
    };
    map.once('click', place);
    document.addEventListener('keydown', cancel);
  });

  // --- Live feed -----------------------------------------------------------

  livePane.tools.hidden = false;
  livePane.tools.classList.add('live-tools');
  // Fade time: events dim as they age and drop once past it, on the feed AND the
  // map. Per-browser override (saved to localStorage); starts from the admin
  // default. 0 = never fade / show all. Standard steps plus whatever the current
  // value is, so an admin default like 8h still shows selected.
  const fadeSteps = [...new Set([1, 3, 6, 12, 24, 48,
    ...(liveFadeHours() > 0 ? [liveFadeHours()] : [])])].sort((a, b) => a - b);
  // Two deliberate rows, not one overflowing one: the top row is what a
  // reader touches constantly (which feed, find a thing) and the search gets
  // the leftover width; the bottom row is the display knobs. One row held
  // seven controls and the SEARCH was what got squeezed — the control whose
  // whole value is having room to type.
  livePane.tools.innerHTML = `
    <div class="lt-row">
      <span class="feed-mode" role="group" aria-label="Feed mode">
        <button type="button" class="mini" data-f="mode-sits"
          title="Situations — reports grouped into developing incidents">Situations</button>
        <button type="button" class="mini" data-f="mode-all"
          title="Everything — every event as its own row, strictly by when we last heard. The trust valve: what grouping folds, this shows raw.">All</button>
        <button type="button" class="mini" data-f="mode-news"
          title="News — conflict reporting with no place on the map: statements, diplomacy, economic effects, casualty logistics. Never pinned, never grouped into a situation.">News</button>
      </span>
      <input type="text" data-f="q" placeholder="Search place, text…" />
    </div>
    <div class="lt-row">
      <select data-f="fade" title="Time window — the feed FILTERS to this (older events wait behind &quot;show 10 more&quot;); the map ages dots toward gone across it">
        ${fadeSteps.map((h) =>
          `<option value="${h}"${h === liveFadeHours() ? ' selected' : ''}>last ${h}h</option>`).join('')}
        <option value="0"${liveFadeHours() <= 0 ? ' selected' : ''}>all time</option>
      </select>
      <select data-f="kind" title="Event kind">
        <option value="all">all kinds</option>
        ${Object.entries(LIVE_KINDS)
          .map(([k, v]) => `<option value="${k}">${v.glyph} ${v.label}</option>`).join('')}
      </select>
      <button type="button" class="mini" data-f="colorby"
        title="Colour the feed accent by event type instead of age">🎨</button>
      <button type="button" class="mini" data-f="dim"
        title="Dim feed rows as they age (off: every row inside the window renders at full strength — the window filters, it does not fade)">🌗</button>
      <button type="button" class="mini" data-f="thumbs"
        title="Thumbnails on feed rows (on by default). Off: a row with footage shows a ▶ 3 mark instead — click it to view.">🖼</button>
      <button type="button" class="mini" data-f="bubble"
        title="Bubble updates: sort by last report, so an old event a new post just enriched jumps to the top. Off (default): the feed orders by when things HAPPENED, and updates stay in place wearing an ↻ update chip — late detail is not a live event.">upd ⤒</button>
      <button type="button" class="mini" data-f="rings"
        title="Ring colours — colour event rings by age, by attributed faction, or by attack vs impact. Live map and replay each pick their own mode.">◎ rings</button>
      ${me.isAdmin
        ? '<button type="button" class="mini" data-f="clear" title="Clear all live events">Clear</button>'
        : ''}
    </div>`;
  const liveFade = livePane.tools.querySelector('[data-f="fade"]') as HTMLSelectElement;
  const liveKind = livePane.tools.querySelector('[data-f="kind"]') as HTMLSelectElement;
  const liveSearch = livePane.tools.querySelector('[data-f="q"]') as HTMLInputElement;
  // Situations (grouped, the default) vs Everything (one row per event, no
  // grouping). The flat view is the TRUST VALVE: whenever the smart feed makes
  // you wonder whether grouping is hiding something, one click shows the tape.
  // A third lane, not a third filter: news items are a different KIND of record
  // (no coordinate, no event kind, no situation), so they get their own view
  // rather than diluting the two that answer "what happened where".
  type FeedMode = 'situations' | 'everything' | 'news';
  const storedMode = localStorage.getItem(FEEDMODE_KEY);
  let feedMode: FeedMode = storedMode === 'everything' ? 'everything'
    : storedMode === 'news' ? 'news' : 'situations';
  const modeSits = livePane.tools.querySelector('[data-f="mode-sits"]') as HTMLButtonElement;
  const modeAll = livePane.tools.querySelector('[data-f="mode-all"]') as HTMLButtonElement;
  const modeNews = livePane.tools.querySelector('[data-f="mode-news"]') as HTMLButtonElement;
  const syncModeBtns = () => {
    modeSits.classList.toggle('active', feedMode === 'situations');
    modeAll.classList.toggle('active', feedMode === 'everything');
    modeNews.classList.toggle('active', feedMode === 'news');
    // The kind filter is an EVENT vocabulary and news has no kind. Disabling it
    // rather than silently ignoring it: a control that is visibly on while
    // doing nothing is how "the toggles are broken" gets reported.
    liveKind.disabled = feedMode === 'news';
    liveKind.title = feedMode === 'news'
      ? 'Event kinds do not apply to news — news items have no kind'
      : 'Event kind';
  };
  syncModeBtns();
  const setFeedMode = (m: FeedMode) => {
    feedMode = m;
    localStorage.setItem(FEEDMODE_KEY, m);
    // The paging state belongs to the lane you were reading; carrying "show 10
    // more" across a lane switch would reveal a count nobody asked for.
    liveExtra = 0;
    capExtra = 0;
    syncModeBtns();
    renderLive();
  };
  modeSits.addEventListener('click', () => setFeedMode('situations'));
  modeAll.addEventListener('click', () => setFeedMode('everything'));
  modeNews.addEventListener('click', () => setFeedMode('news'));
  // Age-dimming is OPT-IN: the window already filters, and dimming on top of a
  // filter punished exactly the rows the reader asked to keep.
  const dimBtn = livePane.tools.querySelector('[data-f="dim"]') as HTMLButtonElement;
  let feedDim = localStorage.getItem(FEEDDIM_KEY) === '1';
  dimBtn.classList.toggle('active', feedDim);
  dimBtn.addEventListener('click', () => {
    feedDim = !feedDim;
    localStorage.setItem(FEEDDIM_KEY, feedDim ? '1' : '0');
    dimBtn.classList.toggle('active', feedDim);
    renderLive();
  });
  // Every row with footage carries ONE tiny thumbnail beside its words
  // (media.ts). This turns them off for a reader who wants text only; a
  // "▶ 3" mark still says the footage is there, and opens it.
  const thumbsBtn = livePane.tools.querySelector('[data-f="thumbs"]') as HTMLButtonElement;
  thumbsBtn.classList.toggle('active', feedThumbsOn());
  thumbsBtn.addEventListener('click', () => {
    setFeedThumbs(!feedThumbsOn());
    thumbsBtn.classList.toggle('active', feedThumbsOn());
    renderLive();
  });
  // Update-bubbling is OPT-IN (2026-07-30 product decision, reversing the
  // original "a feed is a newsroom" default): a 5-hour-old tanker fire whose
  // coverage post landed 3 minutes ago was sitting ABOVE genuinely new
  // events, wearing a fresh-looking clock. Still-being-reported is different
  // from happening-now. Default order is occurrence; this button restores
  // newsroom order for readers who want updates to resurface.
  const bubbleBtn = livePane.tools.querySelector('[data-f="bubble"]') as HTMLButtonElement;
  let feedBubble = localStorage.getItem(FEEDBUBBLE_KEY) === '1';
  bubbleBtn.classList.toggle('active', feedBubble);
  bubbleBtn.addEventListener('click', () => {
    feedBubble = !feedBubble;
    localStorage.setItem(FEEDBUBBLE_KEY, feedBubble ? '1' : '0');
    bubbleBtn.classList.toggle('active', feedBubble);
    renderLive();
  });
  // Ring colours, reachable WITHOUT opening the replay transport — the rules
  // colour the live map too, so their editor cannot live only on the timebar.
  // Same shared component the transport mounts (colorpick.ts); this one drops
  // BELOW the toolbar instead of above it.
  const ringsPopover = createColorsPopover(livePane.tools as HTMLElement,
    () => opts.map, 'colors-below');
  livePane.tools.querySelector('[data-f="rings"]')!.addEventListener('click', () =>
    ringsPopover.toggle());
  // "Show 10 more" paging: how far past the window (liveExtra) and past the
  // render cap (capExtra) the reader has asked to see. Reset when the window
  // changes — the question they were digging into has been re-asked.
  let liveExtra = 0;
  let capExtra = 0;
  // Feed colouring cycles age -> type accent -> type tint. `age` keeps the
  // left strip on the red->grey age ramp; `type` colours the strip by kind;
  // `tint` also washes the row body in the kind colour for the strongest split.
  const colorByBtn = livePane.tools.querySelector('[data-f="colorby"]') as HTMLButtonElement;
  const COLOR_MODES = ['age', 'type', 'tint'] as const;
  type ColorMode = typeof COLOR_MODES[number];
  const COLOR_LABEL: Record<ColorMode, string> = {
    age: '🎨 age', type: '🎨 type', tint: '🎨 tint',
  };
  const COLOR_TITLE: Record<ColorMode, string> = {
    age: 'Accent by event age — click to colour by type',
    type: 'Accent by event type — click to also tint the row',
    tint: 'Rows tinted by event type — click to reset to age',
  };
  let colorMode = (localStorage.getItem(COLORBY_KEY) ?? 'age') as ColorMode;
  if (!COLOR_MODES.includes(colorMode)) colorMode = 'age';
  const syncColorBtn = () => {
    colorByBtn.textContent = COLOR_LABEL[colorMode];
    colorByBtn.title = COLOR_TITLE[colorMode];
    colorByBtn.classList.toggle('active', colorMode !== 'age');
  };
  syncColorBtn();
  colorByBtn.addEventListener('click', () => {
    colorMode = COLOR_MODES[(COLOR_MODES.indexOf(colorMode) + 1) % COLOR_MODES.length]!;
    localStorage.setItem(COLORBY_KEY, colorMode);
    syncColorBtn();
    renderLive();
  });
  liveFade.addEventListener('change', () => {
    const hours = Number(liveFade.value);
    setLiveTiming({ fadeHours: hours });
    localStorage.setItem(FADE_KEY, String(hours));
    liveExtra = 0;
    capExtra = 0;
    setLiveEvents(map, liveEvents);   // re-bake map dots with the new window
    renderLive();
  });
  liveKind.addEventListener('change', () => renderLive());
  // Debounced like the other search boxes: a repaint per keystroke.
  liveSearch.addEventListener('input', debounced(() => renderLive()));
  livePane.tools.querySelector('[data-f="clear"]')?.addEventListener('click', () => {
    if (!confirm('Clear all live events?')) return;
    post('/api/live/clear')
      .then(() => refresh())
      .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Clear failed.'));
  });

  // Source posts as small "@channel ↗" links (up to `max`, then a +N). Each
  // opens the Telegram post in a new tab and never bubbles to the row click.
  // The tooltip on a citation carries the operator's tradecraft — rating when
  // it isn't the default, and the treatment note ("often breaks news first,
  // verify") — so how to read a source arrives at the moment of reading it.
  function sourceTip(channel: string, s?: SourceLink): string {
    const parts: string[] = [];
    const rel = reliabilityOf(channel);
    if (rel && rel !== 'standard') parts.push(`${rel} reliability`);
    const note = treatmentOf(channel);
    if (note) parts.push(note);
    const media = s ? mediaLabel(s) : '';
    if (media) parts.push(`post carries ${media}`);
    const role = s ? roleLabel(s) : '';
    if (role) parts.push(role);
    return parts.length
      ? `${channelLabel(channel) || 'post'} — ${parts.join(' · ')}`
      : 'Open source post';
  }

  // A ▶ (video) or ◳ (photo) on the citation marks which post actually has
  // the footage — the thing you click through for. Only ever an assertion of
  // PRESENCE: a source with no media renders nothing, because absence here
  // also covers every event stored before media detection existed, and
  // "no video" would be a claim the data cannot support.
  function sourceLinksHtml(attrs: Record<string, unknown>, max = 2): string {
    const links = sourceLinks(attrs);
    if (!links.length) return '';
    const shown = links.slice(0, max).map((s) => {
      const g = mediaGlyph(s.media);
      return `<a class="src-link" href="${esc(s.url)}" target="_blank" rel="noopener"
         title="${esc(sourceTip(s.channel, s))}">${esc(channelLabel(s.channel) || 'post')}${
        g ? `<b class="src-media">${g}</b>` : ''}<i>↗</i></a>`;
    }).join('');
    // The +N stands for links that are not shown, so it has to answer "is the
    // footage in there" as well — otherwise the glyph is a lie by omission.
    const restMedia = bestMedia(links.slice(max));
    const more = links.length > max
      ? `<span class="src-more" title="${
          restMedia ? `${links.length - max} more, including ${restMedia}`
                    : `${links.length - max} more`}">+${links.length - max}${
          mediaGlyph(restMedia)}</span>`
      : '';
    return `<span class="src-links">${shown}${more}</span>`;
  }

  // --- corroboration + triage state ----------------------------------------
  // Heat is fetched per view and cached here. A MISS is recorded too, so an event
  // with no anomaly is asked about once rather than on every 30 s re-render.
  const HEAT_MISS: HeatHit = { km: 0, at: '', satellite: null, unusual: false };
  let heatById: Record<string, HeatHit> = {};
  let heatPending = false;
  // The recycled-image caution, cached exactly like heat — same endpoint
  // shape, same miss-recording, opposite meaning.
  const ECHO_MISS: MediaEcho = { url: '', channel: null, at: '' };
  let echoById: Record<string, MediaEcho> = {};
  let echoPending = false;
  let lastShown: EntityDto[] = [];
  /** The situation cards painted, by key — what refreshUnreadMarks reads. */
  let shownGroups = new Map<string, EntityDto[]>();

  async function refreshHeat(events: EntityDto[]): Promise<void> {
    if (heatPending) return;
    const ask = events.map((e) => e.id).filter((id) => !(id in heatById)).slice(0, 200);
    if (!ask.length) return;
    heatPending = true;
    const got = await fetchHeat(ask);
    for (const id of ask) heatById[id] = got[id] ?? HEAT_MISS;
    heatPending = false;
    // Only re-render when something was actually found; otherwise a view with no
    // anomalies would repaint for nothing every poll.
    if (Object.keys(got).length) renderLive();
  }

  async function refreshEcho(events: EntityDto[]): Promise<void> {
    if (echoPending) return;
    const ask = events.map((e) => e.id).filter((id) => !(id in echoById)).slice(0, 200);
    if (!ask.length) return;
    echoPending = true;
    const got = await fetchMediaEcho(ask);
    for (const id of ask) echoById[id] = got[id] ?? ECHO_MISS;
    echoPending = false;
    if (Object.keys(got).length) renderLive();
  }

  /** Chips for one event: imagery first (a hole is stronger evidence than heat). */
  function corroborationHtml(e: EntityDto): string {
    const chips: Corroboration[] = [];
    const dmg = damageFor(e, damage);
    if (dmg) chips.push(dmg);
    const hot = heatById[e.id];
    if (hot) {
      const chip = heatChip(e, hot);
      if (chip) chips.push(chip);
    }
    const echo = echoById[e.id];
    if (echo?.url) {
      const chip = echoChip(echo);
      if (chip) chips.push(chip);
    }
    if (!chips.length) return '';
    const GLYPH = { damage: '✔', heat: '🔥', echo: '⟳' } as const;
    return `<span class="corrob">${chips.map((c) =>
      `<span class="corrob-chip ${c.kind}" title="${esc(c.title)}"${
        c.entityId ? ` data-open="${esc(c.entityId)}"` : ''
      }>${GLYPH[c.kind]} ${esc(c.label)}</span>`).join('')}</span>`;
  }

  /** Pin / dismiss, always visible — `hover: none` on a phone means anything
   *  revealed only on :hover is unreachable (CLAUDE.md). */
  function triageHtml(e: EntityDto, extra = ''): string {
    return `<span class="triage">
      <button type="button" class="tri" data-pin="${esc(e.id)}"
        title="${isPinned(e) ? 'Unpin' : 'Pin — keep this in front of me'}"
        aria-pressed="${isPinned(e)}">${isPinned(e) ? '★' : '☆'}</button>
      <button type="button" class="tri" data-dismiss="${esc(e.id)}"
        title="Dismiss this report">✕</button>${extra}
    </span>`;
  }

  /** Wire the delegated actions on a row/card. Each stops propagation so it does
   *  not also trigger the row's open-entity click. */
  function wireTriage(host: HTMLElement, events: EntityDto[]): void {
    host.querySelectorAll('[data-pin],[data-dismiss],[data-mute],[data-open]')
      .forEach((el) => el.addEventListener('click', (evt) => {
        evt.stopPropagation();
        const node = el as HTMLElement;
        const find = (id: string) => events.find((x) => x.id === id);
        const pin = node.dataset.pin && find(node.dataset.pin);
        if (pin) { togglePin(pin); return; }
        const drop = node.dataset.dismiss && find(node.dataset.dismiss);
        if (drop) { dismiss(drop); return; }
        const mute = node.dataset.mute && find(node.dataset.mute);
        if (mute) { mutePlace(mute); return; }
        if (node.dataset.open) openEntity(node.dataset.open);
      }));
  }

  // A FEED ROW IS ITS HEADLINE, ITS DESCRIPTION AND ITS PICTURE (2026-09-30).
  // Every row used to stack the where line, the claim line, the sequence
  // chip, the corroboration chips and a column of source links, and a feed
  // of them read as a wall of words in which nobody could tell which reports
  // had footage. Now the row is the title, a description clamped to two
  // lines, a tiny thumbnail (media.ts) and — only when there is one — a
  // FLAGS line saying what is exceptional about it: a denial, a contested
  // outcome, sources all rated low, recycled footage, sources disagreeing,
  // or a measurement agreeing. Those stay on the row because each is a
  // caveat or a verdict about whether the headline holds, and a caveat that
  // needs a click is a caveat a screenshot drops. Everything routine — where,
  // who, how many channels, the sequence, every source and every picture —
  // is the row's DETAILS, behind ▸, built on first open.
  function flagsHtml(...bits: string[]): string {
    const html = bits.filter(Boolean).join('');
    return html ? `<span class="live-flags">${html}</span>` : '';
  }

  const MORE_BTN = `<button type="button" class="row-more" aria-expanded="false"
    title="Details — where, who, channels, every source and picture"><span
    class="car">▸</span></button>`;

  // Rows whose details the reader opened, by event id: kept across the
  // repaint every poll makes, the way expandedSits keeps a card open.
  const openRows = new Set<string>();

  /** Wire a row's ▸ to its `.live-more`, filled by `fill` on first open —
   *  a collapsed feed must not pay for every row's claim line, sequence
   *  lookup and pictures. */
  function wireMore(row: HTMLElement, key: string, fill: (host: HTMLElement) => void): void {
    const more = row.querySelector(':scope > .live-more') as HTMLElement;
    const btn = row.querySelector('.row-more') as HTMLButtonElement;
    let filled = false;
    const show = (open: boolean): void => {
      if (open && !filled) {
        filled = true;
        fill(more);
        more.querySelectorAll('.src-link').forEach((a) =>
          a.addEventListener('click', (ev) => ev.stopPropagation()));
      }
      more.hidden = !open;
      row.classList.toggle('more-open', open);
      btn.setAttribute('aria-expanded', String(open));
    };
    show(openRows.has(key));
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();   // opening the details is not a navigation
      const open = !openRows.has(key);
      if (open) openRows.add(key); else openRows.delete(key);
      show(open);
    });
  }

  // The chip leading a feed title (area.ts): the country or the named water,
  // left out when the title already is that area.
  function areaTagHtml(area: Area | null, shown: string): string {
    if (!area || redundantArea(area, shown)) return '';
    return `<span class="area-tag${area.water ? ' water' : ''}" title="${
      esc(area.title)}">${esc(area.label)}</span>`;
  }

  // The extractor's rating rides EVERY row now (2026-09-01). It used to be
  // opt-in for the two member lists, on the argument that a number on a feed
  // being SCANNED is noise — see claim.ts's confidenceChip for why that was
  // reversed. The claim line drops its own "low confidence" part wherever the
  // chip renders, or a weak row would carry the same caveat twice.
  function liveRow(e: EntityDto): HTMLElement {
    const kind = String(e.attrs.kind ?? 'unclear');
    const km = LIVE_KINDS[kind] ?? UNCLEAR;
    const place = String(e.attrs.place ?? '') || (e.name ?? '(unknown)');
    // summary FIRST: it is the extractor's English rendering. attrs.text is the
    // raw post, and most of these channels post in Arabic or Farsi — preferring
    // it meant the feed was a wall of untranslated source text (and `text` is
    // always set, so the summary never surfaced at all). Raw text stays on the
    // detail card for anyone who wants the original wording.
    const text = String(e.attrs.summary ?? e.attrs.text ?? '');
    // place_name is the current attach key; bookmark is its pre-008 spelling.
    // Suppressed when match_basis is set: that attachment is an INFERENCE from
    // the event's kind (a launch -> nearest missile base within 80 km), so "at X"
    // would assert a location the report never gave. The claim line says
    // "nearest naval base, 37.8 km" instead, which is the honest form.
    const inferredAnchor = Boolean(e.attrs.match_basis);
    const near = inferredAnchor
      ? '' : String(e.attrs.place_name ?? e.attrs.bookmark ?? '');
    let where = km.label;
    if (near) {
      where += ` · at ${near}`;
    } else if (e.attrs.location_kind === 'admin') {
      const admin1 = String(e.attrs.admin1 ?? '');
      where += ` · in ${admin1 ? `${admin1}, ` : ''}${String(e.attrs.admin0 ?? '')}`;
    }

    const media = mediaGroups([e]);

    const row = document.createElement('div');
    row.className = 'row live-row';
    row.dataset.eid = e.id;
    if (colorMode === 'tint') row.classList.add('live-tinted');
    if (isUnread(e)) row.classList.add('live-unread');
    if (isPinned(e)) row.classList.add('live-pinned');
    // Age is a question about the WORLD, so every one of these reads the
    // occurrence clock. They all used to read validFrom, which is when we wrote
    // the row — and a merge rewrites it, so a corroborated event snapped back to
    // full opacity and full-red every time another channel repeated it.
    const at = whenOf(e).at;
    if (feedDim) row.style.opacity = String(feedOpacity(at));   // opt-in dimming
    row.style.setProperty('--kc', kindColor(kind));          // glyph chip + tint
    row.style.setProperty('--pin', colorMode === 'age' ? agePin(at) : kindColor(kind));
    row.innerHTML = `
      <span class="glyph" title="${esc(km.label)}">${km.glyph}</span>
      <span class="t">
        <span class="n">${areaTagHtml(areaFor(e), place)}${esc(place)}</span>
        ${text ? `<span class="s live-desc">${esc(text)}</span>` : ''}
        ${flagsHtml(claimHtml(e.attrs, { cautions: 'only' }), corroborationHtml(e))}
      </span>
      ${feedThumbHtml(media)}
      <span class="live-side">
        ${timeHtml(e)}
        ${confidenceChip(e.attrs)}
        ${triageHtml(e, MORE_BTN)}
      </span>
      <div class="live-more" hidden></div>`;
    wireFeedThumb(row, media);
    wireTriage(row, [e]);
    wireMore(row, e.id, (host) => {
      host.innerHTML = `
        <span class="s live-meta">${esc(where)}</span>
        ${claimHtml(e.attrs, { skipConfidence: true, cautions: 'skip' })}
        ${sequenceChipHtml(e, liveEvents)}
        ${sourceLinksHtml(e.attrs, 6)}
        ${mediaStripHtml(media, 8, ROW_PX)}`;
      wireMediaStrip(host, media);
    });
    if (selectedFeedKey === e.id) row.classList.add('feed-selected');
    row.addEventListener('click', () => {
      if (e.geometry?.type === 'Point') {
        map.flyTo({ center: e.geometry.coordinates as [number, number], zoom: 11 });
      }
      markFeedSelection(row, e.id);
      closeGroupCard();   // the entity card takes the slot
      openEntity(e.id);
    });
    return row;
  }

  /** One news item. Structurally the live row with everything positional taken
   *  out — no glyph chip keyed to a kind, no "at X", no map flight on click,
   *  because none of those mean anything for a record with no coordinate.
   *  What it keeps is what a claim needs: the subject, what this post said, who
   *  it is about, when, and every source post behind it. */
  function newsRow(e: EntityDto): HTMLElement {
    const topic = topicOf(e);
    const headline = newsHeadlineOf(e);
    const actors = actorsOf(e);
    const context = contextOf(e);
    const reports = reportCountOf(e);

    const media = mediaGroups([e]);
    const row = document.createElement('div');
    row.className = 'row live-row news-row';
    row.dataset.eid = e.id;
    if (isUnread(e)) row.classList.add('live-unread');
    if (isPinned(e)) row.classList.add('live-pinned');
    const at = whenOf(e).at;
    if (feedDim) row.style.opacity = String(feedOpacity(at));
    // One flat colour, deliberately: the kind palette encodes what KIND of
    // thing happened, and applying it here would imply news has a kind.
    row.style.setProperty('--kc', '#5b6b7a');
    row.style.setProperty('--pin', '#5b6b7a');
    const meta = [
      context,
      // Corroboration, the one thing news shares with an event claim: several
      // channels carrying one statement is worth saying out loud.
      reports > 1 ? `${reports} reports` : '',
    ].filter(Boolean).join(' · ');
    row.innerHTML = `
      <span class="glyph" title="News — no location">📰</span>
      <span class="t">
        <span class="n">${esc(topic)}</span>
        ${headline && headline !== topic
          ? `<span class="s live-desc">${esc(headline)}</span>` : ''}
      </span>
      ${feedThumbHtml(media)}
      <span class="live-side">
        ${timeHtml(e)}
        ${confidenceChip(e.attrs, '', {
          what: 'a real statement, reported as summarised' })}
        ${triageHtml(e, MORE_BTN)}
      </span>
      <div class="live-more" hidden></div>`;
    wireFeedThumb(row, media);
    wireTriage(row, [e]);
    wireMore(row, e.id, (host) => {
      host.innerHTML = `
        ${meta ? `<span class="s live-meta">${esc(meta)}</span>` : ''}
        ${actors.length
          ? `<span class="news-actors">${actors
              .map((a) => `<span class="news-actor">${esc(a)}</span>`).join('')}</span>`
          : ''}
        ${sourceLinksHtml(e.attrs, 6)}
        ${mediaStripHtml(media, 8, ROW_PX)}`;
      wireMediaStrip(host, media);
    });
    if (selectedFeedKey === e.id) row.classList.add('feed-selected');
    // Opens the entity card and nothing else. No flyTo — there is nowhere to
    // fly, and moving the map for a record with no position would be a lie
    // told by the camera.
    row.addEventListener('click', () => {
      markFeedSelection(row, e.id);
      closeGroupCard();
      openEntity(e.id);
    });
    return row;
  }

  // A GROUP shows its HEADLINE's rating, not an average and not the newest
  // report's: the card's body is the headline's claim, so the number beside it
  // has to be the rating of the words the reader is actually looking at. The
  // note says so, because a group of one 90% report and one 30% report shows
  // 90% and the reader is owed that.
  const SIT_CONF_NOTE = 'This is the best-established report in the group; '
    + 'expand for the rest, which may be rated differently. Corroboration is '
    + 'counted across the whole group.';

  // Expanded cards survive the 30 s re-render and updates merges: the
  // situation key is its oldest event id, stable while the event exists.
  const expandedSits = new Set<string>();
  const SIT_FLASH_MS = 3 * 60_000;   // new activity pulses the card briefly

  // --- the group card: the situation as a bottom-left popup ------------------
  // Same slot and styling as the entity selection card, because to the reader
  // a group IS an object — a plain click presents it, exactly as a plain
  // click on a pin presents an event, no dedicated button. The full curation
  // surface (rename, links editing, merge/split) is behind its "Full view".
  let groupCard: HTMLElement | null = null;
  // What the open card is ABOUT and what it is currently SHOWING. The second
  // is why: a card is built on click and then sits there, so a merge arriving
  // while it is open (another channel corroborates — the counts, the ⤷ latest
  // line and the extractor's confidence all move) used to leave it displaying
  // numbers the feed behind it had already replaced. renderLive repaints it,
  // but only when the signature actually moved.
  let groupCardKey: string | null = null;
  let groupCardSig = '';
  // The card's own reports, so it can be found again after the router renames
  // its container from a local key to `inc:<uuid>` (see incidents.regroup).
  let groupCardEvents = new Set<string>();
  function closeGroupCard(): void {
    groupCard?.remove();
    groupCard = null;
    groupCardKey = null;
    groupCardSig = '';
    groupCardEvents = new Set();
  }
  // Selecting an entity (map click, pane row) closes an open group card —
  // the same slot, so the two must never stack.
  setCompanionCardClose(closeGroupCard);

  // Which feed entry the reader last opened — carried across re-renders so
  // the feed always shows what the open card is ABOUT.
  let selectedFeedKey: string | null = null;
  function markFeedSelection(el: HTMLElement, key: string): void {
    selectedFeedKey = key;
    livePane.body.querySelectorAll('.feed-selected')
      .forEach((n) => n.classList.remove('feed-selected'));
    el.classList.add('feed-selected');
  }

  function openGroupCard(sit: FeedGroup): void {
    if (isMapEditing()) return;
    // Same card, by the same membership rule regroup uses — the key changes
    // under a card the router places mid-read, and the scroll must survive it.
    const same = groupCard !== null && (groupCardKey === sit.key
      || sit.events.some((e) => groupCardEvents.has(e.id)));
    const keepScroll = same
      ? (groupCard!.querySelector('.body')?.scrollTop ?? 0) : 0;
    closeGroupCard();
    closeSelection(map);   // one bottom-left card at a time
    const latest = sit.events[0]!;
    const headline = headlineOf(sit.events);
    const dev = latestDevelopment(sit.events);
    const nch = Math.max(sit.channels.length, sit.incident?.channelCount ?? 0);
    const nrep = sit.incident?.memberCount ?? sit.reportCount;
    const links = (sit.incident?.links ?? [])
      .filter((l) => /^https?:\/\//i.test(l.url));
    const linkLabel = (l: { url: string; label?: string }): string => {
      if (l.label) return l.label;
      try { return new URL(l.url).hostname.replace(/^www\./, ''); }
      catch { return l.url.slice(0, 30); }
    };
    // AGGREGATED links: the incident's curator links plus every child
    // event's own attrs.links, deduped — the group answers for its members.
    const memberLinks = [...new Set(sit.events
      .flatMap((e) => (Array.isArray(e.attrs.links)
        ? (e.attrs.links as unknown[]).map(String) : [])))]
      .filter((u) => /^https?:\/\//i.test(u))
      .filter((u) => !links.some((l) => l.url === u))
      .map((u) => ({ url: u }));
    const allLinks = [...links, ...memberLinks];

    // WHAT THE CHILDREN SAY, aggregated — the parent answers for its members.
    // Kind histogram (the situation's composition), the time span, and the
    // channel tally with per-channel report counts (tooltips carry the
    // operator's treatment notes).
    const kindCount = new Map<string, number>();
    for (const e of sit.events) {
      const k = String(e.attrs.kind ?? 'unclear');
      kindCount.set(k, (kindCount.get(k) ?? 0) + 1);
    }
    const kindsLine = [...kindCount.entries()].map(([k, n]) => {
      const kk = LIVE_KINDS[k] ?? UNCLEAR;
      return `<span style="--kc:${kk.color}">${kk.glyph}</span> ${esc(kk.label)}${
        n > 1 ? ` ×${n}` : ''}`;
    }).join(' · ');
    const firstAt = Math.min(...sit.events.map((e) => whenOf(e).at || Infinity));
    const spanLine = Number.isFinite(firstAt) && sit.lastAt - firstAt > 60_000
      ? `began ${relTimeMs(firstAt)} ago · latest ${relTimeMs(whenOf(latest).at)} ago`
      : `single report, ${relTimeMs(whenOf(latest).at)} ago`;
    const chCount = new Map<string, { n: number; links: SourceLink[] }>();
    for (const e of sit.events) {
      for (const s of sourceLinks(e.attrs)) {
        if (!s.channel) continue;
        const cur = chCount.get(s.channel) ?? { n: 0, links: [] };
        cur.n += 1;
        cur.links.push(s);
        chCount.set(s.channel, cur);
      }
    }
    const channelsLine = [...chCount.entries()]
      .sort((a, b) => b[1].n - a[1].n)
      // A channel's chip carries the strongest media it contributed, so the
      // card answers "who has footage" without expanding the member list.
      .map(([ch, v]) => {
        const m = bestMedia(v.links);
        return `<span class="gc-ch" title="${esc(sourceTip(ch))}${
          m ? esc(` — carries ${m}`) : ''}">${esc(channelLabel(ch) || 'post')}${
          v.n > 1 ? ` ×${v.n}` : ''}${
          m ? `<b class="src-media">${mediaGlyph(m)}</b>` : ''}</span>`;
      })
      .join(' · ');

    const km = LIVE_KINDS[String(latest.attrs.kind ?? 'unclear')] ?? UNCLEAR;
    const el = document.createElement('div');
    // `pod` is load-bearing: it is the class that positions the floating
    // card. Without it this div appends as static flow content below the
    // viewport — present in the DOM, invisible on screen, and not one error
    // anywhere, which is exactly how it shipped broken twice.
    el.className = 'pod selcard group-card';
    el.style.setProperty('--sel-color', km.color);
    el.innerHTML = `
      <header>
        <span class="sel-dot"></span>
        <div style="flex:1;min-width:0">
          <div class="type">${sit.incident ? 'Incident' : 'Situation'} · ${esc(km.label)}</div>
          <h4>${areaTagHtml(groupArea(sit.events), sit.title)}${esc(sit.title)}</h4>
          <div class="sel-when">${relTimeMs(whenOf(latest).at)} · ${
            nrep === 1 ? '1 report' : `${nrep} reports`} · ${
            nch === 1 ? '1 channel' : `${nch} channels`}${
            confidenceChip(headline.attrs, ' · ',
                           { note: SIT_CONF_NOTE, channels: sit.channels })}</div>
        </div>
        <span class="x" title="Close">✕</span>
      </header>
      <div class="body">
        <p class="gc-text">${esc(String(headline.attrs.summary ?? ''))}</p>
        ${dev ? `<p class="gc-latest">⤷ latest ${relTimeMs(whenOf(dev).at)}: ${
          esc(truncate(String(dev.attrs.summary ?? ''), 120))}</p>` : ''}
        <div class="gc-media card-media"></div>
        <div class="gc-agg">
          <div>${kindsLine}</div>
          <div class="gc-agg-dim">${esc(spanLine)}</div>
          ${channelsLine ? `<div class="gc-agg-dim">${channelsLine}</div>` : ''}
        </div>
        ${claimHtml(headline.attrs, { skipCorroboration: true, skipConfidence: true })}
        ${sequenceChipHtml(headline, liveEvents)}
        ${corroborationHtml(headline)}
        ${allLinks.length ? `<div class="gc-h">links</div>
          <div class="sel-sources gc-links">${allLinks.map((l) =>
          `<a href="${esc(l.url)}" target="_blank" rel="noopener noreferrer">${
            esc(linkLabel(l))} ↗</a>`).join('')}</div>` : ''}
        <div class="gc-sources"></div>
        <div class="gc-evidence"></div>
        <div class="gc-related"></div>
        <div class="gc-h">reports</div>
        <div class="gc-members">${sit.events.slice(0, 8).map((e) => `
          <div class="rel" data-id="${esc(e.id)}">
            <span class="gc-m-txt">${
              (LIVE_KINDS[String(e.attrs.kind ?? '')] ?? UNCLEAR).glyph} ${
              esc(truncate(String(e.attrs.summary ?? e.attrs.place ?? ''), 64))}</span>
            <span class="gc-m-meta">${relTimeMs(whenOf(e).at)}${
              confidenceChip(e.attrs, ' · ')}</span></div>`).join('')}
          ${sit.events.length > 8
            ? `<small>${sit.events.length - 8} more in the full view</small>` : ''}</div>
      </div>
      <div class="actions">
        ${sit.incident ? '<button class="primary" data-a="full">Full view</button>' : ''}
        ${sit.incident && me.isAdmin ? '<button data-a="rename">Rename</button>' : ''}
        <button data-a="new-damage" title="Drop a damage marker on the map, born linked to this group's headline event and dated by its occurrence">＋ Damage</button>
        <button data-a="new-obs" title="Drop an observation marker — same linking, for what isn't damage">＋ Obs</button>
        <button data-a="add-link" title="${sit.incident
          ? 'Attach a URL to the whole group (curator links)'
          : 'Attach a URL to the headline event'}">＋ Link</button>
        <button data-a="zoom">Zoom</button>
      </div>`;
    el.querySelector('.x')!.addEventListener('click', closeGroupCard);
    // Every member's footage, one thumbnail per PICTURE, open unless its chip
    // was closed.
    mountMedia(el.querySelector('.gc-media') as HTMLElement, `card:${sit.key}`,
               sit.events, { max: 8, px: CARD_PX });
    el.querySelectorAll<HTMLElement>('.gc-members .rel').forEach((r) =>
      r.addEventListener('click', () => {
        closeGroupCard();
        openEntity(r.dataset.id!);
      }));
    el.querySelector('[data-a="full"]')?.addEventListener('click', () => {
      closeGroupCard();
      shell.open('live');
      void openIncident(sit.incident!.id);
    });
    el.querySelector('[data-a="zoom"]')!.addEventListener('click', () => {
      const at = situationAnchor(sit.events);
      if (at) map.flyTo({ center: at, zoom: 11 });
    });
    el.querySelector('[data-a="rename"]')?.addEventListener('click', () => {
      void (async () => {
        const title = prompt('Rename this incident — a hand-typed title is '
          + 'never overwritten by the router:', sit.title);
        if (!title?.trim() || title.trim() === sit.title) return;
        try {
          sit.incident = await renameIncident(sit.incident!.id, title.trim());
          sit.title = sit.incident.title;
          openGroupCard(sit);
          renderLive();
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Could not rename.');
        }
      })();
    });
    el.querySelector('[data-a="add-link"]')!.addEventListener('click', () => {
      void (async () => {
        const url = prompt('URL to attach (video, post, article):')?.trim();
        if (!url || !/^https?:\/\//i.test(url)) return;
        try {
          if (sit.incident) {
            sit.incident = await setIncidentLinks(sit.incident.id,
              [...(sit.incident.links ?? []), { url }]);
          } else {
            const existing = Array.isArray(headline.attrs.links)
              ? (headline.attrs.links as unknown[]).map(String) : [];
            await put(`/api/entities/${headline.id}`,
                      { attrs: { ...headline.attrs, links: [...existing, url] } });
          }
          openGroupCard(sit);
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Could not attach the link.');
        }
      })();
    });

    // ＋ Damage / ＋ Obs: click the map to place a marker that is born LINKED
    // to the group's headline event (documents edge) and dated by the group's
    // occurrence — the Studio's aging rule. Feature clicks are swallowed while
    // placing so they cannot hijack the flow into opening cards.
    const armPlace = (markerType: string): void => {
      const release = armMapPick(() => { /* swallow while placing */ });
      map.getCanvas().style.cursor = 'crosshair';
      toast('Click the map where the marker goes — Esc cancels.');
      const cancel = (): void => {
        release();
        map.getCanvas().style.cursor = '';
        map.off('click', handler);
        document.removeEventListener('keydown', onEsc);
      };
      const onEsc = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') cancel();
      };
      const handler = (ev: maplibregl.MapMouseEvent): void => {
        void (async () => {
          cancel();
          try {
            const when = whenOf(headline).at;
            const created = await post<EntityDto>('/api/entities', {
              type: markerType,
              name: `${sit.title} — ${
                markerType === 'damage_marker' ? 'damage' : 'observation'}`,
              geometry: { type: 'Point',
                          coordinates: [ev.lngLat.lng, ev.lngLat.lat] },
              attrs: Number.isFinite(when)
                ? { date: new Date(when).toISOString().slice(0, 10) } : {},
            });
            cacheEntities([created]);
            await post('/api/edges', { src: created.id, dst: headline.id,
                                       type: 'documents' });
            closeGroupCard();
            openEntity(created.id);   // straight onto the marker for note/links
          } catch (err) {
            toast(err instanceof Error ? err.message : 'Could not create the marker.');
          }
        })();
      };
      document.addEventListener('keydown', onEsc);
      map.once('click', handler);
    };
    el.querySelector('[data-a="new-damage"]')!
      .addEventListener('click', () => armPlace('damage_marker'));
    el.querySelector('[data-a="new-obs"]')!
      .addEventListener('click', () => armPlace('observation'));

    // AGGREGATED evidence: markers documenting any child event, collected
    // lazily (edges only ride the single-entity GET). The detail fetches also
    // warm the cache, so member clicks land with relations already there.
    const evHost = el.querySelector('.gc-evidence') as HTMLElement;
    void (async () => {
      const detailed = (await Promise.all(sit.events.slice(0, 12).map((e) =>
        get<EntityDto>(`/api/entities/${e.id}`).catch(() => null))))
        .filter((d): d is EntityDto => d !== null);
      cacheEntities(detailed);
      const markers = new Map<string, { name: string; type: string }>();
      for (const d of detailed) {
        for (const ed of d.edges ?? []) {
          if (ed.type === 'documents' && ed.direction === 'in') {
            markers.set(ed.peerId, {
              name: ed.peerName ?? ed.peerType, type: ed.peerType });
          }
        }
      }
      if (groupCard !== el || !markers.size) return;
      evHost.innerHTML = `<div class="gc-h">evidence</div>${
        [...markers.entries()].map(([id, m]) => `
          <div class="rel gc-ev" data-id="${esc(id)}">📎 ${
            esc(truncate(m.name, 56))} <small>${
            m.type === 'damage_marker' ? 'damage' : 'observation'}</small></div>`)
          .join('')}`;
      evHost.querySelectorAll<HTMLElement>('.gc-ev').forEach((r) =>
        r.addEventListener('click', () => {
          closeGroupCard();
          openEntity(r.dataset.id!);
        }));
    })();

    // POSSIBLY RELATED — worded as maybes because that is what they are: the
    // advisor's duplicate suggestions (a model's judgment awaiting a curator)
    // and the sequence precedent (the launch any member may answer to).
    const relHost = el.querySelector('.gc-related') as HTMLElement;
    const relRows: string[] = [];
    const suggestions = sit.incident ? mergeSuggestionsFor(sit.incident.id) : [];
    for (const s of suggestions.slice(0, 3)) {
      const otherId = s.sourceId === sit.incident!.id ? s.targetId : s.sourceId;
      const other = s.sourceId === sit.incident!.id ? s.targetTitle : s.sourceTitle;
      // The decision costs ONE CLICK here, where the reader already is. The
      // incident view keeps the full instrument, but 90% of these are
      // "obviously the same, join them" (Noorabad/Nurabad — one spelling
      // geocoded to the wrong province, so the matcher's distance guard
      // rightly refused and the advisor rightly asked). Row click still
      // opens the other incident for the reader who wants to look first.
      relRows.push(`<div class="rel gc-rel gc-sug" data-inc="${esc(otherId)}"
        title="${esc(s.why)}"><span class="gc-sug-txt">⚭ may be the same as
        <b>${esc(truncate(other, 44))}</b>${
        s.confidence ? ` · ${esc(s.confidence)}` : ''}</span>
        <button type="button" class="mini" data-sug-apply="${esc(s.id)}"
          data-other="${esc(other)}"
          title="Fold the two incidents into one — the younger folds into the older. Splitting is the undo.">Merge</button>
        <button type="button" class="mini" data-sug-dismiss="${esc(s.id)}"
          title="Record that these are different events — the advisor will not re-ask.">not same</button></div>`);
    }
    // NARRATIVE LINKS (043) — different events, connected. Suggestions carry
    // the maybe in words and a one-click Link; applied links render as curated
    // fact with the ✕ undo. Direction survives rendering: "answers" from this
    // side is "answered by" from the other.
    if (sit.incident) {
      const incId = sit.incident.id;
      for (const s of linkSuggestionsFor(incId).slice(0, 3)) {
        const fromSource = s.sourceId === incId;
        const otherId = fromSource ? s.targetId : s.sourceId;
        const other = fromSource ? s.targetTitle : s.sourceTitle;
        relRows.push(`<div class="rel gc-rel gc-sug" data-inc="${esc(otherId)}"
          title="${esc(s.why)}"><span class="gc-sug-txt">⧉ ${
          esc(linkWording(s.kind, fromSource, false))}
          <b>${esc(truncate(other, 40))}</b>${
          s.confidence ? ` · ${esc(s.confidence)}` : ''}</span>
          <button type="button" class="mini" data-adv-apply="links:${esc(s.id)}"
            title="Record the connection — both incidents stay separate events; the link is undoable.">Link</button>
          <button type="button" class="mini" data-adv-dismiss="links:${esc(s.id)}"
            title="Record that these are not connected.">not related</button></div>`);
      }
      for (const l of incidentLinksFor(incId).slice(0, 4)) {
        const fromSource = l.srcId === incId;
        const otherId = fromSource ? l.dstId : l.srcId;
        const other = fromSource ? l.dstTitle : l.srcTitle;
        relRows.push(`<div class="rel gc-rel" data-inc="${esc(otherId)}"
          title="${esc(l.why ?? 'Linked by an operator.')}">⧉ ${
          esc(linkWording(l.kind, fromSource, true))}
          <b>${esc(truncate(other, 44))}</b> · linked
          <button type="button" class="mini" data-unlink="${esc(l.id)}"
            title="Remove the link. The applied suggestion keeps that the call was once made.">✕</button></div>`);
      }
    }
    // STORY FILING (043) — a model argues an unfiled member belongs to a
    // tracked story; File runs the same SCD2 refile a human filing does.
    const memberIds = new Set(sit.events.map((e) => e.id));
    const filing = filingFor(memberIds);
    if (filing) {
      relRows.push(`<div class="rel gc-rel gc-sug" title="${esc(filing.why)}">
        <span class="gc-sug-txt">⚑ may belong to
        <b>${esc(truncate(filing.storyName, 40))}</b>${
        filing.confidence ? ` · ${esc(filing.confidence)}` : ''}</span>
        <button type="button" class="mini" data-adv-apply="filings:${esc(filing.id)}"
          data-story="${esc(filing.storyName)}"
          title="File the report into that story — the same re-file a human performs, undoable the same way.">File</button>
        <button type="button" class="mini" data-adv-dismiss="filings:${esc(filing.id)}"
          title="Record that it does not belong there.">not this</button></div>`);
    }
    // FACTION ALIASES (043) — an unresolved actor name a model says is a
    // tracked faction under another spelling. Accept teaches resolution the
    // equivalence from the next report on; nothing already stored is rewritten.
    const unresolved = new Set<string>();
    for (const e of sit.events) {
      const a = e.attrs as Record<string, unknown>;
      for (const [nameKey, idKey] of [['attributedTo', 'attributed_to_id'],
                                      ['targetFaction', 'target_faction_id']] as const) {
        const n = String(a[nameKey] ?? '').trim();
        if (n && !a[idKey]) unresolved.add(n);
      }
    }
    for (const s of aliasSuggestionsFor(unresolved).slice(0, 2)) {
      relRows.push(`<div class="rel gc-rel gc-sug" title="${esc(s.why)}">
        <span class="gc-sug-txt">⍟ “${esc(truncate(s.alias, 32))}” may be
        <b>${esc(truncate(s.factionName, 32))}</b>${
        s.confidence ? ` · ${esc(s.confidence)}` : ''}</span>
        <button type="button" class="mini" data-adv-apply="aliases:${esc(s.id)}"
          title="Approve the spelling as an alias — future reports naming it resolve to that faction. Stored events keep the name as reported.">Accept</button>
        <button type="button" class="mini" data-adv-dismiss="aliases:${esc(s.id)}"
          title="Record that this is a different actor.">not same</button></div>`);
    }
    let prec: ReturnType<typeof precedentFor> = null;
    let precMember: EntityDto | null = null;
    for (const e of sit.events) {
      const p = precedentFor(e, liveEvents);
      if (p) { prec = p; precMember = e; break; }
    }
    if (prec && precMember) {
      // Confirm turns the inference into a FACT ON THE GRAPH: an event
      // --follows--> launch edge (042). The wording flips from "may follow"
      // to "follows · confirmed", and the sequences miner reads the pair as
      // ground truth — a confirmed link is a pattern from n=1.
      relRows.push(`<div class="rel gc-rel gc-prec" data-ev="${esc(prec.launch.id)}"
        title="An inference from timing${prec.pattern
          ? ` plus ${prec.pattern.n} prior sequences` : ''} — no report claims the connection.">
        <span class="gc-prec-txt">⤳ may follow the ${
        esc(String(prec.launch.attrs.place ?? 'launch site'))} launch ${
        Math.max(1, Math.round(prec.deltaMs / 60_000))}m earlier${
        prec.pattern ? ` · seen ${prec.pattern.n}× before` : ''}</span>
        <button type="button" class="mini" data-prec-confirm
          title="Record it as fact: this event follows that launch. The pattern miner treats confirmed links as ground truth.">Confirm</button></div>`);
    }
    if (relRows.length) {
      relHost.innerHTML = `<div class="gc-h">possibly related</div>${relRows.join('')}`;
      relHost.querySelectorAll<HTMLElement>('[data-inc]').forEach((r) =>
        r.addEventListener('click', () => {
          closeGroupCard();
          shell.open('live');
          void openIncident(r.dataset.inc!);
        }));
      relHost.querySelectorAll<HTMLButtonElement>('[data-sug-apply]').forEach((b) =>
        b.addEventListener('click', (ev) => {
          ev.stopPropagation();   // the row click underneath opens the incident
          void (async () => {
            try {
              await applySuggestion(b.dataset.sugApply!);
              toast(`Merged with "${b.dataset.other}".`);
              closeGroupCard();   // the group it described no longer exists
              await refresh();
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Could not merge.');
            }
          })();
        }));
      relHost.querySelectorAll<HTMLButtonElement>('[data-sug-dismiss]').forEach((b) =>
        b.addEventListener('click', (ev) => {
          ev.stopPropagation();
          void (async () => {
            try {
              await dismissSuggestion(b.dataset.sugDismiss!);
              b.closest('.gc-sug')?.remove();
              rebuildViews();
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Could not dismiss.');
            }
          })();
        }));
      // The 043 lanes share one wiring: "lane:id" on the button, apply or
      // dismiss, then repaint. A filing apply also refreshes the feed — the
      // refile bumped a rev and the scope readouts should say so.
      relHost.querySelectorAll<HTMLButtonElement>('[data-adv-apply]').forEach((b) =>
        b.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const [lane, id] = b.dataset.advApply!.split(':') as
            ['aliases' | 'filings' | 'links', string];
          void (async () => {
            try {
              await applyAdvice(lane, id);
              toast(lane === 'filings'
                ? `Filed into "${b.dataset.story}".`
                : lane === 'aliases' ? 'Alias approved — future reports will resolve.'
                  : 'Linked.');
              if (lane === 'filings') await refresh();
              openGroupCard(sit);
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Could not apply.');
            }
          })();
        }));
      relHost.querySelectorAll<HTMLButtonElement>('[data-adv-dismiss]').forEach((b) =>
        b.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const [lane, id] = b.dataset.advDismiss!.split(':') as
            ['aliases' | 'filings' | 'links', string];
          void (async () => {
            try {
              await dismissAdvice(lane, id);
              b.closest('.gc-sug')?.remove();
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Could not dismiss.');
            }
          })();
        }));
      relHost.querySelectorAll<HTMLButtonElement>('[data-unlink]').forEach((b) =>
        b.addEventListener('click', (ev) => {
          ev.stopPropagation();
          void (async () => {
            try {
              await removeIncidentLink(b.dataset.unlink!);
              openGroupCard(sit);
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Could not unlink.');
            }
          })();
        }));
      relHost.querySelectorAll<HTMLElement>('[data-ev]').forEach((r) =>
        r.addEventListener('click', () => {
          closeGroupCard();
          openEntity(r.dataset.ev!);
        }));
      const precRow = relHost.querySelector('.gc-prec') as HTMLElement | null;
      if (precRow && prec && precMember) {
        const launchId = prec.launch.id;
        const memberId = precMember.id;
        const setConfirmed = (edgeId: string): void => {
          precRow.querySelector('[data-prec-confirm]')?.remove();
          if (precRow.querySelector('.gc-prec-undo')) return;
          const txt = precRow.querySelector('.gc-prec-txt') as HTMLElement;
          txt.textContent = `${txt.textContent!
            .replace('may follow', 'follows')} · confirmed`;
          precRow.title = 'Confirmed by an operator — a follows relation on the graph.';
          const undo = document.createElement('button');
          undo.type = 'button';
          undo.className = 'mini gc-prec-undo';
          undo.textContent = '✕';
          undo.title = 'Remove the confirmation. Closes the relation; '
            + 'history keeps that it was once made.';
          undo.addEventListener('click', (ev) => {
            ev.stopPropagation();
            void (async () => {
              try {
                await del(`/api/edges/${edgeId}`);
                openGroupCard(sit);   // repaint: back to "may follow"
              } catch (err) {
                toast(err instanceof Error ? err.message : 'Could not remove it.');
              }
            })();
          });
          precRow.appendChild(undo);
        };
        precRow.querySelector('[data-prec-confirm]')!.addEventListener('click', (ev) => {
          ev.stopPropagation();   // the row click underneath opens the launch
          void (async () => {
            try {
              const made = await post<{ id: string }>('/api/edges',
                { src: memberId, dst: launchId, type: 'follows' });
              toast('Confirmed — this event follows that launch.');
              setConfirmed(made.id);
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Could not confirm.');
            }
          })();
        });
        // Already confirmed on a previous visit? The single-entity read
        // carries edges; a failed probe just leaves the button, which the
        // server would refuse identically anyway.
        void get<EntityDto>(`/api/entities/${memberId}`).then((full) => {
          const e = (full.edges ?? []).find((x) => x.type === 'follows'
            && x.direction === 'out' && x.peerId === launchId);
          if (e) setConfirmed(e.id);
        }).catch(() => { /* unconfirmed look; the button still works */ });
      }
    }

    // The claim's own media, one click from becoming the GROUP's curated
    // links: every member post URL, deduped, minus what is already attached.
    // Incident-backed groups only (the links column lives on the incident)
    // and admin only, so no one holds a button the server would refuse.
    const srcHost = el.querySelector('.gc-sources') as HTMLElement;
    if (sit.incident && me.isAdmin) {
      const attached = new Set(links.map((l) => l.url));
      const seen = new Set<string>();
      const candidates = sit.events
        .flatMap((e) => sourceLinks(e.attrs))
        .filter((s) => {
          if (attached.has(s.url) || seen.has(s.url)) return false;
          seen.add(s.url);
          return true;
        })
        .slice(0, 10);
      if (candidates.length) {
        // A channel with several posts in the group would render as identical
        // labels; the post number off the URL tail tells them apart.
        const dupCh = new Set([...candidates.reduce((m, s) => m.set(s.channel,
          (m.get(s.channel) ?? 0) + 1), new Map<string, number>())]
          .filter(([, n]) => n > 1).map(([ch]) => ch));
        const label = (s: { channel: string; url: string }): string => {
          const tail = s.url.split('/').pop() ?? '';
          return `${channelLabel(s.channel) || 'post'}${dupCh.has(s.channel) && tail
            ? `/${tail}` : ''}`;
        };
        srcHost.innerHTML = `<div class="gc-h">source posts — ＋ attaches to the group</div>
          <div class="sel-sources">${candidates.map((s) =>
          `<span class="gc-src"><a href="${esc(s.url)}" target="_blank"
             title="${esc(sourceTip(s.channel))}"
             rel="noopener noreferrer">${esc(label(s))} ↗</a><button
             type="button" class="src-keep" data-url="${esc(s.url)}"
             data-ch="${esc(s.channel)}"
             title="Attach this post to the whole group's links — one click">＋</button></span>`)
          .join('')}</div>`;
        srcHost.querySelectorAll<HTMLButtonElement>('.src-keep').forEach((b) =>
          b.addEventListener('click', () => {
            void (async () => {
              try {
                const saved = await setIncidentLinks(sit.incident!.id, [
                  ...(sit.incident!.links ?? []),
                  { url: b.dataset.url!,
                    label: channelLabel(b.dataset.ch) || 'post' },
                ]);
                sit.incident = saved;
                openGroupCard(sit);   // repaint with the link attached
              } catch (err) {
                toast(err instanceof Error ? err.message : 'Could not attach.');
              }
            })();
          }));
      }
    }

    document.querySelector('.cockpit')!.appendChild(el);
    // A repaint restores where the reader had scrolled to. openGroupCard is
    // the sanctioned rebuild (every in-card action already ends in one), so a
    // card that jumped back to the top on every corroborating report would
    // make the live update worse than the stale numbers it fixes.
    if (keepScroll) el.querySelector('.body')!.scrollTop = keepScroll;
    groupCard = el;
    groupCardKey = sit.key;
    groupCardSig = groupSignature(sit);
    groupCardEvents = new Set(sit.events.map((e) => e.id));
  }

  function situationCard(sit: FeedGroup): HTMLElement {
    // A one-report group renders as a row UNLESS it is a real incident: an
    // incident is a thing you can open and rename even when it holds one report,
    // and hiding that until a second arrives is what made groups feel like a
    // rendering trick rather than an object.
    if (sit.events.length === 1 && !sit.incident) return liveRow(sit.events[0]!);

    const latest = sit.events[0]!;
    const latestKind = String(latest.attrs.kind ?? 'unclear');
    // The header carries TWO clocks and they answer different questions: how old
    // the newest activity is (occurrence), and whether reports are still coming
    // in (sit.lastAt). It used to render only the latter, through
    // `new Date(sit.lastAt).toISOString()` — which also threw a RangeError and
    // blanked the whole pane if lastAt was ever non-finite.
    const headAt = whenOf(latest).at;
    const stillComing = sit.lastAt - headAt > MATERIAL_SKEW_MS;
    const arc = sit.kinds.map((k) => LIVE_KINDS[k] ?? UNCLEAR);
    // The card is a LIVING OBJECT: its body is the best-ESTABLISHED claim
    // (which upgrades in place as better reports arrive), the ⤷ line is the
    // newest wrinkle when one exists, and disagreement between sources is
    // said out loud. Expansion is provenance audit, never the only way to
    // the current picture.
    const headline = headlineOf(sit.events);
    const headlineText = String(headline.attrs.summary ?? headline.attrs.text ?? '');
    const dev = latestDevelopment(sit.events);
    const devKind = dev ? (LIVE_KINDS[String(dev.attrs.kind ?? '')] ?? UNCLEAR) : null;
    const conflict = attributionConflict(sit.events);
    // The server projection sees every member, including ones this client is
    // not holding; the local count covers unrouted groups and pre-035 rows
    // whose stamped count is still 0. Max of the two is honest either way.
    const nch = Math.max(sit.channels.length, sit.incident?.channelCount ?? 0);
    const nrep = sit.incident?.memberCount ?? sit.reportCount;
    const meta = `${nrep === 1 ? '1 report' : `${nrep} reports`} · `
      + (nch === 1 ? '1 channel' : `${nch} channels`);
    // Members the window would otherwise have cut. They ride inside the card
    // because the window picks CARDS, not their contents — and the count is
    // named in the badge's tooltip so a row older than the window reads as the
    // situation's beginning rather than as a filter that stopped working.
    const agedIn = sit.events.filter((e) => visibilityOf(e) === 'hidden').length;

    const card = document.createElement('div');
    card.className = 'live-sit';
    card.dataset.skey = sit.key;
    // A situation that GREW counts as new — a corroborating report bumps rev —
    // while its age stays the occurrence clock. That split is why this can
    // resurface without lying about when the thing happened.
    if (sit.events.some(isUnread)) card.classList.add('live-unread');
    if (sit.events.some(isPinned)) card.classList.add('live-pinned');
    card.style.setProperty('--kc', kindColor(latestKind));
    card.style.setProperty('--pin',
      colorMode === 'age' ? agePin(headAt) : kindColor(latestKind));
    if (expandedSits.has(sit.key)) card.classList.add('sit-open');
    if (Date.now() - sit.lastAt < SIT_FLASH_MS) card.classList.add('sit-new');

    const sitMedia = mediaGroups(sit.events);
    const head = document.createElement('div');
    head.className = 'row live-row sit-head';
    if (colorMode === 'tint') head.classList.add('live-tinted');
    if (feedDim) head.style.opacity = String(feedOpacity(headAt));
    // The same grammar as a single row: the title, the headline (clamped to
    // three lines), the ⤷ newest wrinkle on one line, the lead picture and
    // the flags. The meta line, the claim line, the sequence and every
    // picture are the card's details, opened by the count pill together with
    // the member reports. The leading glyph is the latest kind (the
    // situation's current state); the kind arc rides the meta line, where
    // "this evolved" belongs.
    const latestGlyph = LIVE_KINDS[latestKind] ?? UNCLEAR;
    const localTip = 'Grouped in your browser — the server has not filed these '
      + 'into an incident yet. Everything still works; this goes once it does.';
    head.innerHTML = `
      <span class="glyph" title="${esc(latestGlyph.label)}">${latestGlyph.glyph}</span>
      <span class="t">
        <span class="n">${areaTagHtml(groupArea(sit.events), sit.title)}${esc(sit.title)}${
          sit.incident?.titleBy === 'human'
            ? '<i class="sit-named" title="Renamed by hand — the router will not overwrite it">✎</i>'
            : ''}</span>
        ${headlineText
          ? `<span class="s sit-headline">${esc(headlineText)}</span>` : ''}
        ${dev ? `<span class="s sit-latest" title="The newest report adds
 something over the established picture">⤷ latest ${
            relTimeMs(whenOf(dev).at)}: <span style="--kc:${devKind!.color}">${
            devKind!.glyph}</span> ${
            esc(String(dev.attrs.summary ?? ''))}</span>` : ''}
        ${flagsHtml(
          claimHtml(headline.attrs, { cautions: 'only' }),
          conflict.length
            ? `<b class="sit-conflict" title="Different channels state different attributions. Disagreement between sources is signal — shown rather than resolved to whichever report rendered.">sources disagree: ${
                esc(conflict.join(' / '))}</b>`
            : '',
          sit.incident && mergeSuggestionsFor(sit.incident.id).length
            ? '<span class="sit-suggest-mark" title="The advisor thinks this may be the same event as another incident — expand to review">⚭ possible duplicate</span>'
            : '',
          corroborationHtml(headline))}
      </span>
      ${feedThumbHtml(sitMedia)}
      <span class="live-side">
        <span class="triage">
          <button type="button" class="tri" data-pin="${esc(latest.id)}"
            title="${sit.events.some(isPinned) ? 'Unpin' : 'Pin this situation'}"
            aria-pressed="${sit.events.some(isPinned)}">${
              sit.events.some(isPinned) ? '★' : '☆'}</button>
          <button type="button" class="tri" data-mute="${esc(latest.id)}"
            title="Mute this place — reversible, and the count stays visible">🔇</button>
        </span>
        ${stillComing
          ? `<span class="when-again" title="New reporting on an older situation — not new activity. Last report: ${
              esc(fmtAbs(sit.lastAt))}">↻ update ${relTimeMs(sit.lastAt)}</span>`
          : ''}
        <time${Number.isFinite(headAt)
          ? ` datetime="${new Date(headAt).toISOString()}"` : ''}
              class="${stillComing ? 'when-orig' : ''}"
              title="Newest activity: ${esc(fmtAbs(headAt))}&#10;Last report: ${
                esc(fmtAbs(sit.lastAt))}">${relTimeMs(headAt)}</time>
        ${confidenceChip(headline.attrs, '',
                         { note: SIT_CONF_NOTE, channels: sit.channels })}
        <button type="button" class="sit-expand${sit.local ? ' sit-expand-local' : ''}" title="${
          sit.incident ? `${nrep} reports filed into this incident`
                       : `${sit.events.length} events in this situation`
          } — show them and the details${agedIn
            ? esc(`, including ${agedIn} older than the ${liveFadeHours()}h window`)
            : ''}${sit.local ? esc(`. ${localTip}`) : ''}"><b>${nrep}</b><span class="car">▸</span></button>
      </span>
      <div class="live-more" hidden></div>`;

    // The card's details, above its member reports: filled on first open.
    const headMore = head.querySelector(':scope > .live-more') as HTMLElement;
    let headFilled = false;
    const showHeadMore = (open: boolean): void => {
      if (open && !headFilled) {
        headFilled = true;
        headMore.innerHTML = `
        <span class="s live-meta">${
          arc.length > 1
            ? `<span class="sit-arc-mini" title="${
                esc(arc.map((k) => k.label).join(' → '))}">${
                arc.slice(-4).map((k) =>
                  `<span style="--kc:${k.color}">${k.glyph}</span>`).join('→')}</span> · `
            : ''}${esc(meta)}${
          nch === 1 ? ' · <b class="sit-single">unconfirmed</b>' : ''}${
          // A router outage must be VISIBLE, not silent. This group was assembled
          // in the browser because nothing had placed its reports yet — which is
          // also the normal state for the ~20 s before the slow path runs. The
          // closed card says it too: its count pill is hollow (.sit-expand-local).
          sit.local
            ? ` · <span class="sit-local" title="${esc(localTip)}">grouped locally</span>`
            : ''}</span>
        ${claimHtml(headline.attrs,
                    { skipCorroboration: true, skipConfidence: true, cautions: 'skip' })}
        ${sequenceChipHtml(headline, liveEvents)}
        ${mediaStripHtml(sitMedia, 8, ROW_PX)}`;
        wireMediaStrip(headMore, sitMedia);
      }
      headMore.hidden = !open;
    };
    wireFeedThumb(head, sitMedia);

    const list = document.createElement('div');
    list.className = 'sit-events';
    list.hidden = !expandedSits.has(sit.key);
    // THE MEMBER ROWS ARE BUILT ON FIRST EXPANSION, not on every repaint.
    // Every card used to build a row per member up front — each with its
    // claim line, corroboration sweep and sequence lookup — and hide them,
    // which made the collapsed feed cost as much as every card expanded.
    let listFilled = false;
    const fillList = (): void => {
      if (listFilled) return;
      listFilled = true;
      for (const e of sit.events) {
        const row = liveRow(e);
        // Dimmed, not hidden: this report is older than the window the reader
        // set, and it is here because the situation it belongs to is current.
        if (visibilityOf(e) === 'hidden') row.classList.add('live-outwindow');
        list.appendChild(row);
      }
  
      // The advisor's queue, rendered where the decision is made: "these two
      // incidents may be one event", with the model's own reasoning in the
      // tooltip. Advice only — Merge applies it (undoable: split), "Not the
      // same" records the human's judgement so a repeat suggestion reads as a
      // repeat. Writes are curator-gated server-side; anyone can read the claim.
      if (sit.incident) {
        for (const s of mergeSuggestionsFor(sit.incident.id)) {
          const other = s.sourceId === sit.incident.id ? s.targetTitle : s.sourceTitle;
          const row = document.createElement('div');
          row.className = 'sit-suggest';
          row.innerHTML = `
            <span class="sit-suggest-why" title="${esc(s.why)}">⚭ May be the same as
              <b>${esc(other)}</b>${s.confidence ? ` · ${esc(s.confidence)}` : ''}</span>
            <button type="button" class="mini" data-a="apply">Merge</button>
            <button type="button" class="mini" data-a="dismiss">Not the same</button>`;
          row.querySelector('[data-a="apply"]')!.addEventListener('click', async (ev) => {
            ev.stopPropagation();
            try {
              await applySuggestion(s.id);
              toast(`Merged with "${other}".`);
              await refresh();
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Could not merge.');
            }
          });
          row.querySelector('[data-a="dismiss"]')!.addEventListener('click', async (ev) => {
            ev.stopPropagation();
            try {
              await dismissSuggestion(s.id);
              rebuildViews();
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Could not dismiss.');
            }
          });
          list.appendChild(row);
        }
      }
  
      // A real incident gets an OPEN affordance as well as a twisty. Expanding
      // shows the reports you already have; opening shows the incident — every
      // report the server has filed into it, its title, and the corrections.
      if (sit.incident) {
        const open = document.createElement('button');
        open.type = 'button';
        open.className = 'sit-open-btn';
        open.textContent = 'Open incident →';
        open.addEventListener('click', (ev) => {
          ev.stopPropagation();
          void openIncident(sit.incident!.id);
        });
        list.appendChild(open);
      }
    };
    if (!list.hidden) { fillList(); showHeadMore(true); }

    wireTriage(head, sit.events);
    // Clicking the CARD goes there and nothing else — the same contract as a
    // single row: the busiest ~2 km cell of the precise members, most recent
    // point (situationAnchor). Expansion is its own control, the count badge
    // on the right: a card that opened on any click made every navigation
    // also rearrange the feed under the reader.
    if (selectedFeedKey === sit.key) head.classList.add('feed-selected');
    head.addEventListener('click', () => {
      const at = situationAnchor(sit.events);
      if (at) map.flyTo({ center: at, zoom: 11 });
      markFeedSelection(head, sit.key);
      openGroupCard(sit);   // present the group, the way clicking a pin presents an event
    });
    head.querySelector('.sit-expand')!.addEventListener('click', (ev) => {
      ev.stopPropagation();   // expanding is not a navigation
      const open = list.hidden;
      if (open) fillList();
      showHeadMore(open);
      list.hidden = !open;
      card.classList.toggle('sit-open', open);
      if (open) expandedSits.add(sit.key); else expandedSits.delete(sit.key);
    });
    card.append(head, list);
    return card;
  }

  // --- find nearby: the context sweep around an incident --------------------
  // One button answers "what else is around this?": places, damage, other
  // events (all already in memory from the entity list) plus FIRMS heat (48 h,
  // fetched per sweep — detections are not entities). Results are LISTED with
  // distances and RUNG on the map through a client source, which deliberately
  // bypasses the zoom gates — a sweep that silently omitted the infrastructure
  // rows below z10 would read as "nothing there", the damage-window lie again.

  const NEARBY_SRC = 'nearby-viz';

  function clearNearby(): void {
    (map.getSource(NEARBY_SRC) as maplibregl.GeoJSONSource | undefined)
      ?.setData({ type: 'FeatureCollection', features: [] });
  }

  function ensureNearbyLayers(): void {
    if (map.getSource(NEARBY_SRC)) return;
    map.addSource(NEARBY_SRC, {
      type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
      id: 'nearby-ring', type: 'line', source: NEARBY_SRC,
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: { 'line-color': '#38bdf8', 'line-width': 1.6,
               'line-dasharray': [3, 2], 'line-opacity': 0.85 } as never,
    });
    map.addLayer({
      id: 'nearby-pts', type: 'circle', source: NEARBY_SRC,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': 8, 'circle-color': 'rgba(0,0,0,0)',
               'circle-stroke-color': ['get', 'color'] as never,
               'circle-stroke-width': 2 },
    });
  }

  /** A dashed radius ring, good enough at these scales (≤ 50 km). */
  function ringAround(lon: number, lat: number, km: number): GeoJSON.Feature {
    const dLat = km / 110.574;
    const dLon = km / (111.32 * Math.cos((lat * Math.PI) / 180));
    const pts: [number, number][] = [];
    for (let i = 0; i <= 72; i++) {
      const a = (i / 72) * 2 * Math.PI;
      pts.push([lon + dLon * Math.sin(a), lat + dLat * Math.cos(a)]);
    }
    return { type: 'Feature',
             geometry: { type: 'LineString', coordinates: pts }, properties: {} };
  }

  /** Point for a point, rough ring-average for a polygon — the sweep needs a
   *  comparable position, not survey fidelity. */
  function roughCenter(g: EntityDto['geometry']): [number, number] | null {
    if (!g) return null;
    if (g.type === 'Point') return g.coordinates as [number, number];
    if (g.type === 'Polygon') {
      const ring = (g.coordinates as [number, number][][])[0] ?? [];
      if (!ring.length) return null;
      const lon = ring.reduce((s, p) => s + p[0], 0) / ring.length;
      const lat = ring.reduce((s, p) => s + p[1], 0) / ring.length;
      return [lon, lat];
    }
    return null;
  }

  // --- the incident detail view --------------------------------------------
  // Replaces the feed body rather than opening a second panel: on a phone every
  // side panel is already a full-width sheet (mobile.css), so a modal over a
  // sheet is a dead end with no reachable dismiss. A back button is reachable
  // with a thumb and works identically on desktop.

  async function openIncident(id: string): Promise<void> {
    if (isMapEditing()) return;
    const body = livePane.body;
    // The marker renderLive() checks before painting: without it, the 30 s
    // re-render (and every updates poll and read-state change) rewrote the
    // pane body and silently dumped the reader back into the feed — the
    // incident view "closing itself" after a few seconds.
    body.dataset.view = 'incident';
    body.innerHTML = '<div class="empty">Loading incident…</div>';
    const editRevision = mapEditRevision();
    let inc: IncidentDto;
    try {
      inc = await incidentDetail(id);
    } catch (err) {
      if (isMapEditing() || editRevision !== mapEditRevision()) return;
      body.innerHTML = `<div class="empty">${
        esc(err instanceof Error ? err.message : 'Could not open that incident.')}</div>`;
      return;
    }
    if (isMapEditing() || editRevision !== mapEditRevision()) return;
    const picked = new Set<string>();
    const members = inc.members ?? [];

    const view = document.createElement('div');
    view.className = 'inc-view';
    view.innerHTML = `
      <button type="button" class="inc-back" data-a="back">← Back to feed</button>
      <div class="inc-head">
        <input type="text" class="inc-title" value="${esc(inc.title)}"
               title="Rename permanently. A hand-typed title is never overwritten by the router." />
        <button type="button" class="mini commit" data-a="rename">Rename</button>
      </div>
      <p class="adm-hint inc-meta">${inc.memberCount} report${
        inc.memberCount === 1 ? '' : 's'} · ${esc(inc.status)}${
        inc.titleBy === 'human' ? ' · title set by hand' : ''}${
        inc.locatedFrom
          ? ` · located from ${inc.locatedFrom} point report${inc.locatedFrom === 1 ? '' : 's'}`
          : ' · no point-precision report, so no location'}</p>
      <div class="inc-actions inc-nearby">
        <label>within <select data-a="nearby-km">
          <option value="2">2 km</option><option value="5">5 km</option>
          <option value="10" selected>10 km</option><option value="25">25 km</option>
          <option value="50">50 km</option></select></label>
        <button type="button" class="mini" data-a="nearby"
          title="List hotspots, places, damage and other events around this incident, and ring them on the map.">Find nearby</button>
        <button type="button" class="mini" data-a="nearby-clear" hidden>Clear</button>
      </div>
      <div class="inc-nearby-out"></div>
      <div class="inc-links"></div>
      <div class="inc-actions inc-linkadd">
        <input type="url" data-a="link-url" placeholder="https://… (article, video, thread)" />
        <input type="text" data-a="link-label" placeholder="label (optional)" />
        <button type="button" class="mini" data-a="link-add">Add link</button>
      </div>
      <div class="inc-members"></div>
      <div class="inc-actions">
        <button type="button" class="mini" data-a="split" disabled>Split out selected</button>
        <button type="button" class="mini" data-a="detach" disabled>Detach selected</button>
      </div>
      <p class="adm-hint">Splitting makes a new incident from the reports you tick.
        Detaching leaves them unrouted — they go back to being grouped in your
        browser and nothing about who can see them changes.</p>
      <div class="inc-actions inc-merge">
        <select data-a="merge-into"><option value="">Merge this into…</option></select>
        <button type="button" class="mini" data-a="merge" disabled>Merge</button>
      </div>
      <p class="adm-hint">Merging folds THIS incident into the one you pick and
        retires this one — its reports and its history move across, and splitting
        them back out is the undo. Only incidents in the same story are listed,
        because an incident belongs to exactly one.</p>`;

    const host = view.querySelector('.inc-members') as HTMLElement;
    for (const e of members) {
      const wrap = document.createElement('label');
      wrap.className = 'inc-member';
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.addEventListener('change', () => {
        if (box.checked) picked.add(e.id); else picked.delete(e.id);
        for (const a of ['split', 'detach']) {
          (view.querySelector(`[data-a="${a}"]`) as HTMLButtonElement)
            .disabled = picked.size === 0;
        }
      });
      wrap.appendChild(box);
      wrap.appendChild(liveRow(e));
      host.appendChild(wrap);
    }
    if (!members.length) {
      host.innerHTML = '<div class="empty">No reports in this incident.</div>';
    }

    const act = (name: string, fn: () => Promise<void>) =>
      view.querySelector(`[data-a="${name}"]`)!
        .addEventListener('click', () => void fn().catch((err: unknown) =>
          toast(err instanceof Error ? err.message : 'That did not work.')));

    // Curator-attached links: what a member post could never carry — the news
    // writeup, the verified video, the thread pulling it together. Member post
    // URLs already render on each report row below.
    const linksHost = view.querySelector('.inc-links') as HTMLElement;
    const links = inc.links ?? [];
    const labelFor = (l: { url: string; label?: string }): string => {
      if (l.label) return l.label;
      try { return new URL(l.url).hostname.replace(/^www\./, ''); }
      catch { return l.url.slice(0, 40); }
    };
    for (const [i, l] of links.entries()) {
      if (!/^https?:\/\//i.test(l.url)) continue;   // server enforces; belt here
      const row = document.createElement('span');
      row.className = 'inc-link';
      const a = document.createElement('a');
      a.href = l.url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = `${labelFor(l)} ↗`;
      a.title = l.url;
      const x = document.createElement('button');
      x.type = 'button';
      x.className = 'mini';
      x.textContent = '✕';
      x.title = 'Remove this link';
      x.addEventListener('click', () => void (async () => {
        await setIncidentLinks(id, links.filter((_, n) => n !== i));
        await openIncident(id);
      })().catch((err: unknown) =>
        toast(err instanceof Error ? err.message : 'Could not remove the link.')));
      row.append(a, x);
      linksHost.appendChild(row);
    }

    act('back', async () => {
      clearNearby();                       // the sweep's rings go with the view
      delete livePane.body.dataset.view;   // release the guard, then repaint
      renderLive();
    });

    // Find nearby: entities from memory, heat from /api/firms, rings on the
    // map, distances in the list. Rows fly the map; entity rows also open.
    const nearbyOut = view.querySelector('.inc-nearby-out') as HTMLElement;
    const nearbyClear = view.querySelector('[data-a="nearby-clear"]') as HTMLButtonElement;
    act('nearby', async () => {
      const km = Number((view.querySelector('[data-a="nearby-km"]') as
        HTMLSelectElement).value) || 10;
      const anchor = (inc.centroid?.coordinates as [number, number] | undefined)
        ?? situationAnchor(members);
      if (!anchor) {
        toast('This incident has no location to search around.');
        return;
      }
      const [lon, lat] = anchor;
      nearbyOut.innerHTML = '<div class="empty">Searching…</div>';
      const memberIds = new Set(members.map((m) => m.id));
      interface Hit { glyph: string; label: string; km: number;
                      at: [number, number]; id?: string; color: string; sub?: string }
      const hits: Hit[] = [];
      for (const p of scoped) {
        if (memberIds.has(p.id)) continue;
        const at = roughCenter(p.geometry);
        if (!at) continue;
        const d = haversineKm(lat, lon, at[1], at[0]);
        if (d > km) continue;
        if (p.type === 'live_event') {
          const k = LIVE_KINDS[String(p.attrs.kind ?? '')] ?? UNCLEAR;
          hits.push({ glyph: k.glyph, color: k.color, km: d, at, id: p.id,
            label: String(p.attrs.summary ?? p.attrs.place ?? k.label) });
        } else if (p.category === 'damage') {
          hits.push({ glyph: '💥', color: '#e11d2e', km: d, at, id: p.id,
            label: `damage · ${String(p.attrs.date ?? '')} ${
              String(p.attrs.note ?? p.name ?? '')}`.trim() });
        } else if (domainOf(p) === 'place') {
          hits.push({ glyph: '📍', color: '#38bdf8', km: d, at, id: p.id,
            label: p.name ?? p.type });
        } else {
          hits.push({ glyph: '◆', color: '#8b9db0', km: d, at, id: p.id,
            label: `${p.name ?? p.type} (${p.type})` });
        }
      }
      let heatNote = '';
      try {
        const dLat = km / 110.574;
        const dLon = km / (111.32 * Math.cos((lat * Math.PI) / 180));
        const fc = await get<{ features: { geometry: { coordinates: [number, number] };
          properties: Record<string, unknown> }[] }>(
          `/api/firms?west=${lon - dLon}&south=${lat - dLat}`
          + `&east=${lon + dLon}&north=${lat + dLat}&days=2`);
        for (const f of fc.features ?? []) {
          const [flon, flat] = f.geometry.coordinates;
          const d = haversineKm(lat, lon, flat, flon);
          if (d > km) continue;
          const u = f.properties.unusual;
          hits.push({ glyph: '🔥', color: '#ff8c00', km: d, at: [flon, flat],
            label: `heat detection${typeof f.properties.frp === 'number'
              ? ` · ${f.properties.frp} MW` : ''}`,
            sub: u === true ? 'unusual — no persistent source here'
              : u === false ? 'persistent source (flare/industry)' : undefined });
        }
      } catch {
        heatNote = 'Heat sweep unavailable (FIRMS not configured or offline).';
      }
      hits.sort((a, b) => a.km - b.km);
      const shown = hits.slice(0, 60);

      ensureNearbyLayers();
      (map.getSource(NEARBY_SRC) as maplibregl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [ringAround(lon, lat, km),
          ...shown.map((h) => ({ type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: h.at },
            properties: { color: h.color } }))],
      });
      map.fitBounds([[lon - (km / (111.32 * Math.cos((lat * Math.PI) / 180))),
                      lat - km / 110.574],
                     [lon + (km / (111.32 * Math.cos((lat * Math.PI) / 180))),
                      lat + km / 110.574]], { padding: 48, maxZoom: 13 });
      nearbyClear.hidden = false;

      nearbyOut.innerHTML = `<div class="gc-h">within ${km} km — ${hits.length} found${
        hits.length > shown.length ? `, nearest ${shown.length} listed` : ''} · heat is last 48 h</div>${
        heatNote ? `<p class="adm-hint">${esc(heatNote)}</p>` : ''}`;
      for (const h of shown) {
        const row = document.createElement('div');
        row.className = 'rel nearby-row';
        row.innerHTML = `<span>${h.glyph} ${esc(truncate(h.label, 64))}</span>
          <small>${h.km < 1 ? `${Math.round(h.km * 1000)} m` : `${h.km.toFixed(1)} km`}${
            h.sub ? ` · ${esc(h.sub)}` : ''}</small>`;
        row.addEventListener('click', () => {
          map.flyTo({ center: h.at, zoom: Math.max(map.getZoom(), 12) });
          if (h.id) openEntity(h.id);
        });
        nearbyOut.appendChild(row);
      }
      if (!shown.length) {
        nearbyOut.innerHTML += '<div class="empty">Nothing within the radius.</div>';
      }
    });
    act('nearby-clear', async () => {
      clearNearby();
      nearbyOut.innerHTML = '';
      nearbyClear.hidden = true;
    });
    act('link-add', async () => {
      const urlBox = view.querySelector('[data-a="link-url"]') as HTMLInputElement;
      const labelBox = view.querySelector('[data-a="link-label"]') as HTMLInputElement;
      const url = urlBox.value.trim();
      if (!/^https?:\/\//i.test(url)) {
        toast('Links must start with http:// or https://');
        return;
      }
      const label = labelBox.value.trim();
      await setIncidentLinks(id, [...links, { url, ...(label ? { label } : {}) }]);
      toast('Link added.');
      await openIncident(id);
    });
    act('rename', async () => {
      const value = (view.querySelector('.inc-title') as HTMLInputElement).value;
      await renameIncident(id, value);
      toast('Renamed. The router will not overwrite it.');
      await openIncident(id);
    });
    act('split', async () => {
      const title = prompt('Title for the new incident?', inc.title);
      if (!title) return;
      await splitOut(id, [...picked], title);
      toast(`Split ${picked.size} report(s) out.`);
      await refresh();
      await openIncident(id);
    });
    act('detach', async () => {
      await detachReports(id, [...picked]);
      toast(`Detached ${picked.size} report(s).`);
      await refresh();
      await openIncident(id);
    });

    // Same story only: an incident belongs to exactly one, and the service 422s
    // on a cross-story merge rather than letting 033's trigger refuse the member
    // writes with a message nobody can act on. Offering the impossible choice and
    // explaining the failure afterwards is worse than not offering it.
    const target = view.querySelector('[data-a="merge-into"]') as HTMLSelectElement;
    const mergeBtn = view.querySelector('[data-a="merge"]') as HTMLButtonElement;
    const others = [...knownIncidents().values()]
      .filter((o) => o.id !== id && o.storyId === inc.storyId)
      .sort((a, b) => (Date.parse(b.lastReportAt ?? '') || 0)
                    - (Date.parse(a.lastReportAt ?? '') || 0));
    for (const o of others) {
      const opt = document.createElement('option');
      opt.value = o.id;
      opt.textContent = `${o.title} (${o.memberCount})`;
      target.appendChild(opt);
    }
    if (!others.length) {
      target.disabled = true;
      target.options[0]!.textContent = 'No other incident in this story';
    }
    target.addEventListener('change', () => { mergeBtn.disabled = !target.value; });
    act('merge', async () => {
      const into = others.find((o) => o.id === target.value);
      if (!into) return;
      if (!confirm(`Fold "${inc.title}" into "${into.title}"?\n\n`
                   + `${inc.memberCount} report(s) move across and this incident `
                   + `is retired. You can split them back out afterwards.`)) return;
      await mergeInto(id, into.id);
      toast(`Merged into "${into.title}".`);
      await refresh();
      await openIncident(into.id);
    });

    body.innerHTML = '';
    body.appendChild(view);
  }

  function renderLive(): void {
    // The incident view owns the pane while it is open; every periodic caller
    // (30 s interval, updates poll, read-state change) lands here, so this is
    // the one guard that keeps them all from dumping the reader back to the
    // feed mid-read. The Back button releases it.
    if (livePane.body.dataset.view === 'incident') return;
    // Painting is only for the open pane; the badge and the open group card
    // are kept current regardless, since both show outside the drawer.
    const paint = paneShowing('live');
    const kind = liveKind.value;
    const q = liveSearch.value.trim().toLowerCase();
    let muted = 0;
    const passes = (e: EntityDto, count: boolean): boolean => {
      // A pinned event outranks every quieting filter: pinning is the reader
      // saying "keep this in front of me", so a mute must not override it.
      if (!isPinned(e) && (isMuted(e) || isDismissed(e))) {
        if (count) muted += 1;
        return false;
      }
      if (kind !== 'all' && String(e.attrs.kind ?? '') !== kind) return false;
      if (q) {
        const hay = `${String(e.attrs.place ?? '')} ${e.name ?? ''} `
          + `${String(e.attrs.text ?? '')} ${String(e.attrs.summary ?? '')}`;
        if (!hay.toLowerCase().includes(q)) return false;
      }
      return true;
    };
    // visibilityOf, not isFaded(validFrom): the window now measures when the
    // event HAPPENED, so corroboration no longer keeps an old event alive
    // forever — and the 'late' band stops that change from silently swallowing
    // a backlog replay, whose events are already outside the window on arrival.
    // Aged-out events are HELD, not gone: the window is a filter, and the
    // "show 10 more" button at the bottom pages into them newest-first.
    // The news lane reads the SAME window, mutes and search as the event lanes
    // — it is a different kind of record, not a different set of rules — so it
    // simply supplies a different source list to the identical machinery below.
    const source = feedMode === 'news' ? newsItems : liveEvents;
    const aged: EntityDto[] = [];
    const inWindow: EntityDto[] = [];
    for (const e of source) {
      (visibilityOf(e) === 'hidden' ? aged : inWindow).push(e);
    }
    const agedEligible = aged.filter((e) => passes(e, false))
      .sort((a, b) => lastReportOf(b) - lastReportOf(a));
    const revived = agedEligible.slice(0, liveExtra);
    let agedHeld = agedEligible.length - revived.length;
    const filtered = [...inWindow.filter((e) => passes(e, true)), ...revived];
    if (feedMode === 'news') {
      // Newest first by OCCURRENCE, the same default the event feed settled on:
      // a statement re-reported an hour later has not become newer. Bubbling
      // restores last-report order for readers who want updates to resurface.
      const rows = [...filtered].sort((a, b) => {
        const pa = isPinned(a) ? 1 : 0;
        const pb = isPinned(b) ? 1 : 0;
        return pb - pa || (feedBubble
          ? lastReportOf(b) - lastReportOf(a)
          : whenOf(b).at - whenOf(a).at);
      });
      if (paint) {
        livePane.body.innerHTML = '';
        if (!rows.length) {
          livePane.body.innerHTML = `<div class="empty">${
            newsItems.length
              ? 'No news matches the filters.'
              : 'No news yet. Conflict reporting with no location on the map '
                + 'lands here — statements, diplomacy, economic effects.'}</div>`;
        } else {
          const shown = rows.slice(0, 300 + capExtra);
          for (const e of shown) livePane.body.appendChild(newsRow(e));
          if (rows.length > shown.length) {
            const more = document.createElement('button');
            more.type = 'button';
            more.className = 'live-agedout';
            more.textContent = `${rows.length - shown.length} more — show 10 more`;
            more.addEventListener('click', () => { capExtra += 10; renderLive(); });
            livePane.body.appendChild(more);
          }
          lastShown = shown;
        }
      }
    } else if (feedMode === 'everything') {
      // The tape: every event its own row, pinned first, then by when it
      // HAPPENED — or by when we last heard, when update-bubbling is on. No
      // grouping, no cards — the raw view the smart feed is checked against.
      const rows = [...filtered].sort((a, b) => {
        const pa = isPinned(a) ? 1 : 0;
        const pb = isPinned(b) ? 1 : 0;
        return pb - pa || (feedBubble
          ? lastReportOf(b) - lastReportOf(a)
          : whenOf(b).at - whenOf(a).at);
      });
      if (paint) {
        livePane.body.innerHTML = '';
        if (!rows.length) {
          livePane.body.innerHTML = `<div class="empty">${
            liveEvents.length ? 'No events match the filters.' : 'No recent events.'}</div>`;
        } else {
          const shown = rows.slice(0, 300 + capExtra);
          for (const e of shown) livePane.body.appendChild(liveRow(e));
          if (rows.length > shown.length) {
            const more = document.createElement('button');
            more.type = 'button';
            more.className = 'live-agedout';
            more.textContent = `${rows.length - shown.length} more — show 10 more`;
            more.addEventListener('click', () => { capExtra += 10; renderLive(); });
            livePane.body.appendChild(more);
          }
          lastShown = shown;
        }
      }
    } else {
    // Filter first, group second: narrowing to one kind regroups around just
    // those events, so "airstrike" shows airstrike threads, not mixed cards.
    // groupFeed puts server-routed incidents first and falls back to the
    // browser's own grouping for whatever the router has not placed yet.
    //
    // The window chooses the CARDS, not their contents: an aged-out report
    // still belongs to the situation it is part of, and expansion is the one
    // place that must show every one of them (see groupFeedWindowed). Whatever
    // a surfaced card absorbs stops counting as held back, or the pager below
    // would offer to reveal rows already on screen.
    const { groups: sits, attached } =
      groupFeedWindowed(filtered, agedEligible.slice(revived.length));
    agedHeld -= attached.length;
    // A card left open follows the feed. Every number on it is live — reports,
    // channels, the ⤷ latest line, the headline (it upgrades in place) and the
    // extractor's confidence, which a merge raises to the best report's — so a
    // card built three reports ago was quietly showing an old picture beside a
    // feed that had already moved on.
    //
    // Only on a CHANGED signature, and only for the group still under the same
    // key: this runs on the 30 s tick and on every updates poll, and rebuilding
    // an open card on a tick that changed nothing would move it under the
    // reader's hands for no reason. A group that has dropped out of `sits`
    // (the reader narrowed by kind, or muted a channel) is left alone — the
    // card presents an OBJECT, and a feed filter does not close it.
    if (groupCardKey) {
      const fresh = regroup(sits, groupCardKey, groupCardEvents);
      if (fresh && groupSignature(fresh) !== groupCardSig) openGroupCard(fresh);
    }
    // Pinned situations first, then newest OCCURRENCE first — a card whose
    // newest member happened 5 h ago sits 5 h down the feed even when a
    // coverage post touched it 3 minutes ago (that is the ↻ update chip's
    // job to say). A genuinely new member IS a new occurrence, so a card
    // that develops still rises. Bubbling restores last-report order.
    const sitOccAt = (s: { events: EntityDto[] }): number =>
      Math.max(...s.events.map((e) => whenOf(e).at || 0));
    sits.sort((a, b) => {
      const pa = a.events.some(isPinned) ? 1 : 0;
      const pb = b.events.some(isPinned) ? 1 : 0;
      return pb - pa || (feedBubble
        ? b.lastAt - a.lastAt
        : sitOccAt(b) - sitOccAt(a));
    });
    if (paint) {
      livePane.body.innerHTML = '';
      if (!sits.length) {
        livePane.body.innerHTML = `<div class="empty">${
          liveEvents.length ? 'No events match the filters.' : 'No recent events.'}</div>`;
        shownGroups = new Map();
      } else {
        const shown = sits.slice(0, 150 + capExtra);
        for (const sit of shown) livePane.body.appendChild(situationCard(sit));
        // The cap used to be silent, which makes a truncated feed look complete.
        if (sits.length > shown.length) {
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'live-agedout';
          more.textContent = `${sits.length - shown.length} more — show 10 more`;
          more.addEventListener('click', () => { capExtra += 10; renderLive(); });
          livePane.body.appendChild(more);
        }
        lastShown = shown.flatMap((s) => s.events);
        shownGroups = new Map(shown.map((s) => [s.key, s.events]));
      }
    }
    }
    if (paint && muted) {
      const note = document.createElement('button');
      note.type = 'button';
      note.className = 'live-agedout';
      note.title = 'Muted places/channels and dismissed events. Click to clear.';
      note.textContent = `${muted} hidden by mutes — clear`;
      note.addEventListener('click', () => { clearMutes(); undismissAll(); });
      livePane.body.appendChild(note);
    }
    // NEVER hide a count silently. The damage window once defaulted to 3 days
    // and hid 422 of 441 markers, which read as "the toggles are broken" — so
    // the window says what it is holding back. It pages ("show 10 more") rather
    // than lifting wholesale: the window is THE filter, and flipping it to
    // all-time on a click threw away the reader's own setting to answer a
    // one-off question.
    if (paint && agedHeld) {
      const note = document.createElement('button');
      note.type = 'button';
      note.className = 'live-agedout';
      note.title = 'These happened longer ago than the window. Reveals the next 10, newest first — the window setting itself stays put.';
      note.textContent = `${agedHeld} older than the ${liveFadeHours()}h window — show 10 more`;
      note.addEventListener('click', () => {
        liveExtra += 10;
        renderLive();
      });
      livePane.body.appendChild(note);
    }
    // THE BADGE COUNTS BOTH LANES, AND IT IS SET HERE RATHER THAN INSIDE EACH
    // BRANCH FOR EXACTLY THAT REASON. It used to be per-branch, which was fine
    // while there were two views of one set of records; with a third lane
    // holding a DIFFERENT set, a per-branch badge would mean "unread in the lane
    // you happen to be reading" — so parking in News with the pane closed would
    // hide an event backlog, and the reverse. It also no longer depends on the
    // search box: how much you have not read is not a question about your
    // filters. The event half counts unread SITUATIONS (a number that never
    // goes down is not a signal), which is what the situations lane always did.
    updateLiveBadge();
    if (!paint) return;
    // One thumbnail per picture needs the pictures' hashes; asked after the
    // paint for what is on screen, and a repaint only when something arrived.
    void learnMediaHashes(lastShown)
      .then((fresh) => { if (fresh) renderLive(); });
    // Ask about thermal anomalies for what is actually on screen, after painting
    // so the feed never waits on a badge. News has no coordinate, so nothing to
    // ask about — heat is a question about a place. The image-echo caution
    // rides the same moment: both are read-time verdicts about what is shown.
    if (feedMode !== 'news') {
      void refreshHeat(lastShown);
      void refreshEcho(lastShown);
    }
    armAutoRead();
  }

  function updateLiveBadge(): void {
    const unreadSitCount = groupFeed(liveEvents)
      .filter((s) => s.events.some(isUnread)).length;
    const unreadNewsCount = newsItems.filter(isUnread).length;
    const badge = unreadSitCount + unreadNewsCount;
    shell.setBadge('live', badge ? String(badge) : '');
    // Which lane the unread is IN — otherwise a badge that will not clear sends
    // the reader hunting through the wrong view.
    modeNews.textContent = unreadNewsCount ? `News ${unreadNewsCount}` : 'News';
  }

  /** The read cursor moved and nothing else: every painted row and card keeps
   *  its place and contents, so toggle the unread mark in place and recount the
   *  badge. This fires after every auto-read dwell — once per poll that brought
   *  anything new — and used to rebuild the whole pane. */
  function refreshUnreadMarks(): void {
    const byId = new Map<string, EntityDto>();
    for (const e of lastShown) byId.set(e.id, e);
    livePane.body.querySelectorAll<HTMLElement>('.live-row[data-eid]').forEach((row) => {
      const e = byId.get(row.dataset.eid!);
      if (e) row.classList.toggle('live-unread', isUnread(e));
    });
    livePane.body.querySelectorAll<HTMLElement>('.live-sit[data-skey]').forEach((card) => {
      const events = shownGroups.get(card.dataset.skey!);
      if (events) card.classList.toggle('live-unread', events.some(isUnread));
    });
    updateLiveBadge();
  }

  // Looking at the feed IS reading it: with the pane open, whatever rendered
  // marks itself read after a short dwell — no click required. Re-armed on
  // every render, so events that arrive while the reader is watching get read
  // too; guarded on "anything actually unread" so the markRead → change →
  // re-render cycle terminates. The cursor still only advances to what was
  // ACTUALLY SHOWN (readstate's rule) — lastShown is exactly that, so a
  // truncated poll's missing events stay unread.
  let readDwell: number | undefined;
  function armAutoRead(): void {
    if (readDwell) window.clearTimeout(readDwell);
    if (shell.current() !== 'live') return;
    readDwell = window.setTimeout(() => {
      if (shell.current() === 'live' && lastShown.some(isUnread)) {
        markRead(lastShown);
      }
    }, 2500);
  }

  // Triage is per-browser and instant: a pin, mute or dismiss repaints the feed
  // with no fetch. resetReadStateListeners first because renderApp is re-entrant
  // (the same reason story.ts resets its own listener list) — without it each
  // re-render would stack another subscription and renderLive would run N times.
  resetReadStateListeners();
  onReadStateChange((kind) => {
    // The read cursor moving changes no row's place or contents — only which
    // are marked unread — so restyle in place rather than rebuilding the pane.
    // Pins, mutes and dismissals change what is shown and need the rebuild.
    if (kind === 'read') refreshUnreadMarks(); else renderLive();
  });

  // Ages drive the pin colours, map dots, ticker, and times — redraw periodically.
  window.setInterval(() => {
    if (liveEvents.length) {
      renderLive();
      pushLive();
      setLiveEvents(map, liveEvents);
    }
  }, 30_000);

  // --- Suggestions (the analyst pass, pending user confirmation) -----------

  let suggestions: SuggestionDto[] = [];

  intelPane.tools.hidden = false;
  intelPane.tools.innerHTML = `
    <span class="progress" data-f="lastrun" style="margin-left:0"></span>
    ${me.isAdmin
      ? `<button type="button" class="mini" data-f="debug" style="margin-left:auto"
          title="Why is (or isn't) the analyst proposing anything?">Debug</button>
         <button type="button" class="mini" data-f="seqs"
          title="Build event-sequence patterns from history — which launch sites' events foreshadow impacts where. Powers the ⤳ chips in the feed.">Sequences</button>
         <button type="button" class="mini" data-f="run"
          title="Run the analyst pass now">Run now</button>`
      : ''}`;
  const intelSeqs = intelPane.tools.querySelector('[data-f="seqs"]') as HTMLButtonElement | null;
  if (intelSeqs) {
    intelSeqs.addEventListener('click', async () => {
      intelSeqs.disabled = true;
      intelSeqs.textContent = 'Building…';
      try {
        const out = await post<{ patterns: number; judged: number;
                                 launches: number }>('/api/sequences/run');
        await loadSequences();
        renderLive();
        toast(`Sequences: ${out.patterns} pattern(s) from ${out.launches} `
          + `launch(es), ${out.judged} model-judged.`);
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Sequence build failed.');
      } finally {
        intelSeqs.disabled = false;
        intelSeqs.textContent = 'Sequences';
      }
    });
  }
  const intelRun = intelPane.tools.querySelector('[data-f="run"]') as HTMLButtonElement | null;
  const intelDebug = intelPane.tools.querySelector('[data-f="debug"]') as HTMLButtonElement | null;
  const intelLastRun = intelPane.tools.querySelector('[data-f="lastrun"]') as HTMLElement;
  if (intelRun) {
    intelRun.addEventListener('click', async () => {
      intelRun.disabled = true;
      intelRun.textContent = 'Running…';
      try {
        await post('/api/suggestions/run');
        await loadSuggestions();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Analyst pass failed.');
      } finally {
        intelRun.disabled = false;
        intelRun.textContent = 'Run now';
      }
    });
  }
  if (intelDebug) {
    intelDebug.addEventListener('click', async () => {
      if (intelPane.body.dataset.view === 'debug') {   // toggle back to the list
        delete intelPane.body.dataset.view;
        renderIntel();
        return;
      }
      intelDebug.disabled = true;
      try {
        const dto = await get<SuggestionDebugDto>('/api/suggestions/debug');
        renderDebug(dto);
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Diagnostic failed.');
      } finally {
        intelDebug.disabled = false;
      }
    });
  }

  function renderDebug(d: SuggestionDebugDto): void {
    intelPane.body.dataset.view = 'debug';
    const pct = d.events.total ? Math.round((d.events.eligible / d.events.total) * 100) : 0;
    const llm = d.llm.configured
      ? `${esc(d.llm.model ?? 'model')}${d.llm.webSearch ? ' · web search' : ''}`
        + ` · reviews ${d.llm.reviewsEnabled ? 'on' : 'off'}`
      : `not configured${d.llm.hint ? ` — ${esc(d.llm.hint)}` : ''}`;
    const verdict = d.wouldEmit > 0
      ? `<b class="sug-dbg-ok">Would propose ${d.wouldEmit} now.</b>`
      : `<b class="sug-dbg-none">Would propose nothing right now.</b>`;
    const groups = d.groups.map((g) => {
      const rows = g.clusters.length
        ? g.clusters.map((c) => {
            const ok = c.decision === 'would-emit';
            return `<div class="sug-dbg-cl">
              <span class="${ok ? 'sug-dbg-ok' : 'sug-dbg-skip'}">${ok ? '✓' : '✕'}</span>
              <span>${esc(c.place ?? `${c.lat}, ${c.lon}`)} · ${c.events} events<br>
                <span class="sug-dbg-reason">${esc(c.reason)}</span></span></div>`;
          }).join('')
        : '<div class="sug-dbg-cl sug-dbg-reason">no cluster reached the threshold</div>';
      return `<div class="sug-dbg-grp"><div class="sug-dbg-h">${esc(g.group)}
        <span class="sug-dbg-reason">(${g.clusterCount} cluster${g.clusterCount === 1 ? '' : 's'})</span>
        </div>${rows}</div>`;
    }).join('');
    intelPane.body.innerHTML = `<div class="sug-dbg">
      <div class="sug-dbg-verdict">${verdict}</div>
      <div class="sug-dbg-kv"><b>LLM</b> ${llm}</div>
      <div class="sug-dbg-kv"><b>Events (${d.events.windowDays}d)</b>
        ${d.events.total} total, ${d.events.eligible} eligible to define a site (${pct}%)</div>
      <div class="sug-dbg-kv"><b>Pending</b> ${d.pendingCount} ·
        cluster radius ${d.config.clusterEpsKm} km · min ${d.config.minEvents} events ·
        suppress ${d.config.suppressDays}d ·
        every ${d.config.intervalHours}h${d.config.enabled ? '' : ' (DISABLED)'}</div>
      ${groups}
      <div class="sug-dbg-kv"><b>Graph</b> ${d.graph.snapshotEntities} entities in snapshot,
        ${d.graph.heuristicPairs} co-location pair(s)</div>
      <div class="sug-dbg-reason" style="margin-top:8px">Clusters that snap onto a place you
        already track — or that were suggested in the last ${d.config.suppressDays} days — are
        skipped on purpose. “Would propose nothing” with healthy clusters means the map already
        covers them.</div>
    </div>`;
  }

  // A needs_placement suggestion can land as a single point or a drawn area
  // (polygon footprint). Swap the row's actions for that choice; picking one
  // arms the matching map interaction. Cancel restores the row on next reload.
  function showPlacementChoice(s: SuggestionDto, row: HTMLElement): void {
    const actions = row.querySelector('.sug-actions');
    if (!actions) { void beginPlacement(s, 'point'); return; }
    const canArea = typeof opts.captureArea === 'function';
    actions.innerHTML = `
      <button type="button" class="mini sug-add" data-p="point"
        title="Drop a single marker">Point</button>
      ${canArea ? `<button type="button" class="mini sug-add" data-p="area"
        title="Draw a polygon footprint">Area</button>` : ''}
      <button type="button" class="mini" data-p="cancel" title="Cancel">✕</button>`;
    actions.querySelector('[data-p="point"]')!.addEventListener('click', (ev) => {
      ev.stopPropagation();
      void beginPlacement(s, 'point');
    });
    actions.querySelector('[data-p="area"]')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      void beginPlacement(s, 'area');
    });
    actions.querySelector('[data-p="cancel"]')!.addEventListener('click', (ev) => {
      ev.stopPropagation();
      void loadSuggestions();
    });
  }

  async function acceptPlacement(s: SuggestionDto, geometry: object): Promise<void> {
    try {
      const out = await post<{ entity?: EntityDto }>(
        `/api/suggestions/${s.id}/accept`, { geometry });
      if (out.entity) {
        bumpTiles(map, out.entity.rev);
        toast(`Placed: ${out.entity.name ?? out.entity.type}`);
        await Promise.all([refresh(), loadSuggestions()]);
        openEntity(out.entity.id);
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not place it.');
    }
  }

  async function beginPlacement(s: SuggestionDto, mode: 'point' | 'area'): Promise<void> {
    if (s.geometry?.type === 'Point') {
      map.flyTo({ center: s.geometry.coordinates, zoom: 11 });
    }
    if (mode === 'area' && opts.captureArea) {
      const ring = await opts.captureArea('this site');
      if (!ring) { await loadSuggestions(); return; }
      await acceptPlacement(s, { type: 'Polygon', coordinates: [ring] });
      return;
    }
    toast('Click the map to place this site (Esc cancels).');
    map.getCanvas().style.cursor = 'crosshair';
    const cancel = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      map.off('click', place);
      map.getCanvas().style.cursor = '';
      document.removeEventListener('keydown', cancel);
      void loadSuggestions();
    };
    const place = async (ev: maplibregl.MapMouseEvent) => {
      map.getCanvas().style.cursor = '';
      document.removeEventListener('keydown', cancel);
      await acceptPlacement(s, { type: 'Point', coordinates: [ev.lngLat.lng, ev.lngLat.lat] });
    };
    map.once('click', place);
    document.addEventListener('keydown', cancel);
  }

  function suggestionRow(s: SuggestionDto): HTMLElement {
    const isEdge = Boolean(s.edgeType);
    const needsPlace = Boolean(s.proposedAttrs?.needs_placement);
    const row = document.createElement('div');
    row.className = 'row sug-row';
    const since = relTime(s.createdAt);
    const meta = isEdge
      ? `relation · suggested ${esc(since)} ago`
      : `${s.evidenceCount} events · suggested ${esc(since)} ago`;
    const addLabel = isEdge ? 'Link' : needsPlace ? 'Place' : 'Add';
    const addTitle = isEdge ? 'Create this relation'
      : needsPlace ? 'Place this site as a point or a drawn area'
      : `Add ${esc(s.proposedName ?? s.proposedType ?? '')} to the map`;
    const attr = (k: string) =>
      typeof s.proposedAttrs?.[k] === 'string' ? (s.proposedAttrs[k] as string) : '';
    const identity = [attr('affiliation'), attr('purpose')].filter(Boolean).join(' · ');
    row.innerHTML = `
      <span class="t">
        <span class="n">${isEdge ? '⤿ ' : ''}${esc(s.title)}</span>
        ${identity ? `<span class="s sug-id">${esc(identity)}</span>` : ''}
        ${s.body ? `<span class="s sug-body">${esc(s.body)}</span>` : ''}
        <span class="s sug-meta">${meta}</span>
      </span>
      <span class="sug-actions">
        <button type="button" class="mini sug-add" data-f="accept" title="${addTitle}">
          ${addLabel}</button>
        <button type="button" class="mini" data-f="dismiss" title="Dismiss">✕</button>
      </span>`;
    row.addEventListener('click', () => {
      if (s.geometry?.type === 'Point') {
        map.flyTo({ center: s.geometry.coordinates, zoom: 10 });
      }
    });
    row.querySelector('[data-f="accept"]')!.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      if (needsPlace) { showPlacementChoice(s, row); return; }
      try {
        const out = await post<{ entity?: EntityDto; edge?: { src: string } }>(
          `/api/suggestions/${s.id}/accept`);
        if (out.entity) {
          bumpTiles(map, out.entity.rev);
          toast(`Added: ${out.entity.name ?? out.entity.type}`);
          await Promise.all([refresh(), loadSuggestions()]);
          openEntity(out.entity.id);
        } else {
          toast('Relation created.');
          await loadSuggestions();
          if (out.edge) openEntity(out.edge.src);
        }
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not add it.');
      }
    });
    row.querySelector('[data-f="dismiss"]')!.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      try {
        await post(`/api/suggestions/${s.id}/dismiss`);
        await loadSuggestions();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Dismiss failed.');
      }
    });
    return row;
  }

  // "Possible new system" (045). A GLOBAL subject — a designation the reports
  // keep using that the registry has no type for — so it belongs here rather
  // than on a group card. Research drafts the type; a human still creates it.
  function equipmentRow(e: ReturnType<typeof equipmentSuggestions>[number]): HTMLElement {
    const row = document.createElement('div');
    row.className = 'row sug-row';
    row.innerHTML = `
      <div class="sug-main">
        <b>⛭ ${esc(e.name)}</b>
        <small>possible new ${esc(e.class)} · not tracked · seen in
          ${e.evidenceCount} report(s) · ${relTime(e.createdAt)} ago</small>
        <small class="sug-why">${esc(e.why)}</small>
      </div>
      <div class="sug-acts">
        <button type="button" class="mini" data-eq-research="${e.id}">Research</button>
        <button type="button" class="mini ghost" data-eq-dismiss="${e.id}"
          title="Not a new system">✕</button>
      </div>`;
    row.querySelector('[data-eq-research]')!.addEventListener('click', async () => {
      const button = row.querySelector('[data-eq-research]') as HTMLButtonElement;
      button.disabled = true;
      button.textContent = 'Researching…';
      try {
        const { name, draft } = await researchEquipment(e.id);
        openTypeCreator(registry, { query: name, draft: draft as never },
                        () => { void applyEquipment(e.id).then(renderIntel); });
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Research failed.');
      } finally {
        button.disabled = false;
        button.textContent = 'Research';
      }
    });
    row.querySelector('[data-eq-dismiss]')!.addEventListener('click', async () => {
      await dismissAdvice('equipment', e.id);
      renderIntel();
    });
    return row;
  }

  // "Unlocatable place" (051), equipment's sibling: reports keep naming a
  // place the geocoder cannot resolve, the model proposes where it actually
  // is, and Teach writes the operator-approved coordinate the geocoder
  // consults from then on. The coords are IN the row — the curator approves
  // a specific point, never a claim.
  function geoRow(g: ReturnType<typeof geoSuggestions>[number]): HTMLElement {
    const row = document.createElement('div');
    row.className = 'row sug-row';
    const coords = `${g.lat.toFixed(4)}, ${g.lon.toFixed(4)}`;
    row.innerHTML = `
      <div class="sug-main">
        <b>⌖ ${esc(g.name)}${g.country ? `, ${esc(g.country)}` : ''}</b>
        <small>unlocatable place · proposed ${esc(coords)} · seen in
          ${g.evidenceCount} report(s) · ${relTime(g.createdAt)} ago</small>
        <small class="sug-why">${esc(g.why)}</small>
      </div>
      <div class="sug-acts">
        <button type="button" class="mini" data-geo-view="${g.id}"
          title="Fly the map to the proposed point">View</button>
        <button type="button" class="mini" data-geo-apply="${g.id}"
          title="Teach the geocoder: future reports of this name resolve to this point. Stored events keep what they got.">Teach</button>
        <button type="button" class="mini ghost" data-geo-dismiss="${g.id}"
          title="Wrong place, or not confident">✕</button>
      </div>`;
    row.querySelector('[data-geo-view]')!.addEventListener('click', () => {
      map.flyTo({ center: [g.lon, g.lat], zoom: 11 });
    });
    row.querySelector('[data-geo-apply]')!.addEventListener('click', async () => {
      try {
        await applyGeo(g.id);
        toast(`Taught: ${g.name} → ${coords}. Future reports resolve there.`);
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not teach.');
      }
      renderIntel();
    });
    row.querySelector('[data-geo-dismiss]')!.addEventListener('click', async () => {
      await dismissAdvice('geo', g.id);
      renderIntel();
    });
    return row;
  }

  function renderIntel(): void {
    delete intelPane.body.dataset.view;
    intelPane.body.innerHTML = '';
    // Territorial control first (054): a district changing hands is the one
    // proposal here that repaints the map, so it leads.
    const control = controlSuggestions();
    for (const row of controlRows(map, renderIntel)) intelPane.body.appendChild(row);
    const equipment = equipmentSuggestions();
    for (const e of equipment) intelPane.body.appendChild(equipmentRow(e));
    const geo = geoSuggestions();
    for (const g of geo) intelPane.body.appendChild(geoRow(g));
    if (!suggestions.length) {
      if (!equipment.length && !geo.length && !control.length) {
        intelPane.body.innerHTML = `<div class="empty">No suggestions right now.
          The analyst pass watches the live feed for patterns — repeated launches
          or interceptions from one area — and proposes what is probably there.</div>`;
      }
      return;
    }
    for (const s of suggestions) intelPane.body.appendChild(suggestionRow(s));
  }

  async function loadSuggestions(): Promise<void> {
    try {
      const dto = await get<{ suggestions: SuggestionDto[]; lastRun: string | null }>(
        '/api/suggestions?status=pending');
      suggestions = dto.suggestions;
      intelLastRun.textContent = dto.lastRun
        ? `last pass ${relTime(dto.lastRun)} ago` : 'no pass yet';
      renderIntel();
    } catch { /* best-effort; next refresh retries */ }
  }

  // --- Log (change history + rule alerts, one chronological stream) --------

  const logIntro = document.createElement('div');
  logIntro.className = 'log-intro';
  logIntro.textContent = 'Every change to the shared picture — who added, moved, '
    + 'or edited what — plus rule alerts.';
  logPane.root.insertBefore(logIntro, logPane.body);

  const recentActivity = new Map<string, { el: HTMLElement; at: number }>();

  shell.onOpen((id) => {
    // A pane that changed while closed paints now (see paneDirty).
    if (id && paneDirty[id]) {
      if (id === 'objects') renderObjects();
      else if (id === 'places') renderPlaces();
      else if (id === 'damage') renderDamage();
      else if (id === 'live') renderLive();
    }
    if (id === 'live') armAutoRead();   // opening the feed starts the read dwell
  });

  function activityRow(e: EntityDto): HTMLElement {
    const type = registry.types.find((t) => t.key === e.type);
    const verb = ACT_VERBS[e.changeKind] ?? e.changeKind;
    const row = document.createElement('div');
    row.className = 'row act-row';
    row.innerHTML = `<span class="dot" style="background:${type?.style.color ?? '#2f6fed'}"></span>
      <span class="t"><span class="n">${esc(e.name ?? '(unnamed)')}</span>
      <span class="s"><b class="verb">${esc(verb)}</b> · ${esc(type?.label ?? e.type)}</span></span>
      <time>${relTime(e.validFrom)}</time>`;
    row.addEventListener('click', () => openEntity(e.id));
    return row;
  }

  function deletedRow(id: string): HTMLElement {
    // The pre-refresh entity cache usually still knows the name.
    const known = all.find((e) => e.id === id);
    const row = document.createElement('div');
    row.className = 'row act-row act-removed';
    row.innerHTML = `<span class="dot" style="background:#b6bec9"></span>
      <span class="t"><span class="n">${known ? esc(known.name ?? '(unnamed)') : '(name unknown)'}</span>
      <span class="s"><b class="verb">removed</b>${known ? ` · ${esc(known.type)}` : ''}</span></span>
      <time>now</time>`;
    return row;
  }

  function logActivity(dto: UpdatesDto): void {
    const now = Date.now();
    // Live events have their own view; damage markers arrive in bulk imports
    // and would bury the real edits.
    const QUIET = ['event', 'damage'];
    const fresh = dto.entities.filter((e) => !QUIET.includes(e.category));
    for (const e of fresh.slice(0, 12)) {
      const row = activityRow(e);
      const prev = recentActivity.get(e.id);
      if (prev && now - prev.at < 5000 && prev.el.isConnected) {
        prev.el.replaceWith(row);  // no-op churn: same object again within 5 s
      } else {
        logPane.body.prepend(row);
      }
      recentActivity.set(e.id, { el: row, at: now });
    }
    // Deletions arrive as bare ids; the pre-refresh cache still knows what
    // they were, so quiet categories stay quiet on the way out too.
    const loud = dto.deleted.filter((id) => {
      const known = all.find((e) => e.id === id);
      return !known || !QUIET.includes(known.category);
    });
    for (const id of loud.slice(0, 12)) logPane.body.prepend(deletedRow(id));
    while (logPane.body.childElementCount > 80) {
      logPane.body.lastElementChild!.remove();
    }
    for (const [id, v] of recentActivity) if (!v.el.isConnected) recentActivity.delete(id);
  }

  function logAlert(n: UpdatesDto['notifications'][number]): void {
    alertCount += 1;
    const row = document.createElement('div');
    row.className = 'row log-alert';
    row.innerHTML = `<span class="dot" style="background:var(--warn)"></span>
      <span class="t"><span class="n">${esc(n.title)}</span>
      <span class="s">${esc(n.body ?? '')}</span></span>
      <time>${relTime(n.createdAt)}</time>`;
    const entityId = n.entityId;
    if (entityId) row.addEventListener('click', () => openEntity(entityId));
    logPane.body.prepend(row);
    toast(n.title);
  }

  // --- Layers popover (category toggles + FIRMS) ---------------------------

  function overlayPrefs(): Record<string, boolean> {
    try {
      return JSON.parse(localStorage.getItem(OVERLAY_KEY) ?? '{}') as Record<string, boolean>;
    } catch {
      return {};
    }
  }
  function saveOverlayPref(key: string, on: boolean): void {
    const p = overlayPrefs();
    p[key] = on;
    localStorage.setItem(OVERLAY_KEY, JSON.stringify(p));
  }

  const cats = (getRegistry() ?? registry).categories.slice().sort((a, b) => a.sort - b.sort);
  const catState: Record<string, boolean> = {};
  {
    const stored = overlayPrefs();
    for (const c of cats) {
      catState[c.key] = stored[c.key] ?? opts.overlayDefaults?.[c.key] ?? true;
    }
  }
  let firmsOn = false;  // FIRMS is always a fresh opt-in — shared server quota
  let admOn = overlayPrefs()['_adm'] ?? false;
  // Aircraft, like FIRMS, is a fresh opt-in every session rather than a saved
  // preference: both poll a shared free upstream, and a tab left open for days
  // is this codebase's standing assumption (watchForNewBuild exists for it).
  let acOn = false;
  let flashOn = overlayPrefs()['_flash'] ?? true;
  let coverageOn = overlayPrefs()['_coverage'] ?? true;
  // Overture building volumes (buildings.ts). Opt-in like every 3D visual.
  // Applied from applyOverlays(), which main.ts calls once the layers exist —
  // panels install before the style loads, so applying here would no-op.
  let buildingsOn = overlayPrefs()['_buildings3d'] ?? false;
  // Oil and gas pipelines (pipelines.ts) — static reference lines from our
  // own tiles, so a saved preference like the admin lines, and on unless
  // turned off: it was asked for to be seen.
  let pipelinesOn = overlayPrefs()['_pipelines'] ?? true;
  // Which damage markers draw (a TimeWindow spec). They are a HISTORICAL
  // record, not a live feed — a campaign's marks are the point of keeping them
  // — so the default is all of them. Narrowing gives back the live-ops view of
  // "what was hit this week", and a custom range answers "what happened during
  // the June exchange" without that meaning "the last N days from today".
  const DAMAGE_PRESETS: [string, string][] = [
    ['', 'All time'], ['h:24', 'Last 24 hours'], [`d:${DAMAGE_RECENT_DAYS}`, 'Last 3 days'],
    ['d:7', 'Last 7 days'], ['d:14', 'Last 14 days'], ['d:30', 'Last 30 days'],
    ['d:90', 'Last 90 days'], ['d:365', 'Last year'],
  ];
  // normalizeWindow folds the older bare-day-count form ('3') into 'd:3' here
  // rather than everywhere downstream — the preset <select> has to find its
  // own option or it would silently read as a custom range.
  let damageSpec: string = normalizeWindow(localStorage.getItem(DAMAGE_DAYS_KEY))
    // Older still: a boolean, where unchecked meant "recent only".
    || (overlayPrefs()['_damageAll'] === false ? `d:${DAMAGE_RECENT_DAYS}` : '');

  // FIRMS windows are bounded by the store's 60-day depth, so there is no
  // "all time" — the widest preset IS everything it holds.
  const FIRMS_PRESETS: [string, string][] = [
    ['h:3', 'Last 3 hours'], ['h:6', 'Last 6 hours'], ['h:12', 'Last 12 hours'],
    ['h:24', 'Last 24 hours'], ['h:48', 'Last 2 days'], ['h:72', 'Last 3 days'],
    ['d:7', 'Last 7 days'], ['d:14', 'Last 14 days'], ['d:30', 'Last 30 days'],
    ['d:60', 'Last 60 days'],
  ];
  let firmsSpec: string = localStorage.getItem(FIRMS_WINDOW_KEY) ?? 'h:24';

  // Map narrowing: per-type visibility (missing = on), watched-only places,
  // and a country filter. All map-side setFilter state, persisted per browser.
  let watchedOnly = overlayPrefs()['_watchedOnly'] ?? false;
  let countryFilter = localStorage.getItem(COUNTRY_KEY) ?? '';
  const typeState: Record<string, boolean> = (() => {
    try { return JSON.parse(localStorage.getItem(TYPE_FILTER_KEY) ?? '{}') as Record<string, boolean>; }
    catch { return {}; }
  })();
  const allTypes = (getRegistry() ?? registry).types.slice().sort((a, b) => a.sort - b.sort);
  const anyTypeOff = () => allTypes.some((t) => typeState[t.key] === false);
  // The category/type state a BULK press replaced (Show all, Hide all, a
  // group's box, a solo), so pressing the same control again can put it back.
  // `by` names the control that owns the undo. Any hand-made change drops it.
  let layerUndo: { by: string; cats: Record<string, boolean>;
                   types: Record<string, boolean> } | null = null;
  // Set once the legend exists (layersHost block); rebuildViews refreshes its
  // counts whenever the data changes while it is open.
  let refreshLegend: (() => void) | null = null;
  // The territorial-control legend under its category row (territory.ts):
  // one row per territory, plus the reassign / track flows for curators.
  let controlLegend: { el: HTMLElement; refresh: () => void } | null = null;

  // Country choices come from the data itself (imports carry attrs.country,
  // live events attrs.admin0; ";"-joined values split into their parts).
  function knownCountries(): string[] {
    const found = new Set<string>();
    for (const e of all) {
      const c = e.attrs.country ?? e.attrs.admin0;
      if (typeof c !== 'string') continue;
      for (const part of c.split(';')) {
        const name = part.trim();
        if (name) found.add(name);
      }
    }
    return [...found].sort((a, b) => a.localeCompare(b));
  }

  let domesOn = overlayPrefs()['_domes'] ?? false;  // 3D is opt-in
  let domeRadar = overlayPrefs()['_domeRadar'] ?? true;
  let domeEngage = overlayPrefs()['_domeEngage'] ?? true;
  // Height exaggeration is numeric (1 = true-scale), so it lives in its own key.
  let domeExagg = Number(localStorage.getItem('cockpit-dome-exagg') || '1') || 1;

  function applyDomes(): void {
    updateDomes(map, objects, { on: domesOn, exaggeration: domeExagg,
      showRadar: domeRadar, showEngage: domeEngage });
  }

  // Re-anchor AGL sensors after terrain is toggled or a relevant DEM tile
  // arrives. Debouncing prevents a mesh rebuild for every tile event.
  let domeTerrainTimer: number | undefined;
  const scheduleTerrainDomeRefresh = () => {
    if (!domesOn) return;
    if (domeTerrainTimer !== undefined) window.clearTimeout(domeTerrainTimer);
    domeTerrainTimer = window.setTimeout(() => {
      domeTerrainTimer = undefined;
      applyDomes();
    }, 120);
  };
  map.on('terrain', scheduleTerrainDomeRefresh);
  map.on('sourcedata', (event) => {
    if (event.sourceId === TERRAIN_SOURCE_ID) scheduleTerrainDomeRefresh();
  });

  function applyCoverage(): void {
    setCoverageEntities(map, objects, coverageOn);
    applyDomes();
  }

  function setCoverage(enabled: boolean): void {
    coverageOn = enabled;
    saveOverlayPref('_coverage', enabled);
    applyCoverage();
    document.querySelectorAll<HTMLInputElement>('[data-coverage-toggle]')
      .forEach((el) => { el.checked = enabled; });
  }

  function setDomes(enabled: boolean): void {
    domesOn = enabled;
    saveOverlayPref('_domes', enabled);
    // The dome shader consumes Mercator world coordinates and no-ops on the
    // globe (see domes.ts render), so on a globe projection the volumes stay
    // invisible until you switch by hand. Flip to the flat tilt view for the
    // operator when they turn volumes on — that's the view the feature names.
    if (enabled && map.getProjection()?.type === 'globe') {
      map.setProjection({ type: 'mercator' });
      toast('Switched to the flat (Mercator) view — 3D sensor volumes render there.');
    }
    applyDomes();   // updateDomes pumps repaints on the off->on transition
    document.querySelectorAll<HTMLInputElement>('[data-domes-toggle]')
      .forEach((el) => { el.checked = enabled; });
  }

  function applyOverlays(): void {
    if (!map.getLayer('entities-symbol')) return;  // object layers not installed yet
    setBuildings(map, buildingsOn);
    setPipelines(map, pipelinesOn);
    applyObjectFilter(map, {
      categories: catState,
      damageRange: resolveWindow(damageSpec),
      types: anyTypeOff()
        ? allTypes.filter((t) => typeState[t.key] !== false).map((t) => t.key)
        : null,
      watchedOnly,
      country: countryFilter || null,
    });
    setAdmOverlay(map, admOn);
  }

  // A sheet's layers (cockpitsheet.ts). Applying writes the same state the
  // panel's own controls write — memory, the stored prefs, the map filter —
  // and then lets the panel repaint its rows (set once the panel exists).
  let onLayerStateApplied: (() => void) | null = null;
  let damageSync: (() => void) | null = null;
  function layerState(): LayerStateDto {
    return { categories: { ...catState }, types: { ...typeState }, adm: admOn, watchedOnly,
             country: countryFilter, damage: damageSpec };
  }
  function applyLayerState(s: LayerStateDto): void {
    layerUndo = null;   // a sheet's layers replace the view; nothing to go back to
    if (s.categories) {
      for (const c of cats) {
        const v = s.categories[c.key];
        if (typeof v !== 'boolean') continue;
        catState[c.key] = v;
        saveOverlayPref(c.key, v);
      }
    }
    if (s.types) {
      for (const k of Object.keys(typeState)) delete typeState[k];
      for (const [k, v] of Object.entries(s.types)) if (v === false) typeState[k] = false;
      localStorage.setItem(TYPE_FILTER_KEY, JSON.stringify(typeState));
    }
    if (typeof s.adm === 'boolean') { admOn = s.adm; saveOverlayPref('_adm', admOn); }
    if (typeof s.watchedOnly === 'boolean') {
      watchedOnly = s.watchedOnly;
      saveOverlayPref('_watchedOnly', watchedOnly);
    }
    if (typeof s.country === 'string') {
      countryFilter = s.country;
      localStorage.setItem(COUNTRY_KEY, countryFilter);
    }
    if (typeof s.damage === 'string') {
      damageSpec = normalizeWindow(s.damage);
      localStorage.setItem(DAMAGE_DAYS_KEY, damageSpec);
    }
    applyOverlays();
    onLayerStateApplied?.();
  }

  if (opts.layersHost) {
    const host = opts.layersHost;   // the right-side map rail (holds basemap too)

    // One right-side panel that is layers AND legend at once: each category
    // row carries its toggle, colour swatch, and live count; multi-type
    // categories expand into per-type rows with the same affordances; and the
    // controls that narrow one category (the damage window, FIRMS age) nest
    // under the row they modify instead of floating in their own list.
    // Section order: Filter, then the four entity domains, then the
    // non-entity overlays. The map's right gutter is the natural home — next
    // to the basemap picker, out of the top strip.
    const wrap = document.createElement('div');
    wrap.className = 'legend-wrap';
    // Top row: basemap picker + the Layers button side by side; the pod opens
    // below spanning the full row width.
    wrap.innerHTML = `
      <div class="rail-top">
        <button type="button" class="legend-btn" title="Layers &amp; legend">
          <span class="lb-glyph">≣</span> Layers</button>
      </div>
      <div class="lg-chips" hidden></div>
      <div class="pod legend-pod" hidden>
        <header>
          <span class="lyr-title">Layers</span>
          <button type="button" class="mini lyr-bulk" data-l="all"></button>
          <button type="button" class="mini lyr-bulk" data-l="none"></button>
          <span class="x" title="Close">✕</span>
        </header>
        <div class="lyr-pane"></div>
      </div>`;
    host.appendChild(wrap);
    // Lift the basemap <select> (main.ts created it as the rail's first child)
    // up beside the Layers button.
    const railTop = wrap.querySelector('.rail-top') as HTMLElement;
    const basemapSel = host.querySelector('#basemap');
    if (basemapSel) railTop.insertBefore(basemapSel, railTop.firstChild);
    const panelBtn = wrap.querySelector('.legend-btn') as HTMLButtonElement;
    const pod = wrap.querySelector('.legend-pod') as HTMLElement;
    const pane = wrap.querySelector('.lyr-pane') as HTMLElement;

    // "partial" means the view is narrowed from its defaults — so it follows
    // each control's DEFAULT, not its truthiness. Damage defaults to the whole
    // record, so any window at all counts as a narrowed view.
    // Every layer switch ends here, so it is also where the layer groups'
    // on / half / off marks follow (set once the groups exist, below).
    let syncGroupsUi = (): void => {};
    const syncBtn = (): void => {
      panelBtn.classList.toggle('partial',
        Object.values(catState).some((v) => !v) || firmsOn || acOn || admOn || !coverageOn
        || damageSpec !== '' || domesOn || watchedOnly || !!countryFilter || anyTypeOff()
        || !bmShown || !markupOn);
      syncGroupsUi();
    };

    // A preset dropdown plus a "Custom range…" escape hatch that reveals two
    // date fields. Damage and FIRMS both need exactly this, so it is built once
    // and instantiated twice; each owns what its resolved window then means.
    function windowControl(cfg: {
      title: string; hint: string; presets: [string, string][];
      get: () => string; set: (spec: string) => void;
    }): { rows: HTMLElement[]; sync: () => void } {
      const row = document.createElement('label');
      row.className = 'ovl-row ovl-subrow ovl-country';
      row.innerHTML = `<span class="t">${esc(cfg.title)}
          <span class="s">${esc(cfg.hint)}</span></span>
        <select>${cfg.presets.map(([value, label]) =>
          `<option value="${esc(value)}">${esc(label)}</option>`).join('')}
          <option value="c:">Custom range…</option></select>`;
      const sel = row.querySelector('select')!;

      // Shown only for a custom range. Blank edges are legal and mean open —
      // "since June 1" is a range with no end.
      const range = document.createElement('div');
      range.className = 'ovl-range';
      range.innerHTML = `<label>From <input type="date" data-edge="from" /></label>
        <label>To <input type="date" data-edge="to" /></label>`;
      const fromIn = range.querySelector('[data-edge="from"]') as HTMLInputElement;
      const toIn = range.querySelector('[data-edge="to"]') as HTMLInputElement;

      const sync = (): void => {
        const spec = cfg.get();
        sel.value = spec.startsWith('c:') ? 'c:' : spec;
        // A stored spec matching no preset would leave the <select> blank and
        // silently disagree with the map. Fall back to custom — and decide
        // `custom` from what the <select> ENDED UP on, not from the raw spec,
        // or the dropdown would read "Custom range…" over hidden date fields.
        if (sel.selectedIndex < 0) sel.value = 'c:';
        const custom = sel.value === 'c:';
        range.style.display = custom ? '' : 'none';
        if (custom) {
          const [, from = '', to = ''] = spec.split(':');
          fromIn.value = from;
          toIn.value = to;
        }
      };
      const emit = (spec: string): void => { cfg.set(spec); sync(); };
      sel.addEventListener('change', () => {
        // Entering custom mode with nothing filled in would hide everything.
        // Seed it from whatever the previous window resolved to, so the map
        // does not change until the dates are actually edited.
        if (sel.value !== 'c:') { emit(sel.value); return; }
        const { fromMs, toMs } = resolveWindow(cfg.get());
        emit(`c:${isoDay(fromMs)}:${isoDay(toMs)}`);
      });
      for (const input of [fromIn, toIn]) {
        input.addEventListener('change', () => emit(`c:${fromIn.value}:${toIn.value}`));
      }
      sync();
      return { rows: [row, range], sync };
    }

    // The zoom gate is the usual reason a category reads "on" but shows
    // nothing: objects.ts gates each type by style.minZoom, falling back to
    // PLACE_MINZOOM for place types, and only WATCHED places bypass it. That
    // is invisible from here, so each row carries a chip naming the zoom it
    // appears at whenever the map is below it — and clicking the chip zooms
    // there, since knowing the number is not the same as being able to act.
    // A CATEGORY WHOSE TYPES ALL DECLARE geometry_kinds '{none}' CANNOT DRAW,
    // so it gets no row. This panel is layers AND legend at once, and a toggle
    // that is visibly on while affecting nothing is precisely how "the layer
    // switches are broken" gets reported — the damage window hid 422 of 441
    // markers on the same principle. Two categories qualify today: `news`
    // (migration 044, place-less by design) and `actor`, whose factions have
    // been geometry-less since 001 and have carried a dead toggle ever since.
    const drawable = (catKey: string): boolean =>
      allTypes.some((t) => t.category === catKey
        && t.geometryKinds.some((g) => g !== 'none'));

    const placeCatKeys = new Set(cats.filter((c) => c.domain === 'place').map((c) => c.key));
    const typeMinZoom = (t: { category: string; style: { minZoom?: number } }): number =>
      typeof t.style.minZoom === 'number' ? t.style.minZoom
        : placeCatKeys.has(t.category) ? PLACE_MINZOOM : 0;
    const zoomChip = (minZoom: number, what: string): string => minZoom <= 0 ? ''
      : `<button type="button" class="zoomchip" data-zoom="${minZoom}" hidden
          title="${esc(what)} only draws from zoom ${minZoom} — click to zoom there.
Watched places are the exception and draw at every zoom.">z${minZoom}</button>`;
    function syncZoomChips(): void {
      const z = map.getZoom();
      pane.querySelectorAll<HTMLElement>('.zoomchip').forEach((chip) => {
        chip.hidden = z >= Number(chip.dataset.zoom);
      });
    }
    // CAPTURE phase, not bubble: a chip sits inside a row that is itself
    // clickable (a category <label>, or a type row with its own handler). On
    // the way up those fire first and the chip would toggle the very thing it
    // is explaining, so intercept on the way down.
    pane.addEventListener('click', (ev) => {
      const chip = (ev.target as HTMLElement).closest('.zoomchip') as HTMLElement | null;
      if (!chip) return;
      ev.preventDefault();
      ev.stopPropagation();
      map.easeTo({ zoom: Number(chip.dataset.zoom) + 0.25 });
    }, true);
    map.on('zoomend', () => { if (!pod.hidden) syncZoomChips(); });

    // Domain sections in the reading order the rest of the app uses.
    const DOMAIN_LABELS: Record<string, string> = {
      place: 'Places', object: 'Objects', event: 'Events', actor: 'Actors',
    };
    const sectionHead = (label: string): void => {
      const head = document.createElement('div');
      head.className = 'ovl-domain';
      head.textContent = label;
      pane.appendChild(head);
    };

    const saveTypes = () =>
      localStorage.setItem(TYPE_FILTER_KEY, JSON.stringify(typeState));
    function afterVisibilityChange(): void {
      saveTypes();
      applyOverlays();
      syncBtn();
      refreshPanel();
    }

    // A BULK press is undone by pressing the same control again (2026-10-02,
    // from a phone: "sometimes i hit [Show all] and then i have to go unselect
    // everything which takes 4 seconds"). Show all, Hide all, a group's box and
    // a solo each keep the category/type state they replaced in `layerUndo`;
    // the control that owns it reads as the undo. A run of bulk presses keeps
    // the FIRST snapshot — undo goes back to the layers you had before you
    // started pressing, not to the intermediate step — and a hand-made change
    // in between drops it: undo takes back a press, never what you ticked
    // since. One applyOverlays per press, too, where unticking by hand was a
    // filter change (a re-parse of every entity tile) per row.
    const sameLayers = (s: { cats: Record<string, boolean>; types: Record<string, boolean> }) =>
      cats.every((c2) => (s.cats[c2.key] !== false) === (catState[c2.key] !== false))
      && allTypes.every((t2) => (s.types[t2.key] !== false) === (typeState[t2.key] !== false));
    function bulk(by: string, apply: () => void): void {
      const base = layerUndo ?? { by, cats: { ...catState }, types: { ...typeState } };
      apply();
      for (const c2 of cats) saveOverlayPref(c2.key, catState[c2.key] !== false);
      layerUndo = sameLayers(base) ? null : { ...base, by };
      afterVisibilityChange();
    }
    function undoBulk(): void {
      const s = layerUndo;
      if (!s) return;
      layerUndo = null;
      for (const c2 of cats) {
        const on = s.cats[c2.key] !== false;
        catState[c2.key] = on;
        saveOverlayPref(c2.key, on);
      }
      for (const k of Object.keys(typeState)) delete typeState[k];
      for (const [k, v] of Object.entries(s.types)) if (v === false) typeState[k] = false;
      afterVisibilityChange();
    }

    // Solo is a TOGGLE: clicking "only" on the row that is already the solo
    // puts back what it replaced — or, reached by hand with nothing to put
    // back, turns everything on. One-way solo left no path back short of
    // re-checking every row by hand.
    const catSoloed = (key: string): boolean =>
      catState[key] !== false
      && cats.every((c2) => c2.key === key || catState[c2.key] === false);
    const typeSoloed = (key: string): boolean =>
      typeState[key] !== false
      && allTypes.every((t2) => t2.key === key || typeState[t2.key] === false);
    function showAllLayers(): void {
      bulk('all', () => {
        for (const c2 of cats) catState[c2.key] = true;
        for (const t2 of allTypes) delete typeState[t2.key];
      });
    }
    const unSolo = (): void => { if (layerUndo) undoBulk(); else showAllLayers(); };

    // Show all / Hide all: the one the operator pressed turns into its undo,
    // in place, so the same tap takes it back. Disabled when it would change
    // nothing. Hide all touches only categories with a row — `news`/`actor`
    // draw nothing and have no box to tick back.
    const allBtn = pod.querySelector('[data-l="all"]') as HTMLButtonElement;
    const noneBtn = pod.querySelector('[data-l="none"]') as HTMLButtonElement;
    function syncBulkBtns(): void {
      for (const [btn, by, label, tip] of [
        [allBtn, 'all', 'Show all', 'Show every category and type'],
        [noneBtn, 'none', 'Hide all', 'Hide every category — then tick the ones you want'],
      ] as const) {
        const undo = layerUndo?.by === by;
        btn.textContent = undo ? '↶ Undo' : label;
        btn.title = undo ? `Back to the layers you had before ${label}` : tip;
        btn.classList.toggle('on', undo);
      }
      allBtn.disabled = layerUndo?.by !== 'all'
        && cats.every((c2) => catState[c2.key] !== false) && !anyTypeOff();
      noneBtn.disabled = layerUndo?.by !== 'none'
        && cats.every((c2) => !drawable(c2.key) || catState[c2.key] === false);
    }
    allBtn.addEventListener('click', () => {
      if (layerUndo?.by === 'all') undoBulk(); else showAllLayers();
    });
    noneBtn.addEventListener('click', () => {
      if (layerUndo?.by === 'none') { undoBulk(); return; }
      bulk('none', () => {
        for (const c2 of cats) if (drawable(c2.key)) catState[c2.key] = false;
      });
    });

    // A domain's head is a box for the whole group. It CYCLES rather than
    // flips, so a group you had half on is never lost: mixed → all on → all
    // off → your mix again. From all on or all off it is a plain toggle.
    // Categories only — a type hidden inside one stays hidden, the same as
    // ticking that category's own box.
    function groupHead(domain: string, members: { key: string }[]): void {
      const by = `group:${domain}`;
      const keys = members.map((c2) => c2.key);
      const head = document.createElement('label');
      head.className = 'ovl-domain ovl-group';
      head.title = 'Show or hide the whole group — a third tap goes back to your own mix';
      head.innerHTML = `<input type="checkbox" data-group="${esc(domain)}" />
        <span>${esc(DOMAIN_LABELS[domain] ?? domain)}</span>`;
      const cb = head.querySelector('input')!;
      cb.addEventListener('change', () => {
        const all = (on: boolean, s: Record<string, boolean>): boolean =>
          keys.every((k) => (s[k] !== false) === on);
        const mine = layerUndo?.by === by ? layerUndo.cats : null;
        const mixedBefore = !!mine && !all(true, mine) && !all(false, mine);
        if (all(false, catState) && mixedBefore) { undoBulk(); return; }
        const on = !all(true, catState);
        bulk(by, () => { for (const k of keys) catState[k] = on; });
      });
      pane.appendChild(head);
    }

    // One domain section: a row per category that doubles as its legend entry
    // (toggle + swatch + live count + solo), expanding into per-type rows when
    // the category has more than one type.
    function buildDomain(domain: string): void {
      const members = cats.filter((c) => c.domain === domain && drawable(c.key));
      if (!members.length) return;
      groupHead(domain, members);
      for (const c of members) {
        const typesOfCat = allTypes.filter((t) => t.category === c.key);
        const single = typesOfCat.length === 1 ? typesOfCat[0] : null;
        const swatch = single
          ? `<span class="sw" style="background:${esc(single.style.color ?? '#4f7fd9')}"></span>`
          : '';
        // The zoom this category first shows anything at: its earliest type.
        const catMinZoom = typesOfCat.length
          ? Math.min(...typesOfCat.map(typeMinZoom)) : 0;
        const row = document.createElement('label');
        row.className = 'ovl-row ovl-cat';
        row.innerHTML = `<input type="checkbox" data-catkey="${esc(c.key)}"
            ${catState[c.key] ? 'checked' : ''} />
          ${swatch}<span class="t">${esc(c.label)}</span>
          ${zoomChip(catMinZoom, c.label)}
          <button type="button" class="only" title="Show only this category">only</button>
          <span class="cnt" data-cat-cnt="${esc(c.key)}">–</span>`;
        const cb = row.querySelector('input')!;
        cb.addEventListener('change', () => {
          layerUndo = null;
          catState[c.key] = cb.checked;
          saveOverlayPref(c.key, cb.checked);
          applyOverlays();
          syncBtn();
          refreshPanel();   // type rows dim when their category goes off
        });
        row.querySelector('.only')!.addEventListener('click', (ev) => {
          ev.preventDefault();     // inside the label — don't toggle the checkbox
          ev.stopPropagation();
          if (catSoloed(c.key)) { unSolo(); return; }   // second click undoes
          bulk(`solo:${c.key}`, () => {
            for (const other of cats) catState[other.key] = other.key === c.key;
            for (const t of allTypes) delete typeState[t.key];
          });
        });
        pane.appendChild(row);

        // Per-type rows behind a twisty, so a big taxonomy doesn't swallow the
        // panel. Click a row to show/hide that type; "only" solos it.
        if (typesOfCat.length > 1) {
          const tw = document.createElement('button');
          tw.type = 'button';
          tw.className = 'ovl-tw';
          tw.title = 'Show individual types';
          tw.textContent = '▸';
          row.insertBefore(tw, row.querySelector('.only'));
          const sub = document.createElement('div');
          sub.className = 'ovl-sub ovl-types';
          sub.style.display = 'none';
          for (const t of typesOfCat) {
            const tr = document.createElement('div');
            tr.className = 'legend-row';
            tr.dataset.type = t.key;
            tr.dataset.cat = c.key;
            tr.title = 'Click to show/hide';
            tr.innerHTML = `<span class="sw"
                style="background:${esc(t.style.color ?? '#4f7fd9')}"></span>
              <span class="lbl">${esc(t.label)}</span>
              ${zoomChip(typeMinZoom(t), t.label)}
              <button type="button" class="only" title="Show only this type">only</button>
              <span class="cnt">–</span>`;
            tr.addEventListener('click', () => {
              layerUndo = null;
              const nowOn = typeState[t.key] === false || catState[c.key] === false;
              typeState[t.key] = nowOn;
              if (nowOn && catState[c.key] === false) {
                // Turning a type on must actually show it — lift its category.
                catState[c.key] = true;
                saveOverlayPref(c.key, true);
              }
              afterVisibilityChange();
            });
            tr.querySelector('.only')!.addEventListener('click', (ev) => {
              ev.stopPropagation();
              if (typeSoloed(t.key)) { unSolo(); return; }   // second click undoes
              bulk(`solo:${t.key}`, () => {
                for (const t2 of allTypes) typeState[t2.key] = t2.key === t.key;
                for (const c2 of cats) catState[c2.key] = true;
              });
            });
            sub.appendChild(tr);
          }
          tw.addEventListener('click', (ev) => {
            ev.preventDefault();     // inside the label — don't toggle the category
            ev.stopPropagation();
            const open = sub.style.display === 'none';
            sub.style.display = open ? '' : 'none';
            tw.textContent = open ? '▾' : '▸';
          });
          pane.appendChild(sub);
        }

        // Damage's one control belongs under damage. Markers age by their
        // REPORTED date (mvt_entities stamps `ts` from attrs.date for this
        // type), so this reads as "hit during", not "recorded during".
        if (c.key === 'damage') {
          const ctl = windowControl({
            title: 'Damage window',
            hint: 'Dates markers were hit, not recorded',
            presets: DAMAGE_PRESETS,
            get: () => damageSpec,
            set: (spec) => {
              damageSpec = spec;
              localStorage.setItem(DAMAGE_DAYS_KEY, spec);
              applyOverlays();
              syncBtn();
            },
          });
          for (const el of ctl.rows) pane.appendChild(el);
          damageSync = ctl.sync;
        }

        // Territorial control's legend is its holders, not its one type: a
        // row per territory in the holder's colour, and the two ways a
        // curator moves the map by hand (a district, or a whole country).
        if (c.key === 'control') {
          controlLegend = territoryLegend({
            map, me, all: () => scoped, onChanged: () => { void refresh(); },
          });
          pane.appendChild(controlLegend.el);
        }
      }
    }

    // --- Filter: narrowing that spans categories ---------------------------
    sectionHead('Filter');

    // Story scope, said out loud. Selecting a story is the THIRD reason a
    // category can read "on" and draw nothing — after the per-type zoom gate
    // (which at least has its own z-chip) and the damage window — and it is set
    // from the top strip, nowhere near here. So the row names the story and
    // splits the count: what is filed under it, and how many unfiled objects
    // are riding along. Both rows are hidden under "All stories", where there
    // is nothing to say and nothing to turn off.
    const storyRow = document.createElement('div');
    storyRow.className = 'ovl-row ovl-story';
    storyRow.innerHTML = `<span class="t"><span data-story-name></span>
        <span class="s" data-story-counts></span></span>`;
    const storyNameEl = storyRow.querySelector('[data-story-name]') as HTMLElement;
    const storyCountsEl = storyRow.querySelector('[data-story-counts]') as HTMLElement;
    pane.appendChild(storyRow);

    // Unfiled means filed under no story, which means visible in EVERY story —
    // the open default that keeps a young story from looking broken. This turns
    // it off for an operator who wants the story on its own.
    const storyOnlyRow = document.createElement('label');
    storyOnlyRow.className = 'ovl-row ovl-subrow';
    storyOnlyRow.innerHTML = `<input type="checkbox" />
      <span class="t">Only this story
        <span class="s">Also hide objects filed under no story</span></span>`;
    const storyOnlyCb = storyOnlyRow.querySelector('input')!;
    storyOnlyCb.addEventListener('change', () => {
      // setOnlyThisStory notifies, and this module's onStoryChange subscriber
      // re-applies the map filter and rebuilds the panes. Deliberately NOT
      // wired to syncBtn: "partial" means a filter is hiding something you
      // asked to see, and story scope is scope, not a filter.
      setOnlyThisStory(storyOnlyCb.checked);
    });
    pane.appendChild(storyOnlyRow);

    // Counts are of the LOADED rows — GET /api/entities is capped and reports
    // no total — and say nothing about the zoom gate, hence "shown" rather
    // than "on the map".
    function syncStoryRows(): void {
      const story = getActiveStory();
      // [hidden] loses to .ovl-row's display:flex; toggle display instead.
      storyRow.style.display = story ? '' : 'none';
      storyOnlyRow.style.display = story ? '' : 'none';
      if (!story) return;
      let filed = 0;
      let unfiled = 0;
      for (const e of all) {
        if (!e.storyId) unfiled += 1;
        else if (inScope(e.storyId)) filed += 1;
      }
      const only = isOnlyThisStory();
      const readout = `${filed} filed · ${unfiled} unfiled ${only ? 'hidden' : 'shown'}`;
      storyNameEl.textContent = story.name;
      storyCountsEl.textContent = readout;
      storyRow.title = `${story.name} — ${readout}. Unfiled objects belong to no `
        + 'story and stay visible under every one; each type’s zoom gate still '
        + 'applies on top of this.';
      storyOnlyCb.checked = only;
    }

    const countryRow = document.createElement('div');
    countryRow.className = 'ovl-row ovl-country';
    countryRow.innerHTML = `<span class="t">Country
        <span class="s">Only show places &amp; events from one country</span></span>
      <select title="Country filter"></select>`;
    const countrySel = countryRow.querySelector('select')!;
    const fillCountries = () => {
      const options = ['', ...knownCountries()];
      countrySel.innerHTML = options.map((c) =>
        `<option value="${esc(c)}"${c === countryFilter ? ' selected' : ''}>${
          c ? esc(c) : 'All'}</option>`).join('');
      if (countryFilter && !options.includes(countryFilter)) {
        countrySel.insertAdjacentHTML('beforeend',
          `<option value="${esc(countryFilter)}" selected>${esc(countryFilter)}</option>`);
      }
    };
    fillCountries();
    countrySel.addEventListener('change', () => {
      countryFilter = countrySel.value;
      localStorage.setItem(COUNTRY_KEY, countryFilter);
      applyOverlays();
      syncBtn();
    });
    pane.appendChild(countryRow);

    const watchedRow = document.createElement('label');
    watchedRow.className = 'ovl-row';
    watchedRow.innerHTML = `<input type="checkbox" ${watchedOnly ? 'checked' : ''} />
      <span class="t">Watched places only
        <span class="s">Hide places not watched for imagery</span></span>`;
    const watchedCb = watchedRow.querySelector('input')!;
    watchedCb.addEventListener('change', () => {
      watchedOnly = watchedCb.checked;
      saveOverlayPref('_watchedOnly', watchedOnly);
      applyOverlays();
      syncBtn();
    });
    pane.appendChild(watchedRow);

    // --- entity domains ----------------------------------------------------
    buildDomain('place');
    buildDomain('object');

    // Visuals drawn FOR objects (rings and volumes around deployed systems)
    // live at the end of the Objects section.
    const coverageRow = document.createElement('label');
    coverageRow.className = 'ovl-row ovl-coverage';
    coverageRow.innerHTML = `<input type="checkbox" data-coverage-toggle
        ${coverageOn ? 'checked' : ''} />
      <span class="t">Reference range rings
        <span class="s">Nominal range rings for deployed systems</span></span>`;
    const coverageCb = coverageRow.querySelector('input')!;
    coverageCb.addEventListener('change', () => {
      setCoverage(coverageCb.checked);
      syncBtn();
    });
    pane.appendChild(coverageRow);

    const domesRow = document.createElement('label');
    domesRow.className = 'ovl-row ovl-domes';
    domesRow.innerHTML = `<input type="checkbox" data-domes-toggle
        ${domesOn ? 'checked' : ''} />
      <span class="t">Physical sensor volumes
        <span class="s">Horizon/slant-aware radar + nominal engagement — Mercator tilt view</span></span>`;
    domesRow.querySelector('input')!.addEventListener('change', (ev) => {
      setDomes((ev.target as HTMLInputElement).checked);
      syncSub();
      syncBtn();
    });
    pane.appendChild(domesRow);

    // Sub-controls: which volumes, their look, and the height exaggeration.
    const vs = getVolumeStyle();
    const styleEditor = (kind: VolumeKind): string => {
      const s = vs[kind];
      return `<div class="dome-style" data-kind="${kind}">
        <label class="dome-style-row"><span>Colour</span>
          <input type="color" data-vs="color" value="${s.color}" /></label>
        <label class="dome-style-row"><span>Line</span>
          <input type="range" data-vs="lineOpacity" min="0" max="1" step="0.02" value="${s.lineOpacity}" /></label>
        <label class="dome-style-row"><span>Width</span>
          <input type="range" data-vs="lineWidth" min="0.5" max="4" step="0.5" value="${s.lineWidth}" /></label>
        <label class="dome-style-row"><span>Fill</span>
          <input type="checkbox" data-vs="fill" ${s.fill ? 'checked' : ''} />
          <input type="range" data-vs="fillOpacity" min="0" max="0.6" step="0.01" value="${s.fillOpacity}" /></label>
      </div>`;
    };
    const domesSub = document.createElement('div');
    domesSub.className = 'ovl-sub ovl-domes-sub';
    domesSub.innerHTML = `
      <label class="ovl-mini"><input type="checkbox" data-dome-radar
        ${domeRadar ? 'checked' : ''} />
        <span class="dome-swatch" data-swatch="radar" style="background:${vs.radar.color}"></span> Detection (radar)</label>
      ${styleEditor('radar')}
      <label class="ovl-mini"><input type="checkbox" data-dome-engage
        ${domeEngage ? 'checked' : ''} />
        <span class="dome-swatch" data-swatch="engage" style="background:${vs.engage.color}"></span> Engagement (range)</label>
      ${styleEditor('engage')}
      <label class="ovl-slider">Height
        <input type="range" data-dome-exagg min="1" max="15" step="0.5" value="${domeExagg}" />
        <span class="val" data-dome-exaggval>${exaggLabel(domeExagg)}</span></label>`;
    // Live style edits: patch the shared style, persist the per-browser override,
    // update the toggle swatch, and repaint the volumes.
    domesSub.querySelectorAll<HTMLInputElement>('[data-vs]').forEach((el) => {
      const evt = el.type === 'checkbox' || el.type === 'color' ? 'change' : 'input';
      el.addEventListener(evt, () => {
        const kind = (el.closest('[data-kind]') as HTMLElement).dataset.kind as VolumeKind;
        const prop = el.dataset.vs as keyof VolumeStyle;
        const value: string | number | boolean = el.type === 'checkbox' ? el.checked
          : el.type === 'color' ? el.value : Number(el.value);
        setVolumeStyle({ [kind]: { [prop]: value } });
        localStorage.setItem('cockpit-volume-styles', JSON.stringify(getVolumeStyle()));
        if (prop === 'color') {
          const sw = domesSub.querySelector(`[data-swatch="${kind}"]`) as HTMLElement | null;
          if (sw) sw.style.background = String(value);
        }
        applyDomes();
      });
    });
    const syncSub = () => { domesSub.style.display = domesOn ? '' : 'none'; };
    syncSub();
    domesSub.querySelector('[data-dome-radar]')!.addEventListener('change', (ev) => {
      domeRadar = (ev.target as HTMLInputElement).checked;
      saveOverlayPref('_domeRadar', domeRadar);
      applyDomes();
    });
    domesSub.querySelector('[data-dome-engage]')!.addEventListener('change', (ev) => {
      domeEngage = (ev.target as HTMLInputElement).checked;
      saveOverlayPref('_domeEngage', domeEngage);
      applyDomes();
    });
    const exaggInput = domesSub.querySelector('[data-dome-exagg]') as HTMLInputElement;
    const exaggVal = domesSub.querySelector('[data-dome-exaggval]') as HTMLElement;
    exaggInput.addEventListener('input', () => {
      domeExagg = Number(exaggInput.value);
      exaggVal.textContent = exaggLabel(domeExagg);
      localStorage.setItem('cockpit-dome-exagg', String(domeExagg));
      applyDomes();
    });
    pane.appendChild(domesSub);

    buildDomain('event');
    buildDomain('actor');

    // --- overlays that aren't entities -------------------------------------
    sectionHead('Overlays');

    const firmsRow = document.createElement('div');
    firmsRow.className = 'ovl-row ovl-firms';
    firmsRow.innerHTML = `<input type="checkbox" />
      <span class="t">FIRMS hotspots
        <span class="s">NASA VIIRS · MODIS · Meteosat detections — orange
        triangles (events are round)</span></span>
      <span class="ovl-count" hidden title="Hotspots visible in the current view">0</span>`;
    const firmsCb = firmsRow.querySelector('input')!;
    const firmsCountEl = firmsRow.querySelector('.ovl-count') as HTMLElement;

    // The store holds a rolling 60 days and the tile carries exactly the window
    // asked for, so a window is a LENGTH plus an END: `until` null keeps it
    // live and sliding, a date pins it to the past (migration 027).
    const FIRMS_MAX_HOURS = 1440;   // 60 days — the store's depth and the API cap
    function firmsWindowArgs(): { hours: number; until: number | null } {
      const { fromMs, toMs } = resolveWindow(firmsSpec);
      const end = toMs ?? Date.now();
      // An open start means "as far back as there is", not some default span —
      // "up to June 5" has to reach the store's floor, not the day before.
      const start = fromMs ?? end - FIRMS_MAX_HOURS * 3_600_000;
      const hours = Math.ceil((end - start) / 3_600_000);
      return {
        hours: Math.min(FIRMS_MAX_HOURS, Math.max(1, hours)),
        until: toMs === null ? null : end,
      };
    }
    firmsCb.addEventListener('change', () => {
      firmsOn = firmsCb.checked;
      firmsCountEl.hidden = !firmsOn;
      const { hours, until } = firmsWindowArgs();
      firms.setEnabled(map, firmsOn, hours, until);
      syncBtn();
    });
    // Hotspots come from tiles, so this counts what is rendered in the
    // viewport — tiles off-screen were never fetched (see firms.ts).
    firms.onCountChange((n) => {
      firmsCountEl.textContent = String(n);
      firmsCountEl.hidden = !firmsOn;
    });
    pane.appendChild(firmsRow);

    const firmsWindowCtl = windowControl({
      title: 'Detection window',
      hint: 'Up to 60 days — the store’s depth',
      presets: FIRMS_PRESETS,
      get: () => firmsSpec,
      set: (spec) => {
        firmsSpec = spec;
        localStorage.setItem(FIRMS_WINDOW_KEY, spec);
        const { hours, until } = firmsWindowArgs();
        if (firmsOn) firms.setWindow(hours, until);
      },
    });
    for (const el of firmsWindowCtl.rows) pane.appendChild(el);

    const firmsUnusualRow = document.createElement('label');
    firmsUnusualRow.className = 'ovl-row ovl-firms-sub';
    firmsUnusualRow.innerHTML = `<input type="checkbox" />
      <span class="t">Only unusual
        <span class="s">Hide persistent flares, foundries &amp; refineries</span></span>`;
    const firmsUnusualCb = firmsUnusualRow.querySelector('input')!;
    firmsUnusualCb.addEventListener('change', () => {
      firms.setUnusualOnly(firmsUnusualCb.checked);
    });
    pane.appendChild(firmsUnusualRow);

    const bldRow = document.createElement('label');
    bldRow.className = 'ovl-row';
    bldRow.innerHTML = `<input type="checkbox" ${buildingsOn ? 'checked' : ''} />
      <span class="t">3D buildings <span class="z-chip">z13</span>
        <span class="s">Overture massing on any basemap — heights defaulted where
        unreported; context, not measurements</span></span>`;
    const bldCb = bldRow.querySelector('input')!;
    bldCb.addEventListener('change', () => {
      buildingsOn = bldCb.checked;
      saveOverlayPref('_buildings3d', buildingsOn);
      setBuildings(map, buildingsOn);
      syncBtn();
    });
    pane.appendChild(bldRow);

    const pipeRow = document.createElement('div');
    pipeRow.className = 'ovl-row';
    pipeRow.innerHTML = `<input type="checkbox" ${pipelinesOn ? 'checked' : ''} />
      <span class="t">Pipelines
        <span class="s">Oil (brown), gas (amber), refined fuel (red), unknown (grey)
        — OpenStreetMap. Refineries, oil &amp; gas sites and power plants are the
        Energy places.</span></span>
      <span class="ovl-count" title="OpenStreetMap pipelines loaded">…</span>`;
    const pipeCb = pipeRow.querySelector('input')!;
    const pipeCountEl = pipeRow.querySelector('.ovl-count') as HTMLElement;
    pipeCb.addEventListener('change', () => {
      pipelinesOn = pipeCb.checked;
      saveOverlayPref('_pipelines', pipelinesOn);
      setPipelines(map, pipelinesOn);
      syncBtn();
    });
    pane.appendChild(pipeRow);
    // The server fills the layer one area of the theatre at a time after it
    // boots; the row says how far it has got, and the tiles are re-asked as
    // areas land. Polled only while the layer is still filling.
    let pipeDone = -1;
    const pipeStatus = async (): Promise<void> => {
      try {
        const st = await energyStatus();
        const label = statusLabel(st);
        pipeCountEl.textContent = label.text;
        pipeCountEl.title = label.title;
        if (st.tiles.done !== pipeDone) {
          if (pipeDone >= 0) bumpPipelines(map, st.tiles.done);
          pipeDone = st.tiles.done;
        }
        if (st.tiles.done < st.tiles.total) window.setTimeout(() => void pipeStatus(), 120_000);
      } catch {
        pipeCountEl.textContent = '';
      }
    };
    void pipeStatus();

    const acRow = document.createElement('div');
    acRow.className = 'ovl-row';
    acRow.innerHTML = `<input type="checkbox" />
      <span class="t">Aircraft (ADS-B)
        <span class="s">Per-story coverage — orange military, blue civil;
        the tail is the last 30 min</span></span>
      <span class="ovl-count" hidden title="Contacts in this story's watched zones">0</span>`;
    const acCb = acRow.querySelector('input')!;
    const acCountEl = acRow.querySelector('.ovl-count') as HTMLElement;
    acCb.addEventListener('change', () => {
      acOn = acCb.checked;
      acCountEl.hidden = !acOn;
      aircraft.setEnabled(map, acOn);
      // The saved 3D preference applies the moment the layer exists — without
      // this a reloaded tab shows flat planes under a checked 3D box.
      if (acOn) aircraft.set3d(map, ac3d);
      syncBtn();
    });
    // Unlike the FIRMS count this is every contact RECEIVED, not what is in
    // the viewport: the server already clipped to the watched zones, so the
    // payload is the answer rather than a page of it.
    aircraft.onCountChange((n) => {
      // The COUNT is not the whole answer. Zero contacts means one of three
      // things and they need different actions, so the row says which rather
      // than leaving a bare 0 to read as a broken layer.
      const st = aircraft.getStatus();
      acCountEl.textContent = n > 0 ? String(n)
        : st === 'unconfigured' ? 'not set up'
          : st === 'no-ground' ? 'no area' : '0';
      acCountEl.title = n > 0
        ? 'Contacts in this story’s watched zones'
        : st === 'unconfigured'
          ? 'No aircraft coverage is configured. Admin → Aircraft → Add coverage.'
          : st === 'no-ground'
            ? 'Coverage is configured, but its story has no area and no drawn '
              + 'zone — so it watches nothing. Admin → Aircraft → Draw zone.'
            : 'Coverage is working; nothing is in range right now. Normal over '
              + 'the Gulf, where receiver coverage is thin.';
      acCountEl.hidden = !acOn;
    });
    pane.appendChild(acRow);

    // 3D is a RENDERING choice for the same data, so it nests under the
    // aircraft row and needs no own polling or count. The preference persists
    // even though the layer itself deliberately does not.
    let ac3d = overlayPrefs()['_aircraft3d'] ?? false;
    const ac3dRow = document.createElement('label');
    ac3dRow.className = 'ovl-row ovl-firms-sub';
    ac3dRow.innerHTML = `<input type="checkbox" ${ac3d ? 'checked' : ''} />
      <span class="t">3D flight view
        <span class="s">Solid aircraft at reported altitude, turned to their
        heading — tilt with right-drag</span></span>`;
    const ac3dCb = ac3dRow.querySelector('input')!;
    ac3dCb.addEventListener('change', () => {
      ac3d = ac3dCb.checked;
      saveOverlayPref('_aircraft3d', ac3d);
      aircraft.set3d(map, ac3d);
    });
    pane.appendChild(ac3dRow);

    // The Bookmarks pane's "On map" switch, mirrored here because this is
    // where every other layer is turned off; single pins are hidden from the
    // pane's own rows. Kept out of sight for a guest, who has none.
    const bmRow = document.createElement('label');
    bmRow.className = 'ovl-row';
    bmRow.dataset.bookmarksRow = '';
    bmRow.innerHTML = `<input type="checkbox" data-bookmarks-toggle ${bmShown ? 'checked' : ''} />
      <span class="t">My bookmarks
        <span class="s">Your private pins — hide single ones from the Bookmarks pane</span></span>
      <span class="ovl-count" data-bookmarks-count hidden
        title="Bookmarks drawn on the map">0</span>`;
    const bmCb = bmRow.querySelector('input')!;
    bmCb.addEventListener('change', () => setBookmarksShown(bmCb.checked));
    pane.appendChild(bmRow);
    syncLayersBtn = syncBtn;
    syncBookmarkUi();

    const mkRow = document.createElement('label');
    mkRow.className = 'ovl-row';
    mkRow.innerHTML = `<input type="checkbox" ${markupOn ? 'checked' : ''} />
      <span class="t">My markup
        <span class="s">Arrows, notes and shapes on your current sheet —
        nobody else sees them; draw with the Markup tool</span></span>
      <span class="ovl-count" data-markup-count hidden
        title="Shapes you have drawn">0</span>`;
    const mkCb = mkRow.querySelector('input')!;
    const mkCountEl = mkRow.querySelector('[data-markup-count]') as HTMLElement;
    const syncMarkupCount = (n: number): void => {
      mkCountEl.textContent = String(n);
      mkCountEl.hidden = !markupOn || !n;
    };
    mkCb.addEventListener('change', () => {
      markupOn = mkCb.checked;
      saveOverlayPref('_markup', markupOn);
      setMarkupVisible(map, markupOn);
      syncMarkupCount(markupCount(map));
      syncBtn();
    });
    // Arming a markup tool shows the layer whether or not this box was
    // ticked — drawing into a hidden layer shows nothing — so the row follows
    // rather than contradicting the map.
    onMarkupVisibleChange(map, (on) => {
      markupOn = on;
      mkCb.checked = on;
      saveOverlayPref('_markup', on);
      syncMarkupCount(markupCount(map));
      syncBtn();
    });
    onMarkupCountChange(map, syncMarkupCount);
    syncMarkupCount(markupCount(map));
    pane.appendChild(mkRow);

    const admRow = document.createElement('label');
    admRow.className = 'ovl-row';
    admRow.innerHTML = `<input type="checkbox" ${admOn ? 'checked' : ''} />
      <span class="t">Admin boundaries
        <span class="s">Countries · provinces · districts</span></span>`;
    const admCb = admRow.querySelector('input')!;
    admCb.addEventListener('change', () => {
      admOn = admCb.checked;
      saveOverlayPref('_adm', admOn);
      setAdmOverlay(map, admOn);
      syncBtn();
    });
    pane.appendChild(admRow);

    const flashRow = document.createElement('label');
    flashRow.className = 'ovl-row';
    flashRow.innerHTML = `<input type="checkbox" ${flashOn ? 'checked' : ''} />
      <span class="t">Flash alert areas
        <span class="s">Pulse the province of fresh strikes &amp; alerts</span></span>`;
    const flashCb = flashRow.querySelector('input')!;
    flashCb.addEventListener('change', () => {
      flashOn = flashCb.checked;
      saveOverlayPref('_flash', flashOn);
    });
    pane.appendChild(flashRow);
    // --- layer groups ------------------------------------------------------
    // The operator's own named sets of layers (layergroups.ts): a row each at
    // the top of this pod, and a chip each under the Layers button so the
    // commonest switch needs no pod at all. A member is a category key or an
    // overlay, and an overlay is switched through ITS OWN checkbox — the
    // change event runs whatever that row does (FIRMS fetches its window,
    // bookmarks sync their pane), so a group can never drift from the row.
    const overlayBoxes: Record<string, { cb: HTMLInputElement; label: string;
                                         unavailable?: () => boolean }> = {
      firms: { cb: firmsCb, label: 'FIRMS hotspots' },
      pipelines: { cb: pipeCb, label: 'Pipelines' },
      aircraft: { cb: acCb, label: 'Aircraft' },
      adm: { cb: admCb, label: 'Admin boundaries' },
      buildings: { cb: bldCb, label: '3D buildings' },
      bookmarks: { cb: bmCb, label: 'My bookmarks', unavailable: () => bookmarksLocked },
      markup: { cb: mkCb, label: 'My markup' },
    };
    const overlayOf = (m: string) => (m.startsWith(OVERLAY_PREFIX)
      ? overlayBoxes[m.slice(OVERLAY_PREFIX.length)] : undefined);
    const memberKnown = (m: string): boolean => {
      if (m.startsWith(OVERLAY_PREFIX)) {
        const o = overlayOf(m);
        return !!o && !o.unavailable?.();
      }
      return cats.some((c) => c.key === m) && drawable(m);
    };
    const memberOn = (m: string): boolean =>
      m.startsWith(OVERLAY_PREFIX) ? !!overlayOf(m)?.cb.checked : catState[m] !== false;
    const memberLabel = (m: string): string =>
      overlayOf(m)?.label ?? cats.find((c) => c.key === m)?.label ?? m;
    let groups = loadGroups();
    const stateOf = (g: LayerGroup) => groupState(liveMembers(g, memberKnown), memberOn);

    function toggleGroup(id: string): void {
      const g = groups.find((x) => x.id === id);
      if (!g) return;
      const ms = liveMembers(g, memberKnown);
      if (!ms.length) return;
      const on = tapTurnsOn(groupState(ms, memberOn));
      layerUndo = null;   // a group is switches thrown by hand, several at once
      for (const m of ms) {
        const o = overlayOf(m);
        if (o) {
          if (o.cb.checked !== on) {
            o.cb.checked = on;
            o.cb.dispatchEvent(new Event('change'));
          }
        } else {
          catState[m] = on;
          saveOverlayPref(m, on);
        }
      }
      afterVisibilityChange();
    }

    const chipsEl = wrap.querySelector('.lg-chips') as HTMLElement;
    chipsEl.addEventListener('click', (ev) => {
      const chip = (ev.target as HTMLElement).closest<HTMLElement>('[data-lg]');
      if (chip) toggleGroup(chip.dataset.lg!);
    });
    const groupsBox = document.createElement('div');
    groupsBox.className = 'lg-box';
    let editing: string | null = null;   // a group id, 'new', or null

    function renderGroups(): void {
      const known = groups.filter((g) => liveMembers(g, memberKnown).length);
      chipsEl.innerHTML = known.map((g) =>
        `<button type="button" class="lg-chip" data-lg="${esc(g.id)}">${esc(g.name)}</button>`)
        .join('');
      const head = '<div class="ovl-domain lg-head">My groups</div>';
      const rows = known.map((g) => {
        const names = liveMembers(g, memberKnown).map(memberLabel).join(' · ');
        return `<label class="ovl-row lg-row">
            <input type="checkbox" data-lg="${esc(g.id)}" />
            <span class="t">${esc(g.name)}<span class="s">${esc(names)}</span></span>
            <button type="button" class="lg-edit" data-lg-edit="${esc(g.id)}"
              title="Change or delete this group">✎</button>
          </label>`;
      }).join('');
      const add = editing === 'new' ? '' : `<button type="button" class="ovl-row lg-new">
          <span class="t">＋ New group<span class="s">${known.length
            ? 'Another set of layers to switch together'
            : 'Name a set of layers — then one tap turns them all on or off, here or under the Layers button'}</span></span>
        </button>`;
      groupsBox.innerHTML = head + rows + add;
      if (editing) groupsBox.appendChild(groupForm(editing));
      syncGroupsUi();
    }

    syncGroupsUi = (): void => {
      chipsEl.querySelectorAll<HTMLElement>('[data-lg]').forEach((el) => {
        const g = groups.find((x) => x.id === el.dataset.lg);
        if (!g) return;
        const st = stateOf(g);
        el.classList.toggle('on', st === 'on');
        el.classList.toggle('partial', st === 'partial');
        el.title = `${g.name} — ${st === 'on' ? 'on; tap to hide' : 'tap to show'}: `
          + liveMembers(g, memberKnown).map(memberLabel).join(', ');
      });
      groupsBox.querySelectorAll<HTMLInputElement>('input[data-lg]').forEach((el) => {
        const g = groups.find((x) => x.id === el.dataset.lg);
        const st = g ? stateOf(g) : 'off';
        el.checked = st === 'on';
        el.indeterminate = st === 'partial';
      });
      chipsEl.hidden = !pod.hidden || !chipsEl.childElementCount;
    };
    // The chips stand in for the pod while it is shut; open, its rows do.
    new MutationObserver(() => syncGroupsUi())
      .observe(pod, { attributes: true, attributeFilter: ['hidden'] });

    groupsBox.addEventListener('change', (ev) => {
      const cb = (ev.target as HTMLElement).closest<HTMLInputElement>('input[data-lg]');
      if (cb) toggleGroup(cb.dataset.lg!);
    });
    groupsBox.addEventListener('click', (ev) => {
      const t = ev.target as HTMLElement;
      const edit = t.closest<HTMLElement>('[data-lg-edit]');
      if (edit) {
        ev.preventDefault();   // inside the row's label — don't toggle the group
        ev.stopPropagation();
        editing = edit.dataset.lgEdit!;
        renderGroups();
        return;
      }
      if (t.closest('.lg-new')) {
        editing = 'new';
        renderGroups();
      }
    });

    // The editor: a name and a box per layer, starting from what is on the
    // map now (unless that is everything, which is no group) so "set the map
    // up, then save it" and "tick what you want" both work.
    function groupForm(id: string): HTMLElement {
      const g = groups.find((x) => x.id === id);
      const form = document.createElement('div');
      form.className = 'lg-form';
      const catKeys = cats.filter((c) => drawable(c.key)).map((c) => c.key);
      const overlayKeys = Object.keys(overlayBoxes).map((k) => OVERLAY_PREFIX + k)
        .filter(memberKnown);
      const everything = catKeys.every((k) => catState[k] !== false);
      const picked = new Set(g ? g.members
        : everything ? [] : [...catKeys, ...overlayKeys].filter(memberOn));
      const box = (m: string) => `<label><input type="checkbox" value="${esc(m)}"
          ${picked.has(m) ? 'checked' : ''} /> ${esc(memberLabel(m))}</label>`;
      const domains = ['place', 'object', 'event', 'actor'].map((d) => {
        const ks = cats.filter((c) => c.domain === d && drawable(c.key)).map((c) => c.key);
        return ks.length ? `<div class="lg-pick-head">${esc(DOMAIN_LABELS[d] ?? d)}</div>
          <div class="lg-pick">${ks.map(box).join('')}</div>` : '';
      }).join('');
      form.innerHTML = `
        <input type="text" class="lg-name" maxlength="${GROUP_NAME_MAX}"
          placeholder="Name, e.g. Energy" value="${esc(g?.name ?? '')}" />
        ${domains}
        <div class="lg-pick-head">Overlays</div>
        <div class="lg-pick">${overlayKeys.map(box).join('')}</div>
        <div class="lg-msg" hidden></div>
        <div class="lg-btns">
          <button type="button" class="mini" data-f="save">${g ? 'Save' : 'Make group'}</button>
          <button type="button" class="mini" data-f="cancel">Cancel</button>
          ${g ? '<button type="button" class="mini lg-del" data-f="del">Delete</button>' : ''}
        </div>`;
      const nameIn = form.querySelector('.lg-name') as HTMLInputElement;
      const msg = form.querySelector('.lg-msg') as HTMLElement;
      const say = (text: string): void => { msg.textContent = text; msg.hidden = false; };
      const close = (): void => { editing = null; renderGroups(); };
      form.querySelector('[data-f="cancel"]')!.addEventListener('click', close);
      form.querySelector('[data-f="del"]')?.addEventListener('click', () => {
        groups = groups.filter((x) => x.id !== id);
        saveGroups(groups);
        close();
      });
      form.querySelector('[data-f="save"]')!.addEventListener('click', () => {
        const name = cleanName(nameIn.value);
        const members = [...form.querySelectorAll<HTMLInputElement>('.lg-pick input:checked')]
          .map((el) => el.value);
        if (!name) { say('Give the group a name.'); nameIn.focus(); return; }
        if (!members.length) { say('Tick at least one layer.'); return; }
        if (groups.some((x) => x.id !== id && x.name.toLowerCase() === name.toLowerCase())) {
          say(`There is already a group called “${name}”.`);
          return;
        }
        if (!g && groups.length >= GROUPS_MAX) {
          say(`That is the most groups this panel keeps (${GROUPS_MAX}).`);
          return;
        }
        // Members this map has no row for (a guest's bookmarks, a category
        // retired since) are kept: hidden now, back if the layer is.
        const kept = g ? g.members.filter((m) => !memberKnown(m)) : [];
        const next = { id: g?.id ?? newGroupId(), name, members: [...members, ...kept] };
        groups = g ? groups.map((x) => (x.id === id ? next : x)) : [...groups, next];
        saveGroups(groups);
        close();
      });
      nameIn.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') (form.querySelector('[data-f="save"]') as HTMLElement).click();
        if (ev.key === 'Escape') { ev.stopPropagation(); close(); }
      });
      window.setTimeout(() => nameIn.focus(), 0);
      return form;
    }
    // Another tab's edit lands here too; the groups are this browser's.
    window.addEventListener('storage', (ev) => {
      if (ev.key !== LAYER_GROUPS_KEY) return;
      groups = loadGroups();
      if (editing && editing !== 'new' && !groups.some((g) => g.id === editing)) editing = null;
      renderGroups();
    });
    pane.prepend(groupsBox);
    renderGroups();

    syncBtn();
    syncBulkBtns();    // the header buttons are built empty
    syncStoryRows();   // hide the story rows before the pod is ever opened

    // --- open / close ------------------------------------------------------
    panelBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      pod.hidden = !pod.hidden;
      if (!pod.hidden) refreshPanel();
    });
    pod.querySelector('.x')!.addEventListener('click', () => { pod.hidden = true; });
    // Click-away closes it, but not clicks on the rail's basemap select (a
    // sibling): only clicks fully outside the rail dismiss. Asked of the
    // event's PATH, not its target: a button inside the pod that re-renders
    // its own section (the layer groups' New / Save / Cancel) has left the
    // document by the time the click gets here, and `contains` would read
    // that as a click outside.
    document.addEventListener('click', (ev) => {
      if (!pod.hidden && !ev.composedPath().includes(wrap)) pod.hidden = true;
    });

    // Write shared state back into the panel: checkbox positions, off
    // styling, per-type counts from the loaded data, and the country list.
    // Rows are built once and only updated, so open twisties and the scroll
    // position survive every change.
    function refreshPanel(): void {
      // Counted over the story-scoped list, so a category's number is what the
      // map is drawing from, not a total that includes another story's rows.
      const counts = new Map<string, number>();
      for (const e of scoped) counts.set(e.type, (counts.get(e.type) ?? 0) + 1);
      const catCounts = new Map<string, number>();
      for (const t of allTypes) {
        catCounts.set(t.category,
          (catCounts.get(t.category) ?? 0) + (counts.get(t.key) ?? 0));
      }
      pane.querySelectorAll<HTMLInputElement>('[data-catkey]').forEach((el) => {
        const key = el.dataset.catkey!;
        el.checked = catState[key] !== false;
        const row = el.closest('.ovl-cat')!;
        row.classList.toggle('off', catState[key] === false);
        // The active solo stays visible (no hover needed) and reads as the undo.
        const only = row.querySelector('.only');
        if (only) {
          const on = catSoloed(key);
          only.classList.toggle('on', on);
          only.setAttribute('title', on ? 'Showing only this category — click to show all again'
            : 'Show only this category');
        }
      });
      // A group's box: ticked when every category in it is on, a dash when
      // some are — the mix its third tap goes back to.
      pane.querySelectorAll<HTMLInputElement>('[data-group]').forEach((el) => {
        const keys = cats.filter((c) => c.domain === el.dataset.group && drawable(c.key))
          .map((c) => c.key);
        const on = keys.filter((k) => catState[k] !== false).length;
        el.checked = on === keys.length;
        el.indeterminate = on > 0 && on < keys.length;
      });
      syncBulkBtns();
      // Territorial control's holders are a legend for a layer that is off:
      // fold them away rather than spend half a phone screen on them. The
      // curator actions and forms under them stay.
      controlLegend?.el.classList.toggle('cat-off', catState.control === false);
      pane.querySelectorAll<HTMLElement>('[data-cat-cnt]').forEach((el) => {
        el.textContent = String(catCounts.get(el.dataset.catCnt!) ?? 0);
      });
      pane.querySelectorAll<HTMLElement>('.legend-row').forEach((tr) => {
        tr.classList.toggle('off',
          typeState[tr.dataset.type!] === false || catState[tr.dataset.cat!] === false);
        tr.querySelector('.cnt')!.textContent = String(counts.get(tr.dataset.type!) ?? 0);
        const only = tr.querySelector('.only');
        if (only) {
          const on = typeSoloed(tr.dataset.type!);
          only.classList.toggle('on', on);
          only.setAttribute('title', on ? 'Showing only this type — click to show all again'
            : 'Show only this type');
        }
      });
      watchedCb.checked = watchedOnly;
      controlLegend?.refresh();
      syncStoryRows();
      fillCountries();
      syncZoomChips();
    }
    // Keep counts live while the panel is open (rebuildViews calls this).
    refreshLegend = () => { if (!pod.hidden) refreshPanel(); };
    onLayerStateApplied = () => {
      admCb.checked = admOn;
      damageSync?.();
      refreshPanel();
      syncBtn();
    };
  }

  // --- shared data flow ----------------------------------------------------

  // The panes split by the category's registry domain: Places lists the
  // shared map of locations, Objects the units and actors. Event-domain
  // entities have their own views (live feed, damage panel).
  function rebuildViews(): void {
    // Story scope is applied HERE, not in refresh(): mergeUpdates folds the
    // 15 s poll straight into `all` with no scope test, so a fetch-time filter
    // would leak other stories' objects back in on the very next poll. `all`
    // stays the whole visible world — tenancy was already enforced server-side
    // — and this is the display filter over it, the same one the tiles apply.
    scoped = all.filter((e) => inScope(e.storyId));
    // Holder colours for the control layers come from the loaded factions
    // and territories (territory.ts) — re-derived with every data change,
    // since a faction's attrs.color or a new holder can arrive on any poll.
    applyFactionColors(map, all);
    places = scoped.filter((e) => domainOf(e) === 'place');
    // Conflict reporting with no place. Kept in its OWN list, never folded into
    // liveEvents: everything downstream of that array assumes a coordinate —
    // setLiveEvents bakes map dots from it, situations.ts groups it by place,
    // corroborate.ts asks where it was. A news item has no position to give any
    // of them.
    newsItems = scoped
      .filter(isNewsItem)
      .sort((a, b) => lastReportOf(b) - lastReportOf(a));
    liveEvents = scoped
      .filter((e) => e.type === 'live_event')
      // Sorted by LAST REPORT — a feed is a newsroom, so newest *known* first: a
      // just-reported six-hour-old strike belongs above a ten-minute-old siren.
      // The cards re-sorting under the reader was never caused by this key; it
      // was caused by this key and the DISPLAYED time being different clocks.
      .sort((a, b) => lastReportOf(b) - lastReportOf(a));
    objects = scoped.filter((e) => {
      const d = domainOf(e);
      return d === 'object' || d === 'actor';
    });
    damage = scoped.filter((e) => e.category === 'damage')
      .sort((a, b) => damageWhen(b) - damageWhen(a));
    applyCoverage();
    setLiveEvents(map, liveEvents);
    renderPlaces();
    renderObjects();
    renderDamage();
    renderLive();
    pushCounts();
    pushLive();
    refreshLegend?.();
  }

  // The topbar picker only records the choice (story.ts); this is where a
  // switch takes effect, and it is exactly these two calls. The map's story
  // clause is a setFilter over tiles already in hand — no setStyle, no new tile
  // URL, no cursor reset — and the panes re-derive from the list in memory, so
  // switching costs no request. Fires for "Only this story" too.
  onStoryChange((id) => {
    applyOverlays();
    rebuildViews();
    // Aircraft coverage is per-story on the SERVER, so the scope has to travel
    // to it — unlike the entity layers, which are already loaded and narrow
    // with a setFilter. A story-scoped map still showing another theatre's
    // contacts is the one thing this overlay must not do.
    aircraft.setStory(id);
  });

  async function refresh(): Promise<void> {
    // Incidents alongside the entities: the feed groups by them, so arriving
    // without them would render every card as "grouped locally" for one tick.
    // The advisor's merge queue and the channel reliability map ride along so
    // duplicate chips and claim-line annotations land with the rows they
    // describe.
    //
    // Two fetches, split by domain, because the route orders by rev DESC under
    // a 10000-row cap and the two halves want opposite things from that. Live
    // events and news accumulate without bound and NEWEST-CHANGED first is right
    // for them. Reference data — places, units, factions — is a bounded set
    // that must be COMPLETE: an imported base that nobody has edited since
    // import holds the lowest rev in the database, so in one shared fetch it is
    // the first row the cap drops. That is how the Places pane came to list
    // four bases out of hundreds while the tiles drew all of them.
    const eventCats = registry.categories
      .filter((c) => c.domain === 'event').map((c) => c.key);
    const refCats = registry.categories
      .filter((c) => c.domain !== 'event').map((c) => c.key);
    const listUrl = (cats: string[]) =>
      `/api/entities?limit=${ENTITY_FETCH_CAP}&categories=${encodeURIComponent(cats.join(','))}`;
    const [{ entities: events }, { entities: reference }] = await Promise.all([
      eventCats.length ? get<{ entities: EntityDto[] }>(listUrl(eventCats))
        : Promise.resolve({ entities: [] as EntityDto[] }),
      refCats.length ? get<{ entities: EntityDto[] }>(listUrl(refCats))
        : Promise.resolve({ entities: [] as EntityDto[] }),
      loadIncidents(),
      loadMergeSuggestions(),
      loadAdvice(),
      loadChannelMeta(),
      loadSequences(),
    ]);
    referenceTruncated = reference.length >= ENTITY_FETCH_CAP;
    const entities = [...events, ...reference];
    all = entities;
    cacheEntities(entities);   // so a click paints its card without a fetch
    rebuildViews();
    void loadSuggestions();
  }

  // The 15 s poll hands us full EntityDtos — fold them in instead of refetching
  // the whole list every time something changed. Only when a poll comes back at
  // its cap (a huge burst; the delta may be incomplete) do we fall back to the
  // full reload.
  const UPDATES_MERGE_CAP = 400;
  function mergeUpdates(dto: UpdatesDto): void {
    // Incidents ride their own array on the cursor, because /api/updates carries
    // entities, deletions and notifications — an incident's title and counts
    // reach no client through its members' revs. Merged BEFORE the early return,
    // or a poll whose only change is a rename would be dropped on the floor.
    const incidentsChanged = mergeIncidents(dto.incidents);
    if (!dto.entities.length && !dto.deleted.length) {
      if (incidentsChanged) rebuildViews();
      return;
    }
    if (dto.entities.length >= UPDATES_MERGE_CAP) { void refresh(); return; }
    const byId = new Map(all.map((e) => [e.id, e]));
    for (const e of dto.entities) byId.set(e.id, e);
    for (const id of dto.deleted) byId.delete(id);
    all = [...byId.values()];
    cacheEntities(dto.entities);
    uncacheEntities(dto.deleted);
    rebuildViews();
  }

  // Spatial alerting: pulse the containing region for fresh events of the
  // configured kinds, and for rule alerts. Age-gated so a catch-up batch
  // after being away doesn't light up the whole region. Everything tunable
  // (kinds, pulses, timing, level) is the admin `regionFlash` setting, read
  // through boundaries.regionFlashCfg() — the renderer's own config, so the
  // trigger and the flash can never disagree. `flashOn` stays: it is this
  // USER's Layers toggle, layered on the operator's default.
  function flashUpdates(dto: UpdatesDto): void {
    const cfg = regionFlashCfg();
    if (!flashOn || !cfg.enabled) return;
    for (const e of dto.entities) {
      if (e.type !== 'live_event') continue;
      if (!cfg.kindSet.has(String(e.attrs.kind ?? ''))) continue;
      // By occurrence: the gate exists so a catch-up batch after being away does
      // not light up the whole region, and under validFrom a replayed backlog was
      // stamped "now" and flashed every province at once — the exact thing the
      // age gate was written to prevent.
      if (Date.now() - whenOf(e).at > cfg.maxAgeMs) continue;
      if (e.geometry?.type !== 'Point') continue;
      const [lon, lat] = e.geometry.coordinates as [number, number];
      void flashContaining(map, lon, lat, agePin(whenOf(e).at));
    }
    for (const n of dto.notifications) {
      const target = n.entityId ? dto.entities.find((e) => e.id === n.entityId) : undefined;
      if (target?.geometry?.type !== 'Point') continue;
      const [lon, lat] = target.geometry.coordinates as [number, number];
      void flashContaining(map, lon, lat, '#d97706');
    }
  }

  function applyUpdates(dto: UpdatesDto): void {
    mergeUpdates(dto);
    if (dto.entities.length || dto.deleted.length) logActivity(dto);
    // Suggestions are proposals, not alerts: they belong in the Suggestions
    // panel (with its badge), not in the activity log with a toast.
    const alerts = dto.notifications.filter((n) => n.kind !== 'suggestion');
    for (const n of alerts) logAlert(n);
    if (alerts.length) pushCounts();
    if (alerts.length !== dto.notifications.length) void loadSuggestions();
    flashUpdates(dto);
  }

  return {
    refresh, applyUpdates, applyOverlays, counts, open: shell.open, peek: shell.peek,
    layerState, applyLayerState, entities: () => scoped,
  };
}
